import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import {
  jiraSearch,
  jiraSearchReliable,
  jiraGetRemoteLinks,
  jiraTransition,
  jiraComment,
  jiraSetExecPriority,
  confluenceCreatePage,
  confluenceGetPage,
  confluenceUpdatePage,
  cohesionGetTime,
} from "./jiraApi";

/* ─── CONSTANTS ─────────────────────────────────────── */
const CLOUD_ID = "43b42cc8-9158-4a0a-afdc-fed3116f35d0";
const PROJECT_KEY = "LEAD";
const JIRA_BASE = "https://costarpartners.atlassian.net";


const ORGS = {
  OPAL: { accent: "#0F6E56", bg: "#E1F5EE", text: "#085041", ring: "#A8D4B8" },
  CoStar: { accent: "#534AB7", bg: "#EEEDFE", text: "#3C3489", ring: "#C4BFF5" },
  Patter: { accent: "#185FA5", bg: "#E6F1FB", text: "#0C447C", ring: "#A0C9E8" },
};

const STATUS_MAP = {
  "Backlog": "backlog",
  "To Do": "backlog",
  "In Progress": "in_progress",
  "Blocked": "blocked",
  "Awaiting Review": "waiting",
  "Reviewed": "reviewed",
  "Awaiting Info": "awaiting_info",
  "Done": "done",
};

const STATUS = {
  in_progress: { l: "In Progress", c: "#1D7A56", bg: "#E1F5EE" },
  waiting: { l: "Awaiting Review", c: "#8B6914", bg: "#FFF8E1" },
  reviewed: { l: "Reviewed", c: "#534AB7", bg: "#EEEDFE" },
  awaiting_info: { l: "Awaiting Info", c: "#B5651D", bg: "#FBEEDD" },
  blocked: { l: "Blocked", c: "#B33B3B", bg: "#FCEBEB" },
  done: { l: "Done", c: "#2D5A3D", bg: "#D4EDDA" },
  backlog: { l: "Backlog", c: "#6B6960", bg: "#F1EFE8" },
};

const C = {
  ink: "#1B1B18", paper: "#FAFAF7", muted: "#7A786F", border: "#E0DED6",
  accent: "#2D5A3D", accentLt: "#E8F0EB", accentDk: "#1A3D28",
  warn: "#C4421A", warnLt: "#FDF0EC",
  gold: "#8B6914", goldLt: "#FFF8E1",
  blue: "#1B5B8A", blueLt: "#EAF2F8",
  card: "#fff",
};

const TEAM = {
  "712020:09036bc0-c61f-4392-a519-3d9d6179ef9a": "Eric Baughman",
  "5b9fab0dafe2e024e040ad0e": "Lee Duis",
  "712020:b45fb588-945f-4a10-8bb8-00ca0cfe511c": "Scott Newman",
  "5ba2649dba4caa2f7e0137de": "Dave Nichols",
  "712020:6d80fbb5-172d-45ac-b732-b1db93afdab5": "David Comora",
};

/* ─── PERSISTENT CACHE LAYER ───────────────────────── */
async function cacheGet(key) {
  try {
    const raw = localStorage.getItem(key);
    if (raw) return JSON.parse(raw);
  } catch (e) { /* missing key or parse error — treat as cold */ }
  return null;
}

async function cacheSet(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify({ timestamp: Date.now(), data }));
  } catch (e) { console.warn("Cache write failed:", e); }
}

const CACHE_KEY = "exec-dash-v2";
const VISION_CACHE_KEY = "exec-dash-visions-v2"; // small, separate — visions paint instantly, refresh behind (v44). Versioned so a stale empty cache is never shown.
const CACHE_MAX_AGE = 1000 * 60 * 30; // 30 min — show stale, refresh behind
const JIRA_TIMEOUT_MS = 20000; // 20s — past this, surface "Connection issue · Retry" instead of a silent spin

// ── Exec Priority: source of truth for workstream ordering ──
// Workstreams (Epics) render top-to-bottom by exec priority (1 = highest).
// SINGLE SWITCH: when the Epic "Exec Priority" number field is created in Jira,
// set this to its field id (e.g. "customfield_11560"). The dashboard will then
// order by whatever David/Eric set in Jira, and the hardcoded EXEC_PRIORITY_FALLBACK
// map (in the component) becomes a backstop for any Epic that hasn't been given a
// value yet. While this is null, ordering uses the fallback map only.
const EXEC_PRIORITY_FIELD = "customfield_11615"; // "Exec Priority" number field on the Epic issue type (LEAD)
const COHESION_PROJECT_FIELD = "customfield_11780"; // "Cohesion Project" PLAIN TEXT (ENG-18). Was single-select 11714 (deleted). Value = the Cohesion project ID the manager pasted; read the string verbatim (JPalumbo on ENG-18).

// ── Goal authoring (v43 "Create New Goal" modal) ──
// Goals are LEAD issues of type "Goal" carrying a fenced [GOAL] block in the description
// and two labels: the org label (opal|patter|costar) + a goal-<slug> that binds contributing work.
const GOAL_ISSUE_TYPE = "Goal";                 // Jira issue type name (id 10667 in LEAD)
const GOAL_PROJECT_KEY = "LEAD";
const PREFIX_ORG_LABEL = { OPAL: "opal", PAT: "patter", COS: "costar" }; // modal prefix → lowercase org label used on epics/tasks
const PREFIX_LABEL = { OPAL: "OPAL", PAT: "PAT", COS: "COS" };           // canonical prefix tokens for the [GOAL] block
// Owner-graded status vocabulary for the Status field (blank/"set at review" = not yet graded).
const GOAL_STATUS_OPTIONS = ["(set at review)", "On Track", "At Risk", "Off Track"];

// Full user guide lives in Confluence so guide CONTENT can be updated without a JSX rebuild.
// Repoint this one line to the dedicated guide page once it's created (currently → Solution Documentation).
const GUIDE_URL = "https://costarpartners.atlassian.net/wiki/spaces/LEAD/pages/45547521";

// Manager job aide for creating goals (native Confluence page; a styled HTML version is attached to it).
// Surfaced as a "Creating a Goal" card in the Guides tab. One-line repoint if the page ever moves.
const GOAL_GUIDE_URL = "https://costarpartners.atlassian.net/wiki/spaces/LEAD/pages/123895810/Creating+a+Goal+Manager+Job+Aide";

// ── Vision layer (v44) ──
// Each org's Vision is the canonical [VISION] block on its own Confluence page (IDs below).
// Vision is a point of reference, NOT a Jira issue type — it doesn't relate to epics/tasks.
// The dashboard READS these pages live (confluenceGetPage) and the New-Vision wizard WRITES them.
const VISION_PAGE = { OPAL: "123961346", COS: "123994113", PAT: "124026881" }; // Confluence page IDs under Solution Docs
const VISION_STATUS_OPTIONS = ["Draft", "Confirmed"];
const VISION_PILLAR_MIN = 3, VISION_PILLAR_MAX = 5;
const VISION_WORD_TARGET = [15, 30]; // soft guidance band for the statement
// Template config: which orgs this instance shows + its titles. The org switcher renders
// only when >1 org — a single-org spin-off (e.g. "OPAL Center of Excellence") sets orgs:["OPAL"].
const DASHBOARD_TEMPLATE = {
  title: "Executive Portfolio Dashboard",
  eyebrow: "CoStar Partners · Leadership Priorities",
  orgs: ["COS", "PAT", "OPAL"], // prefix tokens, in switcher order
};
// Vision authoring is exec-owned (best practice). Real edit authority is enforced by
// Confluence page permissions (Phase 0); this is the client-side on/off for the affordance.
const VISION_AUTHORING_ENABLED = true;

// Visible build marker — shown in the header and footer so anyone can tell which version is live.
// Bump this one line on each release.
const BUILD = "v44 · Jul 9 2026";

/* Jira data layer lives in ./jiraApi (BFF-backed). */

/* Vision wrappers — re-injected by the port, transport swapped to the jiraApi seam.
   visionGetAll: reads each org's Vision page via confluenceGetPage ({success, html}),
   decodes storage-format entities, parses the [VISION] block client-side.
   visionWrite: overwrites the page via confluenceUpdatePage (version bump server-side). */
async function visionGetAll() {
  const decode = (s) => String(s)
    .replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"').replace(/&#39;/g, "'");
  const entries = await Promise.all(DASHBOARD_TEMPLATE.orgs.map(async prefix => {
    const pageId = VISION_PAGE[prefix];
    let body = "";
    try {
      if (pageId) {
        const res = await confluenceGetPage(pageId);
        body = res && res.success && typeof res.html === "string" ? decode(res.html) : "";
      }
    } catch {}
    const parsed = parseVisionBlock(body) || {};
    const statement = (parsed.statement || "").trim();
    return [prefix, {
      prefix,
      org: PREFIX_ORG[prefix] || null,
      pageId,
      pageUrl: pageId ? `${JIRA_BASE}/wiki/spaces/${PROJECT_KEY}/pages/${pageId}` : null,
      statement,
      pillars: parsed.pillars || [],
      status: parsed.status || null,
      owner: parsed.owner || "",
      drafted: !!statement,
    }];
  }));
  return Object.fromEntries(entries);
}

async function visionWrite(prefix, fields) {
  const pageId = VISION_PAGE[prefix];
  if (!pageId) return { success: false, message: "No Vision page configured for this org." };
  const block = assembleVisionBlock({ ...fields, prefix });
  const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const html = `<p>This page is the canonical source for the ${PREFIX_ORG[prefix]} Vision shown on the Executive Portfolio Dashboard. Author or edit it via the dashboard's New Vision wizard, or edit the block below directly.</p><pre><code>${esc(block)}</code></pre>`;
  const res = await confluenceUpdatePage({ pageId, html });
  return { success: !!res.success, message: res.message || (res.success ? "OK" : "Update failed.") };
}


const TRANSITIONS = {
  BACKLOG: "11",
  SELECTED: "21",
  IN_PROGRESS: "31",
  DONE: "41",
  BLOCKED: "51",
  AWAITING_REVIEW: "61",
  REVIEWED: "2",       // global transition → "Reviewed" status (10922, In Progress category)
  AWAITING_INFO: "3",  // global transition → "Awaiting Info" status (10923, In Progress category)
};

/* ─── DATA PARSING ──────────────────────────────────── */
function getOrgFromLabels(labels = []) {
  if (labels.includes("opal")) return "OPAL";
  if (labels.includes("patter")) return "Patter";
  if (labels.includes("costar")) return "CoStar";
  return "CoStar";
}

// Labels the dashboard hides from the normal label chips (the goal-association layer
// is machine plumbing, surfaced only in the Goals view — keeps the label field uncluttered).
const HIDDEN_LABEL_PREFIXES = ["goal-"];
function visibleLabels(labels = []) {
  return (labels || []).filter(l => !HIDDEN_LABEL_PREFIXES.some(p => l.startsWith(p)));
}

// Goal prefix (OPAL / PAT / COS) → org key used by ORGS and the "One X" bands.
const PREFIX_ORG = { OPAL: "OPAL", PAT: "Patter", COS: "CoStar" };

/* Parse a [GOAL] block out of an issue description. Tolerant by design:
   - handles the fenced [GOAL]…[/GOAL] form (the normalized source of truth) and a
     loose "Goal:/Metric:/…" fallback (pre-normalization safety net);
   - strips markdown emphasis/bullets, unescapes \[ \];
   - tokenizes by KEY NAME anywhere in the block (not by line), so packed lines like
     "Baseline: 0 Target: 2 Unit: milestones" still parse correctly;
   - "(set at review …)" Status → not set; [X]/[N]/[base] target/baseline → TBD;
   - Secondary "—" → none. Returns null when no goal block is present. */
function parseGoalBlock(description) {
  if (!description || typeof description !== "string") return null;
  let text = description.replace(/\r/g, "");
  const delim = text.match(/\[GOAL\]([\s\S]*?)\[\/GOAL\]/i);
  let body = delim ? delim[1] : text;
  if (!/Metric\s*:/i.test(body)) return null; // not a goal block
  // normalize: drop fences, unescape brackets, strip emphasis/mono markers
  body = body.replace(/```/g, "").replace(/\\([\[\]])/g, "$1").replace(/[*_`]/g, "");
  const KEYS = ["Prefix", "Goal", "Metric", "Baseline", "Current", "Target", "Unit", "Secondary", "Target Date", "Owner", "Status", "Notes"];
  // longest-first so "Target Date" wins over "Target"
  const keyAlt = KEYS.slice().sort((a, b) => b.length - a.length).map(k => k.replace(/ /g, "\\s+")).join("|");
  const re = new RegExp("[-*\\u2022]?\\s*(" + keyAlt + ")\\s*:", "gi");
  const found = [];
  let m;
  while ((m = re.exec(body)) !== null) {
    found.push({ key: m[1].replace(/\s+/g, " ").trim().toLowerCase(), start: m.index, valStart: re.lastIndex });
  }
  if (!found.length) return null;
  const raw = {};
  for (let i = 0; i < found.length; i++) {
    const endPos = i + 1 < found.length ? found[i + 1].start : body.length;
    if (raw[found[i].key] === undefined) raw[found[i].key] = body.slice(found[i].valStart, endPos).trim();
  }
  const val = k => (raw[k] || "").trim();
  const isTBD = s => s === "" || /\[(x|n|base)\]/i.test(s);
  const toNum = s => { const c = String(s).replace(/[^0-9.]/g, ""); return c === "" ? null : parseFloat(c); };
  const baselineRaw = val("baseline"), targetRaw = val("target"), currentRaw = val("current");
  const statusRaw = val("status");
  const statusSet = statusRaw && !/set at review/i.test(statusRaw);
  const sec = val("secondary");
  const prefix = (val("prefix") || "").toUpperCase();
  return {
    prefix,
    org: PREFIX_ORG[prefix] || null,
    goal: val("goal"),
    metric: val("metric"),
    baseline: baselineRaw,
    current: currentRaw,
    target: targetRaw,
    unit: val("unit"),
    baselineNum: isTBD(baselineRaw) ? null : toNum(baselineRaw),
    currentNum: isTBD(currentRaw) ? null : toNum(currentRaw),
    targetNum: isTBD(targetRaw) ? null : toNum(targetRaw),
    targetTBD: isTBD(targetRaw) || isTBD(baselineRaw),
    secondary: (sec === "—" || sec === "-" || sec === "") ? null : sec,
    targetDate: val("target date"),
    owner: val("owner"),
    status: statusSet ? statusRaw.replace(/\s+/g, " ").trim() : null,
    notes: val("notes"),
  };
}

/* goal-<slug> from a goal name: lowercase, alphanumeric runs joined by single hyphens,
   trimmed, capped so labels stay readable. Deterministic so the same name → same label. */
function goalSlug(name) {
  const base = String(name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-")
    .slice(0, 40)
    .replace(/-+$/g, "");
  return base ? `goal-${base}` : "";
}

/* Assemble the fenced [GOAL] block from modal fields. Round-trips cleanly through
   parseGoalBlock. Empty optional fields fall back to the contract's "unset" tokens
   so a parsed block is always well-formed. Current is optional and omitted-as-"—"
   when blank (parser treats "—"/blank as no current value → activity rollup shown). */
function assembleGoalBlock(f) {
  const or = (v, dflt) => (v && String(v).trim() ? String(v).trim() : dflt);
  const prefix = PREFIX_LABEL[f.prefix] || f.prefix || "COS";
  const statusLine = (!f.status || f.status === "(set at review)")
    ? "(set at review — On Track / At Risk / Off Track)"
    : f.status;
  const lines = [
    "[GOAL]",
    `Prefix: ${prefix}`,
    `Goal: ${or(f.goal, "")}`,
    `Metric: ${or(f.metric, "")}`,
    `Baseline: ${or(f.baseline, "[base]")}`,
    `Current: ${or(f.current, "—")}`,
    `Target: ${or(f.target, "[N]")}`,
    `Unit: ${or(f.unit, "")}`,
    `Secondary: ${or(f.secondary, "—")}`,
    `Target Date: ${or(f.targetDate, "TBD")}`,
    `Owner: ${or(f.owner, "")}`,
    `Status: ${statusLine}`,
    `Notes: ${or(f.notes, "—")}`,
    "[/GOAL]",
  ];
  // Fenced so the Jira serializer leaves it verbatim (the reliable delimiter).
  return "```\n" + lines.join("\n") + "\n```";
}

// Map a free-text goal Status to a pill style.
function goalStatusStyle(status) {
  const s = (status || "").toLowerCase();
  if (!status) return { key: "notset", label: "Status not set", c: "#6B6960", bg: "#F1EFE8", dot: "#9c9a92" };
  if (s.includes("off")) return { key: "offtrack", label: status, c: "#B33B3B", bg: "#FCEBEB", dot: "#B33B3B" };
  if (s.includes("risk")) return { key: "atrisk", label: status, c: "#8B6914", bg: "#FFF8E1", dot: "#8B6914" };
  if (s.includes("on")) return { key: "ontrack", label: status, c: "#1D7A56", bg: "#E1F5EE", dot: "#1D7A56" };
  return { key: "set", label: status, c: "#1B5B8A", bg: "#EAF2F8", dot: "#1B5B8A" };
}

function getAssigneeName(assignee) {
  if (!assignee) return "Unassigned";
  if (typeof assignee === "string") {
    return TEAM[assignee] || assignee;
  }
  if (assignee.displayName) return assignee.displayName;
  if (assignee.accountId) return TEAM[assignee.accountId] || assignee.accountId;
  return "Unassigned";
}

/* Parse a [VISION] block (v44). Purpose-built — NOT parseGoalBlock: single-value keys
   (Org/Statement/Status/Owner) are first-wins, but Pillar REPEATS and EVERY occurrence
   is collected (the goal tokenizer keeps only the first of each key). Tolerant of code
   fences and markdown, mirroring the goal parser. Returns null if there's no block. */
function parseVisionBlock(text) {
  if (!text || typeof text !== "string") return null;
  let t = text.replace(/\r/g, "");
  const delim = t.match(/\[VISION\]([\s\S]*?)\[\/VISION\]/i);
  let body = delim ? delim[1] : t;
  if (!delim && !/Statement\s*:/i.test(body)) return null;
  body = body.replace(/```/g, "").replace(/\\([\[\]])/g, "$1").replace(/[*_`]/g, "");
  const KEYS = ["Org", "Statement", "Status", "Owner", "Pillar"];
  const keyAlt = KEYS.slice().sort((a, b) => b.length - a.length).join("|");
  const re = new RegExp("[-*\\u2022]?\\s*(" + keyAlt + ")\\s*:", "gi");
  const found = [];
  let m;
  while ((m = re.exec(body)) !== null) {
    found.push({ key: m[1].trim().toLowerCase(), start: m.index, valStart: re.lastIndex });
  }
  if (!found.length) return null;
  const single = {};
  const pillars = [];
  for (let i = 0; i < found.length; i++) {
    const endPos = i + 1 < found.length ? found[i + 1].start : body.length;
    const value = body.slice(found[i].valStart, endPos).trim();
    if (found[i].key === "pillar") {
      if (!value) continue;
      const idx = value.indexOf("::");
      const name = (idx >= 0 ? value.slice(0, idx) : value).trim();
      const delivers = idx >= 0 ? value.slice(idx + 2).trim() : "";
      if (name) pillars.push({ name, delivers });
    } else if (single[found[i].key] === undefined) {
      single[found[i].key] = value;
    }
  }
  const statusRaw = (single["status"] || "").trim();
  const status = /confirm/i.test(statusRaw) ? "Confirmed" : (statusRaw ? "Draft" : null);
  return {
    org: (single["org"] || "").trim().toUpperCase() || null,
    statement: (single["statement"] || "").trim(),
    owner: (single["owner"] || "").trim(),
    status,
    pillars,
  };
}

/* Assemble a [VISION] block from wizard fields. Round-trips through parseVisionBlock.
   Returns the plain block (no code fence — visionWrite wraps it in <pre><code>). */
function assembleVisionBlock(f) {
  const lines = [
    "[VISION]",
    `Org: ${PREFIX_LABEL[f.prefix] || f.prefix || "COS"}`,
    `Statement: ${(f.statement || "").trim()}`,
    `Status: ${f.status === "Confirmed" ? "Confirmed" : "Draft"}`,
    `Owner: ${(f.owner || "").trim()}`,
  ];
  (f.pillars || []).filter(p => p && p.name && p.name.trim()).forEach(p => {
    lines.push(`Pillar: ${p.name.trim()}${p.delivers && p.delivers.trim() ? ` :: ${p.delivers.trim()}` : ""}`);
  });
  lines.push("[/VISION]");
  return lines.join("\n");
}

function getStatusKey(status) {
  if (!status) return "backlog";
  const name = typeof status === "string" ? status : status.name || status;
  return STATUS_MAP[name] || "backlog";
}

function getPriority(pri) {
  if (!pri) return "Medium";
  const name = typeof pri === "string" ? pri : pri.name || pri;
  if (name === "Highest" || name === "High") return "High";
  if (name === "Low" || name === "Lowest") return "Low";
  return "Medium";
}

function daysSince(dateStr) {
  if (!dateStr) return 0;
  const d = new Date(dateStr);
  const now = new Date();
  return Math.floor((now - d) / (1000 * 60 * 60 * 24));
}

function isThisWeek(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const now = new Date();
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - now.getDay());
  startOfWeek.setHours(0, 0, 0, 0);
  return d >= startOfWeek;
}

// Relative timestamp for the Activity feed ("2h ago", "Yesterday", "Jun 23")
function relTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return "";
  const now = new Date();
  const mins = Math.floor((now - d) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// Day bucket label for grouping the Activity feed
function dayBucket(dateStr) {
  if (!dateStr) return "Earlier";
  const d = new Date(dateStr);
  if (isNaN(d)) return "Earlier";
  const days = daysSince(dateStr);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return "Earlier this week";
  if (days < 30) return "Earlier this month";
  return "Older";
}

// Extract plain text from Atlassian Document Format (ADF) JSON
function extractAdfText(node) {
  if (!node) return "";
  if (typeof node === "string") return node;
  // Text node
  if (node.type === "text") return node.text || "";
  // Mention node — use the display text
  if (node.type === "mention") return node.attrs?.text || "@someone";
  // Emoji
  if (node.type === "emoji") return node.attrs?.shortName || "";
  // Hard break
  if (node.type === "hardBreak") return "\n";
  // Recurse into content arrays
  if (Array.isArray(node.content)) {
    const parts = node.content.map(extractAdfText);
    // Add newlines between block-level elements (paragraphs, etc.)
    if (["doc", "bulletList", "orderedList", "table"].includes(node.type)) {
      return parts.join("\n");
    }
    if (node.type === "listItem") return "• " + parts.join("");
    return parts.join("");
  }
  // If it's an object with no content, try text field
  if (node.text) return node.text;
  return "";
}

// Check if an item has recent discussion activity (within last 7 days)
function hasRecentDiscussion(item) {
  if (!item.recentComments || item.recentComments.length === 0) return false;
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  return item.recentComments.some(c => c.created && new Date(c.created) >= sevenDaysAgo);
}

function recentDiscussionCount(item) {
  if (!item.recentComments || item.recentComments.length === 0) return 0;
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  return item.recentComments.filter(c => c.created && new Date(c.created) >= sevenDaysAgo).length;
}

// Strip leaked inline-node markup that the Jira/MCP serializer wraps around @mentions
// and smartlinks when a comment body comes back as a pre-serialized string, e.g.
//   <custom data-type="mention" data-id="id-0">@Lee Duis</custom>
// The visible "@Lee Duis" text lives INSIDE the tag, so we drop only the wrapper tags
// (open + close) and keep the inner text. Also trims trailing zero-width characters
// (U+200B/C/D, U+FEFF) that Jira appends to comment bodies.
function sanitizeInlineMarkup(text) {
  if (!text || typeof text !== "string") return text || "";
  return text
    .replace(/<\/?custom\b[^>]*>/gi, "")   // drop <custom ...> and </custom>, keep inner @mention text
    .replace(/[\u200B\u200C\u200D\uFEFF]/g, "") // strip zero-width junk
    .replace(/[ \t]+\n/g, "\n")            // tidy trailing spaces before newlines
    .trim();
}

// Extract clean body text from a comment body (handles string, ADF object, or nested structures)
function cleanCommentBody(body) {
  if (!body) return "";
  let text;
  if (typeof body === "string") text = body;
  else if (body.type === "doc" && body.content) text = extractAdfText(body); // ADF document format
  else if (body.content) text = extractAdfText(body);                        // nested content array
  else if (body.text) text = body.text;                                      // plain text field
  else { try { text = JSON.stringify(body).substring(0, 300); } catch { text = ""; } } // last resort
  return sanitizeInlineMarkup(text);
}

// Unescape markdown backslash-escapes the Jira/MCP serializer leaves in (e.g. "\~45,000",
// "\-"), strip leftover **bold**/__underline__ markers, and tidy doubled spaces.
function cleanMarkdownInline(s) {
  if (!s || typeof s !== "string") return "";
  return s
    .replace(/\\([\\`*_{}\[\]()#+\-.!~>|])/g, "$1")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/__(.+?)__/g, "$1")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
}

// ── Markdown → Confluence-safe HTML (used by the Weekly Status snapshot) ──
function escHtml(s) { return String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;"); }
function mdInlineToHtml(s) {
  let t = String(s == null ? "" : s)
    .replace(/<custom\b[^>]*>([\s\S]*?)<\/custom>/gi, "$1")
    .replace(/<\/?custom\b[^>]*>/gi, "")
    .replace(/[\u200B\u200C\u200D\uFEFF]/g, "")
    .replace(/\\([\\`*_{}\[\]()#+\-.!~>|])/g, "$1");
  t = escHtml(t);
  t = t.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2">$1</a>');
  t = t.replace(/(^|[\s(])((?:https?:\/\/)[^\s<>()]+)/g, (m, pre, url) => { const jm = url.match(/\/browse\/([A-Z][A-Z0-9]+-\d+)/); return pre + '<a href="' + url + '">' + (jm ? jm[1] : url) + '</a>'; });
  t = t.replace(/\*\*([^*]+?)\*\*/g, "<strong>$1</strong>");
  return t;
}
function mdToHtml(text) {
  const lines = String(text == null ? "" : text).replace(/\r/g, "").split("\n");
  let out = "", para = [], list = [];
  const fp = () => { if (para.length) { out += "<p>" + mdInlineToHtml(para.join(" ").trim()) + "</p>"; para = []; } };
  const fl = () => { if (list.length) { out += "<ul>" + list.map(i => "<li>" + mdInlineToHtml(i) + "</li>").join("") + "</ul>"; list = []; } };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { fp(); fl(); continue; }
    const b = line.match(/^[*\-•]\s+(.*)$/);
    if (b) { fp(); list.push(b[1]); } else { fl(); para.push(line); }
  }
  fp(); fl();
  return out;
}

// ── Faithful inline + block markdown rendering for Jira descriptions ──
// Jira descriptions arrive as a markdown-ish STRING: **bold**, "* " bullets, bare URLs, and
// the leaked <custom data-type="smartlink|mention" ...>inner</custom> wrappers. We render
// them faithfully (bullets as bullets, bold as bold, links clickable) instead of flattening
// to plain text. renderInline handles inline grammar; MarkdownBlock groups lines into
// paragraphs and bullet lists; parseDescriptionSections splits on the team's bold labels.
function renderInline(text, keyPrefix, linkColor) {
  if (text == null) return null;
  const s = String(text)
    .replace(/<custom\b[^>]*>([\s\S]*?)<\/custom>/gi, "$1") // unwrap smartlink/mention, keep inner text/url
    .replace(/<\/?custom\b[^>]*>/gi, "")
    .replace(/[\u200B\u200C\u200D\uFEFF]/g, "")
    .replace(/\\([\\`*_{}\[\]()#+\-.!~>|])/g, "$1");
  const nodes = [];
  let last = 0, k = 0, m;
  const re = /(\*\*([^*]+?)\*\*)|(\[([^\]]+)\]\((https?:\/\/[^\s)]+)\))|(https?:\/\/[^\s<>()]+)/g;
  while ((m = re.exec(s)) !== null) {
    if (m.index > last) nodes.push(s.slice(last, m.index));
    if (m[1]) {
      nodes.push(<strong key={`${keyPrefix}-b${k++}`}>{m[2]}</strong>);
    } else if (m[3]) {
      nodes.push(<a key={`${keyPrefix}-l${k++}`} href={m[5]} target="_blank" rel="noopener noreferrer" style={{ color: linkColor, textDecoration: "none", fontWeight: 600 }}>{m[4]}</a>);
    } else if (m[6]) {
      const url = m[6].replace(/[.,;:]+$/, "");
      const jm = url.match(/\/browse\/([A-Z][A-Z0-9]+-\d+)/);
      const label = jm ? jm[1] : url.replace(/^https?:\/\//, "").replace(/\/+$/, "");
      nodes.push(<a key={`${keyPrefix}-u${k++}`} href={url} target="_blank" rel="noopener noreferrer" style={{ color: linkColor, textDecoration: "none", fontWeight: 600 }}>{label}</a>);
    }
    last = re.lastIndex;
  }
  if (last < s.length) nodes.push(s.slice(last));
  return nodes;
}

// Render a markdown-ish string into paragraphs + bullet lists, preserving structure.
const MarkdownBlock = ({ text, fontSize = 13, color = "#3a3a36", linkColor }) => {
  const lc = linkColor || C.blue;
  const lines = String(text || "").replace(/\r/g, "").split("\n");
  const blocks = [];
  let para = [], list = [];
  const flushPara = () => { if (para.length) { blocks.push({ t: "p", v: para.join(" ").trim() }); para = []; } };
  const flushList = () => { if (list.length) { blocks.push({ t: "ul", v: list.slice() }); list = []; } };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flushPara(); flushList(); continue; }
    const b = line.match(/^[*\-•]\s+(.*)$/);
    if (b) { flushPara(); list.push(b[1]); }
    else { flushList(); para.push(line); }
  }
  flushPara(); flushList();
  if (blocks.length === 0) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {blocks.map((blk, i) => blk.t === "ul"
        ? <ul key={i} style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 3 }}>
            {blk.v.map((it, j) => <li key={j} style={{ fontSize, lineHeight: 1.5, color }}>{renderInline(it, `b${i}i${j}`, lc)}</li>)}
          </ul>
        : <div key={i} style={{ fontSize, lineHeight: 1.55, color }}>{renderInline(blk.v, `b${i}`, lc)}</div>
      )}
    </div>
  );
};

// Split a Jira description into labeled sections using the team's bold-label convention.
// Handles BOTH "**Label:**" (colon inside the bold) and "**Label**:" (colon outside), at the
// start of a line. Section bodies — including bullets and multiple paragraphs — are kept
// INTACT so MarkdownBlock can render them faithfully. Unlabeled lead text becomes {label:null}.
function parseDescriptionSections(raw) {
  if (!raw || typeof raw !== "string") return [];
  const lines = raw.replace(/\r/g, "").split("\n");
  const sections = [];
  let cur = { label: null, lines: [] };
  const hdrInner = /^\*\*\s*([^*]+?):\s*\*\*\s*(.*)$/;  // **Label:** body
  const hdrOuter = /^\*\*\s*([^*]+?)\s*\*\*\s*:?\s*(.*)$/; // **Label**: body  /  **Label**
  const push = () => { if (cur.label || cur.lines.join("").trim()) sections.push(cur); };
  for (const raw2 of lines) {
    const line = raw2.replace(/\s+$/, "");
    const isBullet = /^\s*[*\-•]\s+/.test(line);
    const m = isBullet ? null : (line.match(hdrInner) || line.match(hdrOuter));
    if (m) {
      push();
      cur = { label: m[1].trim().replace(/:\s*$/, ""), lines: m[2] ? [m[2]] : [] };
    } else {
      cur.lines.push(line);
    }
  }
  push();
  return sections.map(s => ({ label: s.label, body: s.lines.join("\n").trim() })).filter(s => s.label || s.body);
}

// Render a Jira description. max=0 → full faithful render (paragraphs, bullets, links) inside a
// scrolling container; max>0 → a compact plain-text preview that won't cut a label mid-word.
const FormattedDescription = ({ text, max = 0, fontSize = 13, color = "#3a3a36", labelColor }) => {
  const sections = parseDescriptionSections(text);
  if (sections.length === 0) return null;
  if (max > 0) {
    let out = "", budget = max;
    for (const s of sections) {
      if (budget <= 0) break;
      const lab = s.label ? s.label + ": " : "";
      const plain = (lab + cleanMarkdownInline(s.body.replace(/<\/?custom\b[^>]*>/gi, "").replace(/\n+/g, " "))).trim();
      const take = plain.slice(0, budget);
      out += (out ? "  " : "") + take;
      budget -= take.length;
    }
    return <div style={{ fontSize, lineHeight: 1.55, color }}>{out.trim()}{out.length >= max ? "…" : ""}</div>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {sections.map((s, i) => (
        <div key={i}>
          {s.label && <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: labelColor || C.blue, fontWeight: 700, marginBottom: 3 }}>{s.label}</div>}
          {s.body && <MarkdownBlock text={s.body} fontSize={fontSize} color={color} linkColor={labelColor || C.blue} />}
        </div>
      ))}
    </div>
  );
};

/* Exec Priority value off an issue: reads the configured Epic field (EXEC_PRIORITY_FIELD),
   tolerant of a raw number, a numeric string, or a {value}/{name} object. Also accepts a
   normalized `execPriority` key from the AI extraction. Returns a finite number or null. */
function parseExecPriority(issue, fields) {
  const candidates = [
    issue && issue.execPriority,
    fields && fields.execPriority,
    EXEC_PRIORITY_FIELD ? (issue && issue[EXEC_PRIORITY_FIELD]) : undefined,
    EXEC_PRIORITY_FIELD ? (fields && fields[EXEC_PRIORITY_FIELD]) : undefined,
  ];
  for (let v of candidates) {
    if (v == null) continue;
    if (typeof v === "object") v = v.value ?? v.name ?? v.id; // single-select / number-field shapes
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function parseIssues(rawIssues) {
  if (!Array.isArray(rawIssues)) return [];
  return rawIssues.map(issue => {
    const fields = issue.fields || issue;
    // Extract comments — prefer recentComments array, fall back to lastComment
    let recentComments = [];
    let totalComments = 0;
    let lastComment = null;

    // Try recentComments from AI extraction first
    const rcFromAI = issue.recentComments || fields.recentComments;
    if (Array.isArray(rcFromAI) && rcFromAI.length > 0) {
      recentComments = rcFromAI.map(c => ({
        author: c.author || "Team",
        body: cleanCommentBody(c.body),
        created: c.created || null,
      }));
      totalComments = issue.totalComments || fields.totalComments || recentComments.length;
      lastComment = recentComments[recentComments.length - 1];
    }

    // Fall back to raw comment field from Jira API
    if (recentComments.length === 0) {
      const commentField = fields.comment || issue.comment;
      if (commentField) {
        const comments = commentField.comments || commentField;
        if (Array.isArray(comments) && comments.length > 0) {
          totalComments = commentField.total || comments.length;
          const recent = comments.slice(-4);
          recentComments = recent.map(c => ({
            author: c.author?.displayName || c.author || "Unknown",
            body: cleanCommentBody(c.body),
            created: c.created || c.updated || null,
          }));
          lastComment = recentComments[recentComments.length - 1];
        }
      }
    }

    // Final fallback to single lastComment from AI extraction
    if (!lastComment && (issue.lastComment || fields.lastComment)) {
      const lc = issue.lastComment || fields.lastComment;
      if (typeof lc === "string") {
        lastComment = { author: "Team", body: cleanCommentBody(lc), created: null };
        recentComments = [lastComment];
        totalComments = 1;
      } else if (lc && lc.body) {
        lastComment = { author: lc.author || "Team", body: cleanCommentBody(lc.body), created: lc.created || null };
        recentComments = [lastComment];
        totalComments = 1;
      }
    }
    return {
      key: issue.key || fields.key,
      summary: fields.summary || issue.summary || "",
      status: getStatusKey(fields.status || issue.status),
      statusName: typeof (fields.status || issue.status) === "string" ? (fields.status || issue.status) : (fields.status || issue.status)?.name || "",
      priority: getPriority(fields.priority || issue.priority),
      assignee: getAssigneeName(fields.assignee || issue.assignee),
      labels: fields.labels || issue.labels || [],
      org: getOrgFromLabels(fields.labels || issue.labels || []),
      isEpic: (fields.issuetype || issue.issuetype)?.name === "Epic" 
        || issue.issuetype === "Epic" 
        || (typeof (fields.issuetype || issue.issuetype) === "string" && (fields.issuetype || issue.issuetype).toLowerCase() === "epic"),
      isGoal: (fields.issuetype || issue.issuetype)?.name === "Goal"
        || issue.issuetype === "Goal"
        || (typeof (fields.issuetype || issue.issuetype) === "string" && (fields.issuetype || issue.issuetype).toLowerCase() === "goal"),
      parentKey: fields.parent?.key || issue.parentKey || issue.parent_key 
        || (typeof fields.parent === "string" ? fields.parent : null) 
        || (typeof issue.parent === "string" ? issue.parent : null)
        || issue.epicKey || fields.epicKey || null,
      components: (fields.components || issue.components || []).map(c => typeof c === "string" ? c : c.name),
      updated: fields.updated || issue.updated,
      created: fields.created || issue.created,
      description: fields.description || issue.description || "",
      lastComment,
      recentComments,
      totalComments,
      confluenceLinks: (issue.confluenceLinks || fields.confluenceLinks || []).filter(l => l && l.url),
      webLinks: (issue.webLinks || fields.webLinks || []).filter(l => l && l.url),
      attachments: (issue.attachments || fields.attachment || fields.attachments || [])
        .map(a => {
          if (!a) return null;
          const id = a.id != null ? String(a.id) : null;
          // The MCP returns a content URL on the api.atlassian.com gateway, which
          // needs a bearer token. Derive the session-cookie-friendly site URL from
          // the attachment id so a human can open/download it from a browser.
          const url = id ? `${JIRA_BASE}/rest/api/3/attachment/content/${id}` : (a.url || a.content || null);
          return { filename: a.filename || a.name || "Attachment", mime: a.mime || a.mimeType || "", id, url };
        })
        .filter(a => a && a.url),
      nextStep: issue.nextStep || fields.nextStep || issue.customfield_11513 || fields.customfield_11513 || null,
      reviewAsk: issue.reviewAsk || fields.reviewAsk || issue.customfield_11546 || (fields.customfield_11546 && fields.customfield_11546.value) || fields.customfield_11546 || null,
      reviewType: issue.reviewType || fields.reviewType || (issue.customfield_11547 && issue.customfield_11547.value) || (fields.customfield_11547 && fields.customfield_11547.value) || (typeof fields.customfield_11547 === "string" ? fields.customfield_11547 : null) || null,
      execPriority: parseExecPriority(issue, fields),
      cohesionProjectId: issue.cohesionProjectId
        ?? fields.cohesionProjectId
        ?? (typeof fields.customfield_11780 === "string" ? fields.customfield_11780 : null)
        ?? (fields.customfield_11780 && fields.customfield_11780.value)
        ?? null,
    };
  });
}

/* Next Step: explicit field value, else a sensible default derived from status.
   Returns { text, explicit } or null (nothing to show, e.g. Done). */
function nextStepDisplay(item) {
  if (item.nextStep && String(item.nextStep).trim()) {
    return { text: String(item.nextStep).trim(), explicit: true };
  }
  switch (item.status) {
    case "blocked": return { text: "Resolve blocker — see latest note", explicit: false };
    case "waiting": return { text: "Pending David's review", explicit: false };
    case "in_progress": return { text: `${item.assignee} completing current work`, explicit: false };
    case "selected": return { text: "Queued — ready to start", explicit: false };
    case "backlog": return { text: "Not started yet", explicit: false };
    default: return null; // done / unknown — no next step
  }
}


/* ─── SMALL COMPONENTS ──────────────────────────────── */
const OrgBadge = ({ org }) => {
  const o = ORGS[org] || ORGS.CoStar;
  return <span style={{ display: "inline-block", fontSize: 10, fontWeight: 600, letterSpacing: "0.06em", padding: "2px 8px", borderRadius: 4, background: o.bg, color: o.text, textTransform: "uppercase" }}>{org}</span>;
};

/* ── COHESION TIME (ENG-18) ─────────────────────────────
   Detail-panel view of a task's Cohesion project time. Three states,
   driven by `state` = cohesionTimeByKey[item.key]:
     • loading            → skeleton line
     • error / no data     → small "couldn't load" note
     • mapped with data    → summary header (hours logged / budgeted, %
                             consumed, budget bar, last entry) PLUS the
                             /projects-style drill-down: hours by month,
                             by person, and recent entries.
   The caller only renders this when the item HAS a cohesionProjectId,
   so the "not mapped → show nothing" state is handled by the caller.
   Older payloads without the byMonth/byPerson/recentEntries arrays fall
   back to the summary alone. */
const CohesionTime = ({ state }) => {
  const wrap = (inner) => (
    <div style={{ gridColumn: "1 / -1", background: C.paper, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px" }}>
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: C.blue, fontWeight: 700, marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
        ⏱ Cohesion Time
      </div>
      {inner}
    </div>
  );

  if (!state || state.loading) {
    return wrap(<div style={{ fontSize: 12, color: C.muted, fontStyle: "italic" }}>Loading project time…</div>);
  }
  const t = state.data;
  if (state.error || !t || t.error) {
    return wrap(<div style={{ fontSize: 12, color: C.warn }}>Couldn't load Cohesion time for this project.</div>);
  }

  const logged = typeof t.hoursLogged === "number" ? t.hoursLogged : null;
  const budget = typeof t.hoursBudgeted === "number" ? t.hoursBudgeted : null;
  const pct = typeof t.percentConsumed === "number"
    ? t.percentConsumed
    : (logged != null && budget) ? Math.round((logged / budget) * 100) : null;
  const over = pct != null && pct > 100;
  const barPct = pct != null ? Math.min(pct, 100) : 0;
  const barColor = over ? C.warn : pct != null && pct >= 85 ? C.gold : C.accent;
  const num = (n) => (n == null ? "—" : n.toLocaleString(undefined, { maximumFractionDigits: 1 }));
  const isSample = typeof t.projectName === "string" && /\(sample\)/i.test(t.projectName);

  const months = Array.isArray(t.byMonth) ? t.byMonth.slice(-24) : [];
  const people = Array.isArray(t.byPerson) ? t.byPerson : [];
  const entries = Array.isArray(t.recentEntries) ? t.recentEntries : [];
  const monthMax = Math.max(...months.map((m) => m.hours), 1);
  const monthMaxIdx = months.reduce((bi, m, i) => (m.hours > (months[bi]?.hours ?? 0) ? i : bi), 0);
  const personMax = Math.max(...people.map((p) => p.hours), 1);
  const colLabel = { fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted, fontWeight: 600, marginBottom: 6 };

  return wrap(
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.ink, minWidth: 0 }}>{t.projectName || t.projectId}</div>
        <div style={{ fontFamily: "'Source Serif 4',Georgia,serif", fontSize: 15, fontWeight: 600, color: barColor, whiteSpace: "nowrap" }}>
          {num(logged)} <span style={{ fontSize: 11, color: C.muted, fontWeight: 400 }}>/ {budget ? num(budget) : "—"} hrs</span>
        </div>
      </div>
      {/* Budget bar — reads like the Goals activity rollup: spend vs. plan */}
      <div style={{ marginTop: 7, height: 7, borderRadius: 4, background: C.border, overflow: "hidden" }}>
        <div style={{ width: `${barPct}%`, height: "100%", background: barColor, borderRadius: 4, transition: "width 0.3s" }} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 11, color: C.muted }}>
        <span style={{ color: over ? C.warn : C.muted, fontWeight: over ? 600 : 400 }}>
          {pct == null ? (budget ? "—" : "no budget set") : `${pct}% of budget`}{over ? " · over budget" : ""}
        </span>
        <span>{t.lastEntryDate ? `Last entry ${new Date(t.lastEntryDate).toLocaleDateString()}` : ""}</span>
      </div>

      {/* Drill-down: the /projects row expansion for this one project */}
      {(months.length > 0 || people.length > 0 || entries.length > 0) && (
        <div style={{ display: "grid", gridTemplateColumns: "1.1fr 1.3fr 1.2fr", gap: 16, marginTop: 12, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
          <div style={{ minWidth: 0 }}>
            <div style={colLabel}>Hours by month (last 24)</div>
            {months.length === 0 ? <div style={{ fontSize: 11, color: C.muted }}>No entries.</div> : (
              <div>
                <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 72 }}>
                  {months.map((m, i) => (
                    <div key={m.period} title={`${m.period}: ${num(m.hours)} hrs`}
                      style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", height: "100%", cursor: "default" }}>
                      {(i === monthMaxIdx || i === months.length - 1) && (
                        <div style={{ fontSize: 9, color: C.muted, marginBottom: 2, whiteSpace: "nowrap" }}>{num(m.hours)}</div>
                      )}
                      <div style={{ width: "100%", maxWidth: 18, height: `${Math.max((m.hours / monthMax) * 52, m.hours > 0 ? 2 : 0)}px`, background: C.blue, borderRadius: "3px 3px 0 0" }} />
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: C.muted, marginTop: 3, borderTop: `1px solid ${C.border}`, paddingTop: 3 }}>
                  <span>{months[0].period}</span>
                  <span>{months[months.length - 1].period}</span>
                </div>
              </div>
            )}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={colLabel}>By person</div>
            <div style={{ maxHeight: 120, overflowY: "auto", paddingRight: 12 }}>
              {people.length === 0 && <div style={{ fontSize: 11, color: C.muted }}>No entries.</div>}
              {people.slice(0, 15).map((p) => (
                <div key={p.name} style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 4 }}>
                  <span style={{ width: 110, fontSize: 11, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flexShrink: 0 }} title={p.name}>{p.name}</span>
                  <div style={{ flex: 1, height: 7, background: C.border, borderRadius: 4 }}>
                    <div style={{ width: `${(p.hours / personMax) * 100}%`, height: 7, background: C.blue, borderRadius: "0 4px 4px 0", minWidth: 2 }} />
                  </div>
                  <span style={{ width: 56, fontSize: 11, color: C.muted, textAlign: "right", flexShrink: 0 }}>{num(p.hours)}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={colLabel}>Recent entries</div>
            <div style={{ maxHeight: 120, overflowY: "auto", fontSize: 11, paddingRight: 12 }}>
              {entries.length === 0 && <div style={{ fontSize: 11, color: C.muted }}>No entries.</div>}
              {entries.map((e, i) => (
                <div key={i} style={{ padding: "3px 0", borderBottom: `1px solid ${C.border}` }}>
                  <div style={{ display: "flex", gap: 7 }}>
                    <span style={{ color: C.muted, flexShrink: 0 }}>{e.workDate}</span>
                    <span style={{ color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={e.name}>{e.name}</span>
                    <span style={{ color: C.ink, flexShrink: 0, fontWeight: 600, marginLeft: "auto" }}>{num(e.hours)}h</span>
                  </div>
                  {e.comment && (
                    <div style={{ color: C.muted, marginTop: 1, whiteSpace: "normal", overflowWrap: "break-word", lineHeight: 1.35 }}>{e.comment}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {isSample && (
        <div style={{ marginTop: 7, fontSize: 10.5, color: C.gold, fontStyle: "italic" }}>
          Sample data — live Cohesion time connects when this ports to the web host.
        </div>
      )}
    </div>
  );
};

const StatusPill = ({ status }) => {
  const s = STATUS[status] || STATUS.backlog;
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 500, color: s.c, background: s.bg, padding: "2px 8px", borderRadius: 4 }}><span style={{ width: 6, height: 6, borderRadius: "50%", background: s.c }} />{s.l}</span>;
};

const PriBadge = ({ pri }) => {
  const styles = {
    High:   { background: "#FCEBEB", color: "#A32D2D", border: "none" },
    Medium: { background: "#FFF8E1", color: "#8B6914", border: "none" },
    Low:    { background: "#F1EFE8", color: "#6B6960", border: "none" },
  };
  const s = styles[pri] || styles.Medium;
  return <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 6px", borderRadius: 3, background: s.background, color: s.color, border: s.border, textTransform: "uppercase", letterSpacing: "0.05em" }}>{pri}</span>;
};

const Btn = ({ children, variant = "outline", onClick, style: s2 }) => {
  const base = { padding: "6px 14px", borderRadius: 6, border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "'Outfit',sans-serif", transition: "all 0.15s", ...s2 };
  const v = {
    primary: { ...base, background: C.accent, color: "#fff" },
    gold: { ...base, background: C.gold, color: "#fff" },
    warn: { ...base, background: C.warn, color: "#fff" },
    outline: { ...base, background: "transparent", border: `1px solid ${C.border}`, color: C.muted },
    ghost: { ...base, background: "transparent", color: C.blue },
  };
  return <button style={v[variant] || v.outline} onClick={onClick}>{children}</button>;
};

const LoadingDot = () => (
  <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: C.accent, animation: "pulse 1.2s ease-in-out infinite" }} />
);

const Spinner = ({ msg }) => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 60, gap: 16 }}>
    <div style={{ display: "flex", gap: 6 }}>
      {[0, 1, 2].map(i => (
        <span key={i} style={{
          display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: C.accent,
          animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite`,
        }} />
      ))}
    </div>
    <div style={{ fontSize: 14, color: C.muted, fontWeight: 500 }}>{msg || "Loading from Jira..."}</div>
  </div>
);

/* ─── COMMENT THREAD COMPONENT ─────────────────────── */
// Pull a labeled "Status Update" out of a Description so it can render as an official
// status, distinct from the definition. Recognizes "Status Update" / "Current Status" /
// "Status" (in that order), with an optional (date). Returns { text, date } or null.
function extractStatusUpdate(desc) {
  if (!desc || typeof desc !== "string") return null;
  for (const label of ["Status Update", "Current Status", "Status"]) {
    const re = new RegExp("\\*{0,2}\\s*" + label + "\\s*(?:\\(([^)]*)\\))?\\s*\\*{0,2}\\s*:\\s*\\*{0,2}\\s*", "i");
    const m = desc.match(re);
    if (m) {
      const rest = desc.slice(m.index + m[0].length);
      const stop = rest.search(/\n\s*\*\*[^*\n]+?:\s*\*\*|\n\s*\*\*[^*\n]+?\*\*\s*:/);
      const text = (stop >= 0 ? rest.slice(0, stop) : rest).replace(/\*\*/g, "").replace(/\s+/g, " ").trim();
      if (text) return { text, date: m[1] || null, label };
    }
  }
  return null;
}

const CommentThread = ({ item, onAddComment, accentColor }) => {
  const [newComment, setNewComment] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const comments = item.recentComments || (item.lastComment ? [item.lastComment] : []);
  const total = item.totalComments || comments.length;
  const hasMore = total > comments.length;
  // Display newest-first to mirror Jira's comment order (stored array stays oldest-first
  // so lastComment = recentComments[last] and activity math are unaffected).
  const ordered = [...comments].reverse();
  const ac = accentColor || C.accent;

  const handleSend = async () => {
    if (!newComment.trim() || sending) return;
    setSending(true);
    try {
      await onAddComment(item.key, newComment.trim());
      setSent("Comment added");
      setNewComment("");
      setTimeout(() => setSent(null), 3000);
    } catch (e) {
      setSent("Failed to add comment");
      setTimeout(() => setSent(null), 4000);
    } finally {
      setSending(false);
    }
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString();
  };

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: ac, fontWeight: 700 }}>
          💬 Discussion {total > 0 ? `(${total})` : ""}
        </div>
        {hasMore && (
          <a href={`https://costarpartners.atlassian.net/browse/${item.key}`} target="_blank" rel="noopener noreferrer"
            style={{ fontSize: 11, color: C.blue, fontWeight: 600, textDecoration: "none" }}>
            See all {total} comments in Jira ↗
          </a>
        )}
      </div>

      {comments.length === 0 ? (
        <div style={{ padding: "10px 14px", background: C.paper, borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 12, color: C.muted, fontStyle: "italic" }}>
          No comments yet. Add one below to start the discussion.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {ordered.map((c, i) => (
            <div key={i} style={{
              padding: "8px 12px",
              background: i === 0 ? `${ac}0D` : C.paper,
              borderRadius: 8,
              border: `1px solid ${i === 0 ? ac + "22" : C.border}`,
              fontSize: 12, lineHeight: 1.5,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                <span style={{ fontWeight: 700, fontSize: 11, color: ac }}>{c.author}</span>
                <span style={{ fontSize: 10, color: C.muted }}>{timeAgo(c.created)}</span>
              </div>
              <div style={{ color: "#3a3a36" }}>{c.body.length > 400 ? c.body.substring(0, 400) + "…" : c.body}</div>
            </div>
          ))}
          {hasMore && (
            <div style={{ textAlign: "center", padding: "6px 0", fontSize: 11, color: C.muted }}>
              ↓ {total - comments.length} earlier comment{total - comments.length > 1 ? "s" : ""} — <a href={`https://costarpartners.atlassian.net/browse/${item.key}`} target="_blank" rel="noopener noreferrer" style={{ color: C.blue, textDecoration: "none" }}>view in Jira</a>
            </div>
          )}
        </div>
      )}

      {/* Add Comment Input */}
      <div style={{ marginTop: 8, display: "flex", gap: 6, alignItems: "flex-start" }}>
        <textarea
          value={newComment}
          onChange={e => setNewComment(e.target.value)}
          placeholder="Add a comment..."
          rows={2}
          style={{
            flex: 1, padding: "8px 10px", borderRadius: 6, border: `1px solid ${C.border}`,
            fontSize: 12, fontFamily: "inherit", resize: "vertical", minHeight: 36,
            outline: "none", transition: "border 0.15s",
          }}
          onFocus={e => e.target.style.borderColor = ac}
          onBlur={e => e.target.style.borderColor = C.border}
          onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleSend(); }}
        />
        <button onClick={handleSend} disabled={sending || !newComment.trim()} style={{
          padding: "8px 14px", borderRadius: 6, border: "none",
          background: sending || !newComment.trim() ? C.border : ac,
          color: sending || !newComment.trim() ? C.muted : "#fff",
          fontSize: 11, fontWeight: 700, cursor: sending || !newComment.trim() ? "default" : "pointer",
          fontFamily: "inherit", transition: "all 0.15s", whiteSpace: "nowrap",
        }}>
          {sending ? "..." : "Send"}
        </button>
      </div>
      {sent && (
        <div style={{ fontSize: 11, color: sent.includes("Failed") ? C.warn : C.accent, marginTop: 4, fontWeight: 600 }}>
          {sent.includes("Failed") ? "⚠" : "✓"} {sent}
        </div>
      )}
      <div style={{ fontSize: 10, color: C.muted, marginTop: 3 }}>Ctrl+Enter to send · Posts to Jira issue {item.key}</div>
    </div>
  );
};


/* ─── MAIN DASHBOARD ────────────────────────────────── */
/* ═══════════════ GOAL AUTHORING UI (v43) ═══════════════
   Create-New-Goal modal + Associate-work modal. These reduce the friction of
   hand-authoring a fenced [GOAL] block so the ~40 One-OPAL/One-CoStar managers can
   define goals and bind work to them without training. Both write through the same
   model-mediated MCP path as every other dashboard write (post as the authenticated
   user; each write surfaces the connector's per-write approval prompt). */

// ⓘ info dot — a hover definition beside a field label (native title = reliable, no state).
function InfoDot({ text }) {
  return (
    <span title={text} role="img" aria-label={text} style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: 14, height: 14, borderRadius: "50%", border: `1px solid ${C.muted}`,
      color: C.muted, fontSize: 9, fontWeight: 700, fontStyle: "italic", cursor: "help",
      marginLeft: 5, userSelect: "none", flexShrink: 0,
    }}>i</span>
  );
}

// Per-field reason/definition — shown on hover so authoring needs no advance training.
const GOAL_FIELD_HELP = {
  prefix: "Which organization this goal belongs to. Sets the org label (opal / patter / costar) so the goal files under One OPAL, One CoStar, or One Patter.",
  goal: "The outcome in one line — e.g. \"OPAL Home Base live for all managers.\" Becomes the goal's title and its goal-… label.",
  metric: "The single measure that proves the goal moved — the one number that matters. No metric = not a goal.",
  baseline: "Where the metric stands today, before the work. Leave blank if not yet known.",
  current: "Optional. Where the metric stands right now — update it as the number moves. When set, the card draws a real baseline → current → target position. Leave blank and the card shows tagged-work progress instead.",
  target: "Where the metric needs to get to. Leave blank if the target isn't set yet.",
  unit: "What baseline / current / target are counted in — e.g. managers, %, capabilities, days.",
  secondary: "Optional supporting measure. Leave blank if there isn't one.",
  targetDate: "When the target should be reached — a date, or a phrase like \"end Q3 2026.\"",
  owner: "Who owns the outcome. Usually you, the creator.",
  status: "Leave at \"set at review\" until an owner grades it. Graded at review as On Track / At Risk / Off Track.",
  notes: "Anything that clarifies scope or what \"done\" means. Optional.",
};

function GoalFieldLabel({ k, children }) {
  return (
    <label style={{ display: "flex", alignItems: "center", fontSize: 11, fontWeight: 600, color: C.ink, marginBottom: 4 }}>
      {children}<InfoDot text={GOAL_FIELD_HELP[k]} />
    </label>
  );
}

const GOAL_INPUT_STYLE = {
  width: "100%", padding: "7px 9px", border: `1px solid ${C.border}`, borderRadius: 6,
  fontSize: 13, fontFamily: "inherit", color: C.ink, background: "#fff", boxSizing: "border-box",
};

function CreateGoalModal({ existingSlugs = [], visions = {}, onClose, onCreated }) {
  const [f, setF] = useState({
    prefix: "OPAL", goal: "", metric: "", baseline: "", current: "", target: "",
    unit: "", secondary: "", targetDate: "", owner: "", status: "(set at review)", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [showBlock, setShowBlock] = useState(false);
  const [result, setResult] = useState(null); // { success, message, key }
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const slug = goalSlug(f.goal);
  const slugClash = slug && existingSlugs.includes(slug);
  const block = assembleGoalBlock(f);
  const canSubmit = f.goal.trim() && f.metric.trim() && !submitting;
  const oc = ORGS[PREFIX_ORG[f.prefix]] || ORGS.CoStar;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true); setResult(null);
    const labels = [PREFIX_ORG_LABEL[f.prefix] || "costar", slug].filter(Boolean);
    const summary = `${PREFIX_LABEL[f.prefix] || f.prefix}: ${f.goal.trim()}`;
    const res = await jiraCreateGoal({ summary, description: block, labels });
    setSubmitting(false);
    setResult(res);
    if (res.success) setTimeout(() => onCreated(res.key), 1500);
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1200, display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: "40px 16px" }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)" }} />
      <div style={{ position: "relative", width: 560, maxWidth: "100%", background: C.card, borderRadius: 12, boxShadow: "0 12px 48px rgba(0,0,0,0.28)", animation: "fadeIn 0.2s ease" }}>
        {/* Header */}
        <div style={{ padding: "18px 22px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div>
            <div style={{ fontFamily: "'Source Serif 4',Georgia,serif", fontSize: 18, fontWeight: 700, color: C.ink }}>Create a new goal</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 3, lineHeight: 1.5, maxWidth: "52ch" }}>
              Fill the fields — the dashboard builds the goal and files it in Jira for you. Hover the <span style={{ fontStyle: "italic", fontWeight: 700 }}>i</span> on any field for what it's for. Only <strong>Goal</strong> and <strong>Metric</strong> are required.
            </div>
          </div>
          <button onClick={onClose} title="Close" style={{ border: "none", background: "none", fontSize: 22, lineHeight: 1, color: C.muted, cursor: "pointer", flexShrink: 0 }}>×</button>
        </div>

        {/* Body */}
        <div style={{ padding: "18px 22px", display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Prefix / org */}
          <div>
            <GoalFieldLabel k="prefix">Organization</GoalFieldLabel>
            <div style={{ display: "flex", gap: 8 }}>
              {["OPAL", "PAT", "COS"].map(p => {
                const on = f.prefix === p;
                const pc = ORGS[PREFIX_ORG[p]] || ORGS.CoStar;
                return (
                  <button key={p} onClick={() => set("prefix", p)} style={{
                    flex: 1, padding: "8px 0", borderRadius: 7, cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit",
                    border: on ? `2px solid ${pc.accent}` : `1px solid ${C.border}`,
                    background: on ? pc.bg : "#fff", color: on ? pc.text : C.muted,
                  }}>{p === "PAT" ? "Patter" : p === "COS" ? "CoStar" : "OPAL"}</button>
                );
              })}
            </div>
          </div>

          {/* Vision reference (v44) — the org's Vision, so the goal is written in alignment */}
          {(() => {
            const vv = visions && visions[f.prefix];
            if (!vv) return null;
            const vc = ORGS[PREFIX_ORG[f.prefix]] || ORGS.CoStar;
            return (
              <div style={{ background: C.paper, border: `1px solid ${C.border}`, borderLeft: `3px solid ${vc.accent}`, borderRadius: 8, padding: "10px 12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 10.5, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: vc.text }}>
                  <EyeIcon size={13} color={vc.accent} /> {PREFIX_ORG[f.prefix]} Vision — align your goal to this
                </div>
                {vv.drafted ? (
                  <>
                    <div style={{ fontSize: 12.5, color: C.ink, marginTop: 6, lineHeight: 1.5 }}>{vv.statement}</div>
                    {vv.status === "Draft" && (
                      <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.04em", color: C.gold, marginTop: 6 }}>DRAFT — AWAITING EXECUTIVE APPROVAL</div>
                    )}
                    {vv.pillars.length > 0 && (
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                        {vv.pillars.map((p, i) => (
                          <span key={i} title={p.delivers || undefined} style={{ fontSize: 11, fontWeight: 700, color: vc.text, background: vc.bg, border: `1px solid ${vc.ring}`, padding: "2px 9px", borderRadius: 16, cursor: p.delivers ? "help" : "default" }}>{p.name}</span>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: 12, color: C.muted, marginTop: 6, fontStyle: "italic" }}>Not yet drafted for {PREFIX_ORG[f.prefix]}. Author it from the Vision bar at the top of the dashboard.</div>
                )}
              </div>
            );
          })()}
          <div>
            <GoalFieldLabel k="goal">Goal <span style={{ color: "#B33B3B", marginLeft: 2 }}>*</span></GoalFieldLabel>
            <input value={f.goal} onChange={e => set("goal", e.target.value)} placeholder="e.g. OPAL Home Base live for all managers" style={GOAL_INPUT_STYLE} />
            {slug && (
              <div style={{ fontSize: 11, color: slugClash ? "#B33B3B" : C.muted, marginTop: 4 }}>
                Label: <code style={{ fontFamily: "'DM Mono',monospace", background: C.paper, padding: "1px 5px", borderRadius: 3 }}>{slug}</code>
                {slugClash ? " — a goal with this label already exists. Tweak the wording to make it unique." : " — this binds contributing work to the goal."}
              </div>
            )}
          </div>

          {/* Metric */}
          <div>
            <GoalFieldLabel k="metric">Metric <span style={{ color: "#B33B3B", marginLeft: 2 }}>*</span></GoalFieldLabel>
            <input value={f.metric} onChange={e => set("metric", e.target.value)} placeholder="e.g. Managers actively using Home Base" style={GOAL_INPUT_STYLE} />
          </div>

          {/* Baseline / Current / Target / Unit */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10 }}>
            <div>
              <GoalFieldLabel k="baseline">Baseline</GoalFieldLabel>
              <input value={f.baseline} onChange={e => set("baseline", e.target.value)} placeholder="0" style={GOAL_INPUT_STYLE} />
            </div>
            <div>
              <GoalFieldLabel k="current">Current</GoalFieldLabel>
              <input value={f.current} onChange={e => set("current", e.target.value)} placeholder="—" style={{ ...GOAL_INPUT_STYLE, borderColor: oc.ring }} />
            </div>
            <div>
              <GoalFieldLabel k="target">Target</GoalFieldLabel>
              <input value={f.target} onChange={e => set("target", e.target.value)} placeholder="40" style={GOAL_INPUT_STYLE} />
            </div>
            <div>
              <GoalFieldLabel k="unit">Unit</GoalFieldLabel>
              <input value={f.unit} onChange={e => set("unit", e.target.value)} placeholder="managers" style={GOAL_INPUT_STYLE} />
            </div>
          </div>

          {/* Secondary */}
          <div>
            <GoalFieldLabel k="secondary">Secondary measure</GoalFieldLabel>
            <input value={f.secondary} onChange={e => set("secondary", e.target.value)} placeholder="optional" style={GOAL_INPUT_STYLE} />
          </div>

          {/* Target date / Owner */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <GoalFieldLabel k="targetDate">Target date</GoalFieldLabel>
              <input value={f.targetDate} onChange={e => set("targetDate", e.target.value)} placeholder="end Q3 2026" style={GOAL_INPUT_STYLE} />
            </div>
            <div>
              <GoalFieldLabel k="owner">Owner</GoalFieldLabel>
              <input value={f.owner} onChange={e => set("owner", e.target.value)} placeholder="your name" style={GOAL_INPUT_STYLE} />
            </div>
          </div>

          {/* Status */}
          <div>
            <GoalFieldLabel k="status">Status</GoalFieldLabel>
            <select value={f.status} onChange={e => set("status", e.target.value)} style={{ ...GOAL_INPUT_STYLE, cursor: "pointer" }}>
              {GOAL_STATUS_OPTIONS.map(s => <option key={s} value={s}>{s === "(set at review)" ? "Not graded yet (set at review)" : s}</option>)}
            </select>
          </div>

          {/* Notes */}
          <div>
            <GoalFieldLabel k="notes">Notes</GoalFieldLabel>
            <textarea value={f.notes} onChange={e => set("notes", e.target.value)} rows={2} placeholder="What 'done' means, scope, context — optional" style={{ ...GOAL_INPUT_STYLE, resize: "vertical" }} />
          </div>

          {/* Live [GOAL] block preview */}
          <div>
            <button onClick={() => setShowBlock(v => !v)} style={{ border: "none", background: "none", color: C.blue, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", padding: 0 }}>
              {showBlock ? "▾ Hide" : "▸ Preview"} the [GOAL] block this creates
            </button>
            {showBlock && (
              <pre style={{ marginTop: 6, padding: "10px 12px", background: "#1C1B18", color: "#E8E6DF", borderRadius: 7, fontSize: 11, fontFamily: "'DM Mono',monospace", overflowX: "auto", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{block}</pre>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 22px", borderTop: `1px solid ${C.border}`, background: C.paper, borderRadius: "0 0 12px 12px" }}>
          {result && (
            <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 10, color: result.success ? "#1D7A56" : "#B33B3B" }}>
              {result.success
                ? `✓ Created${result.key ? ` ${result.key}` : ""} in Backlog. Refreshing the board…`
                : `Couldn't create the goal: ${result.message || "unknown error"}. Nothing was written — try again.`}
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
            <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.4 }}>
              Creates a <strong>Goal</strong> issue in LEAD (Backlog) as you. You'll be asked to approve the write.
            </div>
            <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
              <button onClick={onClose} disabled={submitting} style={{ padding: "8px 16px", border: `1px solid ${C.border}`, borderRadius: 7, background: "#fff", color: C.ink, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
              <button onClick={submit} disabled={!canSubmit || slugClash} title={!f.goal.trim() || !f.metric.trim() ? "Goal and Metric are required" : slugClash ? "Label clash — adjust the goal wording" : "Create the goal"} style={{
                padding: "8px 18px", border: "none", borderRadius: 7, fontSize: 13, fontWeight: 700, fontFamily: "inherit",
                cursor: (!canSubmit || slugClash) ? "not-allowed" : "pointer",
                background: (!canSubmit || slugClash) ? "#B7B4AA" : C.ink, color: "#fff",
              }}>{submitting ? "Creating…" : "Create goal"}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TagWorkModal({ goal, epics = [], tasks = [], onClose, onApplied }) {
  const slug = goal.slug;
  const [picked, setPicked] = useState(new Set());
  const [q, setQ] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const toggle = k => setPicked(p => { const n = new Set(p); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const candidates = [
    ...epics.map(e => ({ ...e, isEpic: true })),
    ...tasks.map(t => ({ ...t, isEpic: false })),
  ];
  const filtered = candidates.filter(c => {
    if ((c.labels || []).includes(slug)) return false; // hide already-tagged
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return c.key.toLowerCase().includes(s) || (c.summary || "").toLowerCase().includes(s);
  });

  const apply = async () => {
    const keys = [...picked];
    if (!keys.length) return;
    setSubmitting(true); setResult(null);
    const results = [];
    for (const k of keys) { const r = await jiraAddLabels(k, [slug]); results.push({ k, ...r }); }
    setSubmitting(false);
    const failed = results.filter(r => !r.success);
    if (failed.length === 0) {
      setResult({ success: true, message: `Tagged ${keys.length} item${keys.length === 1 ? "" : "s"} to ${slug}. Refreshing…` });
      setTimeout(() => onApplied(), 1500);
    } else {
      setResult({ success: false, message: `${failed.length} of ${keys.length} failed (${failed.map(x => x.k).join(", ")}). ${failed[0].message || ""}` });
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1200, display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: "40px 16px" }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)" }} />
      <div style={{ position: "relative", width: 520, maxWidth: "100%", background: C.card, borderRadius: 12, boxShadow: "0 12px 48px rgba(0,0,0,0.28)", display: "flex", flexDirection: "column", maxHeight: "80vh", animation: "fadeIn 0.2s ease" }}>
        <div style={{ padding: "18px 22px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexShrink: 0 }}>
          <div>
            <div style={{ fontFamily: "'Source Serif 4',Georgia,serif", fontSize: 17, fontWeight: 700, color: C.ink }}>Tag work to this goal</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 3, lineHeight: 1.5, maxWidth: "48ch" }}>
              Pick the epics and tasks that serve <strong>{goal.goal?.goal || goal.summary}</strong>. Selected items get the <code style={{ fontFamily: "'DM Mono',monospace", background: C.paper, padding: "1px 5px", borderRadius: 3 }}>{slug}</code> label so they count as contributing work.
            </div>
          </div>
          <button onClick={onClose} title="Close" style={{ border: "none", background: "none", fontSize: 22, lineHeight: 1, color: C.muted, cursor: "pointer", flexShrink: 0 }}>×</button>
        </div>

        <div style={{ padding: "12px 22px", borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter by key or title…" style={GOAL_INPUT_STYLE} />
        </div>

        <div style={{ padding: "6px 12px", overflowY: "auto", flex: 1 }}>
          {filtered.length === 0 ? (
            <div style={{ padding: "24px 12px", textAlign: "center", fontSize: 13, color: C.muted }}>
              {candidates.length ? "Nothing left to tag — everything matching is already on this goal." : "No epics or tasks loaded."}
            </div>
          ) : filtered.map(c => {
            const on = picked.has(c.key);
            return (
              <div key={c.key} onClick={() => toggle(c.key)} style={{
                display: "flex", alignItems: "center", gap: 10, padding: "7px 10px", borderRadius: 6, cursor: "pointer",
                background: on ? C.accentLt : "transparent",
              }}
                onMouseEnter={ev => { if (!on) ev.currentTarget.style.background = C.paper; }}
                onMouseLeave={ev => { if (!on) ev.currentTarget.style.background = "transparent"; }}>
                <span style={{ width: 16, height: 16, borderRadius: 4, border: `1.5px solid ${on ? C.accent : C.border}`, background: on ? C.accent : "#fff", color: "#fff", fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{on ? "✓" : ""}</span>
                <span style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", padding: "1px 5px", borderRadius: 3, background: c.isEpic ? "#EEEDFE" : C.paper, color: c.isEpic ? "#534AB7" : C.muted, border: c.isEpic ? "none" : `1px solid ${C.border}`, flexShrink: 0 }}>{c.isEpic ? "Epic" : "Task"}</span>
                <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: C.blue, flexShrink: 0 }}>{c.key}</span>
                <span style={{ fontSize: 13, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.summary}</span>
              </div>
            );
          })}
        </div>

        <div style={{ padding: "14px 22px", borderTop: `1px solid ${C.border}`, background: C.paper, borderRadius: "0 0 12px 12px", flexShrink: 0 }}>
          {result && (
            <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 10, color: result.success ? "#1D7A56" : "#B33B3B" }}>
              {result.success ? `✓ ${result.message}` : `Some tags didn't apply: ${result.message}`}
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
            <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.4 }}>
              {picked.size} selected. Each tag is a write you'll approve.
            </div>
            <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
              <button onClick={onClose} disabled={submitting} style={{ padding: "8px 16px", border: `1px solid ${C.border}`, borderRadius: 7, background: "#fff", color: C.ink, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
              <button onClick={apply} disabled={submitting || picked.size === 0} style={{
                padding: "8px 18px", border: "none", borderRadius: 7, fontSize: 13, fontWeight: 700, fontFamily: "inherit",
                cursor: (submitting || picked.size === 0) ? "not-allowed" : "pointer",
                background: (submitting || picked.size === 0) ? "#B7B4AA" : C.ink, color: "#fff",
              }}>{submitting ? "Tagging…" : `Tag ${picked.size || ""} item${picked.size === 1 ? "" : "s"}`.trim()}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ═══════════════ VISION LAYER UI (v44) ═══════════════ */
function EyeIcon({ size = 18, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

/* The masthead Vision bar — fixed under the title, above the tabs, on every view.
   Eye + "<ORG> VISION" + status; an org switcher when the template spans >1 org; a
   "+ New Vision" affordance. Renders the statement + pillar chips (hover = delivers),
   a Draft "awaiting approval" footnote, and a source link. */
function VisionStrip({ visions, sel, onSel, onNewVision, loading }) {
  const orgs = DASHBOARD_TEMPLATE.orgs;
  const multi = orgs.length > 1;
  const v = visions && visions[sel];
  const oc = ORGS[PREFIX_ORG[sel]] || ORGS.CoStar;
  const tint = oc.ring; // lighter accent, legible on the dark masthead
  const seg = on => ({
    border: "none", cursor: "pointer", fontSize: 11.5, fontWeight: 700, fontFamily: "'Outfit',sans-serif",
    padding: "5px 12px", borderRadius: 6, letterSpacing: "0.02em",
    background: on ? tint : "transparent", color: on ? "#1B1B18" : "rgba(255,255,255,0.72)",
  });
  const orgName = p => (p === "PAT" ? "Patter" : p === "COS" ? "CoStar" : "OPAL");
  return (
    <div style={{ marginTop: 14, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 10, padding: "11px 14px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 11, flexWrap: "wrap" }}>
        <EyeIcon size={22} color={tint} />
        <span style={{ fontSize: 12, letterSpacing: "0.13em", textTransform: "uppercase", fontWeight: 800, color: "#fff" }}>
          {PREFIX_ORG[sel] ? `${PREFIX_ORG[sel].toUpperCase()} VISION` : "VISION"}
        </span>
        {v && v.status === "Draft" && (
          <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", color: "#E8C15A", background: "rgba(232,193,90,0.14)", border: "1px solid rgba(232,193,90,0.45)", padding: "2px 7px", borderRadius: 5 }}>Draft</span>
        )}
        {v && v.status === "Confirmed" && (
          <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", color: "#8FD3B1", background: "rgba(58,167,122,0.16)", border: "1px solid rgba(58,167,122,0.4)", padding: "2px 7px", borderRadius: 5 }}>Confirmed</span>
        )}
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
          {multi && (
            <div style={{ display: "inline-flex", gap: 2, background: "rgba(0,0,0,0.28)", borderRadius: 8, padding: 3 }}>
              {orgs.map(p => <button key={p} onClick={() => onSel(p)} style={seg(p === sel)}>{orgName(p)}</button>)}
            </div>
          )}
          {VISION_AUTHORING_ENABLED && (
            <button onClick={onNewVision} title={v && v.drafted ? "Edit or revise this Vision — e.g. if the accountable executive disagrees with the draft" : "Author this org's Vision"} style={{
              display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 11px", borderRadius: 6,
              border: "1px solid rgba(255,255,255,0.22)", background: v && v.drafted ? "rgba(255,255,255,0.10)" : "transparent", color: "#fff",
              fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "'Outfit',sans-serif", whiteSpace: "nowrap",
            }}>
              {v && v.drafted ? (
                <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg> Edit Vision</>
              ) : "+ New Vision"}
            </button>
          )}
        </div>
      </div>

      {loading && !v ? (
        <div style={{ marginTop: 9, fontSize: 12.5, color: "rgba(255,255,255,0.5)", fontStyle: "italic" }}>Loading vision…</div>
      ) : v && v.drafted ? (
        <>
          <div style={{ marginTop: 9, fontSize: 14.5, lineHeight: 1.5, color: "rgba(255,255,255,0.92)", maxWidth: "82ch" }}>{v.statement}</div>
          {v.pillars.length > 0 && (
            <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginTop: 10 }}>
              {v.pillars.map((p, i) => (
                <span key={i} title={p.delivers || undefined} style={{
                  fontSize: 11.5, fontWeight: 700, color: "rgba(255,255,255,0.9)",
                  background: "rgba(255,255,255,0.08)", border: `1px solid ${tint}66`, padding: "3px 10px", borderRadius: 16,
                  cursor: p.delivers ? "help" : "default",
                }}>{p.name}</span>
              ))}
            </div>
          )}
          {v.owner && (
            <div style={{ marginTop: 8, fontSize: 10.5, color: "rgba(255,255,255,0.5)" }}>Owner (accountable exec): <span style={{ color: "rgba(255,255,255,0.72)" }}>{v.owner}</span></div>
          )}
          {v.status === "Draft" && (
            <div style={{ marginTop: 9, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.04em", color: "#E8C15A", display: "inline-flex", alignItems: "center", gap: 6 }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
                DRAFT — AWAITING EXECUTIVE APPROVAL
              </span>
              {VISION_AUTHORING_ENABLED && (
                <button onClick={onNewVision} title="Open the editor to revise the wording, or set status to Confirmed to approve" style={{ border: "none", background: "none", color: "#F0D486", textDecoration: "underline", cursor: "pointer", fontSize: 10.5, fontWeight: 700, letterSpacing: "0.03em", fontFamily: "inherit", padding: 0 }}>Revise or approve →</button>
              )}
            </div>
          )}
          {v.pageUrl && (
            <div style={{ marginTop: 9, fontSize: 10.5, color: "rgba(255,255,255,0.45)" }}>
              Source: <a href={v.pageUrl} target="_blank" rel="noopener noreferrer" style={{ color: "rgba(255,255,255,0.7)", textDecoration: "none", borderBottom: "1px dotted rgba(255,255,255,0.4)" }}>Confluence · {PREFIX_ORG[sel]} Vision</a>
            </div>
          )}
        </>
      ) : (
        <div style={{ marginTop: 9, fontSize: 13, color: "rgba(255,255,255,0.55)", fontStyle: "italic" }}>
          {PREFIX_ORG[sel]} Vision not yet drafted.{VISION_AUTHORING_ENABLED ? " Use “+ New Vision” to author it." : ""}
        </div>
      )}
    </div>
  );
}

const VISION_CHARS = [
  ["clear", "Clear", "Plain language a new hire gets on day one — no jargon."],
  ["specific", "Specific", "Concrete imagery, not “be the best.” Rules choices in and out."],
  ["future", "Future-oriented", "Describes a state you’re not in yet. If it’s already true, it’s a mission."],
  ["ambitious", "Ambitious yet real", "A stretch reachable with extraordinary effort — not a fantasy."],
  ["values", "Values-aligned", "Consistent with how the org already operates."],
];
const VISION_STEPS = ["Orient", "Statement", "Pillars", "Status", "Review"];
function visionWordCount(s) { return s && s.trim() ? s.trim().split(/\s+/).length : 0; }

/* Exec-gated New/Edit-Vision wizard. Five steps, live self-check, emits a [VISION] block
   only, then writes the org's Confluence page (propose-confirm surfaces the approval). */
function VisionWizard({ prefix, existing, onClose, onSaved }) {
  const [step, setStep] = useState(0);
  const [f, setF] = useState({
    prefix: prefix || "COS",
    statement: existing && existing.drafted ? existing.statement : "",
    owner: existing && existing.owner ? existing.owner : "",
    status: existing && existing.status ? existing.status : "Draft",
    pillars: existing && existing.pillars && existing.pillars.length
      ? existing.pillars.map(p => ({ name: p.name || "", delivers: p.delivers || "" }))
      : [{ name: "", delivers: "" }, { name: "", delivers: "" }, { name: "", delivers: "" }],
  });
  const [checks, setChecks] = useState({ clear: false, specific: false, future: false, ambitious: false, values: false });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const oc = ORGS[PREFIX_ORG[f.prefix]] || ORGS.CoStar;
  const IN = GOAL_INPUT_STYLE;

  const namedPillars = f.pillars.filter(p => p.name.trim()).length;
  const wc = visionWordCount(f.statement);
  const canSubmit = f.statement.trim() && namedPillars >= VISION_PILLAR_MIN && !submitting && !(result && result.success);

  const setPillar = (i, key, val) => setF(p => { const pl = p.pillars.slice(); pl[i] = { ...pl[i], [key]: val }; return { ...p, pillars: pl }; });
  const addPillar = () => setF(p => (p.pillars.length < VISION_PILLAR_MAX ? { ...p, pillars: [...p.pillars, { name: "", delivers: "" }] } : p));
  const delPillar = i => setF(p => ({ ...p, pillars: p.pillars.filter((_, j) => j !== i) }));

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true); setResult(null);
    const res = await visionWrite(f.prefix, { statement: f.statement, owner: f.owner, status: f.status, pillars: f.pillars });
    setSubmitting(false); setResult(res);
    if (res.success) setTimeout(() => onSaved(), 1400);
  };

  const label = `${PREFIX_ORG[f.prefix].toUpperCase()} VISION`;
  const orgName = p => (p === "PAT" ? "Patter" : p === "COS" ? "CoStar" : "OPAL");

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1200, display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: "40px 16px" }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)" }} />
      <div style={{ position: "relative", width: 580, maxWidth: "100%", background: C.card, borderRadius: 12, boxShadow: "0 12px 48px rgba(0,0,0,0.28)", animation: "fadeIn 0.2s ease" }}>
        {/* Header */}
        <div style={{ padding: "16px 22px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <EyeIcon size={19} color={oc.accent} />
            <div style={{ fontFamily: "'Source Serif 4',Georgia,serif", fontSize: 18, fontWeight: 700, color: C.ink }}>{existing && existing.drafted ? "Edit" : "Create"} a Vision</div>
          </div>
          <button onClick={onClose} title="Close" style={{ border: "none", background: "none", fontSize: 22, lineHeight: 1, color: C.muted, cursor: "pointer" }}>×</button>
        </div>

        {/* Step dots */}
        <div style={{ display: "flex", gap: 5, padding: "12px 22px", borderBottom: `1px solid ${C.border}`, flexWrap: "wrap" }}>
          {VISION_STEPS.map((s, i) => (
            <span key={s} style={{
              fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 20,
              background: i === step ? oc.accent : i < step ? oc.bg : "#F2F1EC",
              color: i === step ? "#fff" : i < step ? oc.text : C.muted,
            }}>{i + 1} · {s}</span>
          ))}
        </div>

        {/* Body */}
        <div style={{ padding: "18px 22px", minHeight: 250 }}>
          {step === 0 && (
            <>
              <div style={{ fontWeight: 700, fontSize: 14, color: C.ink, marginBottom: 4 }}>First — vision, not mission.</div>
              <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5, marginBottom: 14 }}>
                Quick test: <strong>“What do you want to achieve?”</strong> is your vision (the future state). <strong>“How?”</strong> is your mission (what you do today). This captures the <strong>vision</strong>; mission &amp; tagline live on the org’s Confluence page.
              </div>
              <div style={{ fontSize: 11, fontWeight: 600, color: C.ink, marginBottom: 6 }}>Which organization is this vision for?</div>
              <div style={{ display: "flex", gap: 8 }}>
                {DASHBOARD_TEMPLATE.orgs.map(p => {
                  const on = f.prefix === p; const pc = ORGS[PREFIX_ORG[p]] || ORGS.CoStar;
                  return <button key={p} onClick={() => set("prefix", p)} style={{ flex: 1, padding: "8px 0", borderRadius: 7, cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit", border: on ? `2px solid ${pc.accent}` : `1px solid ${C.border}`, background: on ? pc.bg : "#fff", color: on ? pc.text : C.muted }}>{orgName(p)}</button>;
                })}
              </div>
              <div style={{ marginTop: 14, background: C.paper, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", fontSize: 12, lineHeight: 1.5, color: C.ink }}>
                <span style={{ color: C.accent, fontWeight: 700 }}>Good — Tesla:</span> “To accelerate the world’s transition to sustainable energy.” &nbsp;·&nbsp; <span style={{ color: C.warn, fontWeight: 700 }}>Weak:</span> “To deliver exceptional value to our customers.” (true of any company; guides no decision.)
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <div style={{ fontWeight: 700, fontSize: 14, color: C.ink, marginBottom: 4 }}>Write the statement.</div>
              <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5, marginBottom: 12 }}>One future-oriented sentence. Aim for the {VISION_WORD_TARGET[0]}–{VISION_WORD_TARGET[1]} word band — short enough to repeat from memory.</div>
              <textarea value={f.statement} onChange={e => set("statement", e.target.value)} placeholder="A world where…" style={{ ...IN, minHeight: 74, resize: "vertical", lineHeight: 1.5 }} />
              <div style={{ fontSize: 11.5, marginTop: 5, color: C.muted }}>
                <strong style={{ color: C.ink }}>{wc}</strong> words — {wc === 0 ? "start typing…" : wc < 8 ? <span style={{ color: C.gold }}>might be too broad to guide decisions</span> : wc > VISION_WORD_TARGET[1] ? <span style={{ color: C.gold }}>getting long — if you can’t repeat it, trim it</span> : <span style={{ color: C.accent }}>good length — repeatable from memory</span>}
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: C.muted, margin: "14px 0 6px" }}>Self-check — the five marks of a strong vision</div>
              {VISION_CHARS.map(c => (
                <label key={c[0]} style={{ display: "flex", alignItems: "flex-start", gap: 9, padding: "6px 0", fontSize: 12.5, lineHeight: 1.4, cursor: "pointer" }}>
                  <input type="checkbox" checked={checks[c[0]]} onChange={e => setChecks(p => ({ ...p, [c[0]]: e.target.checked }))} style={{ marginTop: 2 }} />
                  <span><strong>{c[1]}</strong><span style={{ display: "block", color: C.muted }}>{c[2]}</span></span>
                </label>
              ))}
            </>
          )}

          {step === 2 && (
            <>
              <div style={{ fontWeight: 700, fontSize: 14, color: C.ink, marginBottom: 4 }}>Break it into {VISION_PILLAR_MIN}–{VISION_PILLAR_MAX} pillars.</div>
              <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5, marginBottom: 12 }}>Pillars are the enduring focus areas that turn the vision into where you invest — the bridge to execution. <strong>Goals ladder up to these.</strong> Each gets a one-line “what it delivers.”</div>
              {f.pillars.map((p, i) => (
                <div key={i} style={{ border: `1px solid ${C.border}`, borderRadius: 9, padding: "10px 11px", marginBottom: 8, position: "relative" }}>
                  {f.pillars.length > 1 && <button onClick={() => delPillar(i)} title="Remove" style={{ position: "absolute", top: 7, right: 9, border: "none", background: "none", color: C.muted, cursor: "pointer", fontSize: 16, lineHeight: 1 }}>×</button>}
                  <input value={p.name} onChange={e => setPillar(i, "name", e.target.value)} placeholder={`Pillar ${i + 1} name (e.g. Reach)`} style={{ ...IN, marginBottom: 6 }} />
                  <input value={p.delivers} onChange={e => setPillar(i, "delivers", e.target.value)} placeholder="What it delivers — one line" style={IN} />
                </div>
              ))}
              <button onClick={addPillar} disabled={f.pillars.length >= VISION_PILLAR_MAX} style={{ width: "100%", padding: 8, borderRadius: 8, border: `1px dashed ${oc.accent}`, background: oc.bg, color: oc.text, fontWeight: 700, fontSize: 13, cursor: f.pillars.length >= VISION_PILLAR_MAX ? "not-allowed" : "pointer", opacity: f.pillars.length >= VISION_PILLAR_MAX ? 0.5 : 1, fontFamily: "inherit" }}>+ Add pillar</button>
              <div style={{ fontSize: 11.5, marginTop: 6, color: namedPillars < VISION_PILLAR_MIN ? C.gold : C.accent }}>
                {namedPillars < VISION_PILLAR_MIN ? `${namedPillars} of ${VISION_PILLAR_MIN}–${VISION_PILLAR_MAX} — add at least ${VISION_PILLAR_MIN - namedPillars} more` : `${namedPillars} pillars — in the sweet spot`}
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <div style={{ fontWeight: 700, fontSize: 14, color: C.ink, marginBottom: 4 }}>Status &amp; owner.</div>
              <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5, marginBottom: 12 }}>A vision is a leadership artifact — keep it <strong>Draft</strong> until the accountable executive signs off. Draft shows a “DRAFT — AWAITING EXECUTIVE APPROVAL” flag on the dashboard; setting it to <strong>Confirmed</strong> is the approval — the flag comes off.</div>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: C.muted, marginBottom: 6 }}>Status</div>
              <div style={{ display: "flex", gap: 16, marginBottom: 14 }}>
                {VISION_STATUS_OPTIONS.map(s => (
                  <label key={s} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 600, cursor: "pointer", fontSize: 13 }}>
                    <input type="radio" name="vstatus" checked={f.status === s} onChange={() => set("status", s)} /> {s}
                  </label>
                ))}
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: C.muted, marginBottom: 6 }}>Owner (accountable exec)</div>
              <input value={f.owner} onChange={e => set("owner", e.target.value)} placeholder="e.g. David Comora" style={IN} />
              <div style={{ marginTop: 12, background: C.paper, border: `1px solid ${C.border}`, borderRadius: 8, padding: "9px 11px", fontSize: 11.5, color: C.muted, lineHeight: 1.5 }}>
                Phase 2 (noted, not built): a manager-proposes / exec-approves workflow. For now, authoring is exec-owned and status is set here.
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <div style={{ fontWeight: 700, fontSize: 14, color: C.ink, marginBottom: 4 }}>Review &amp; save.</div>
              <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5, marginBottom: 12 }}>This writes the {PREFIX_ORG[f.prefix]} Vision page in Confluence as <strong>{f.status}</strong>. You’ll get an approval prompt before it commits.</div>
              <div style={{ background: C.paper, border: `1px solid ${C.border}`, borderLeft: `3px solid ${oc.accent}`, borderRadius: 8, padding: "11px 13px", fontSize: 12.5, lineHeight: 1.55, color: C.ink }}>
                <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: C.muted }}>{label} · {f.status}</div>
                <div style={{ marginTop: 7 }}>{f.statement || <em style={{ color: C.muted }}>(no statement)</em>}</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 9 }}>
                  {f.pillars.filter(p => p.name.trim()).map((p, i) => <span key={i} title={p.delivers} style={{ fontSize: 11, fontWeight: 700, color: oc.text, background: oc.bg, border: `1px solid ${oc.ring}`, padding: "2px 9px", borderRadius: 16 }}>{p.name}</span>)}
                </div>
                {f.owner && <div style={{ marginTop: 9, fontSize: 11.5, color: C.muted }}>Owner: {f.owner}</div>}
              </div>
              {namedPillars < VISION_PILLAR_MIN && <div style={{ marginTop: 10, fontSize: 11.5, color: C.gold }}>Add at least {VISION_PILLAR_MIN} pillars (step 3) before saving.</div>}
              {result && (
                <div style={{ marginTop: 12, padding: "9px 12px", borderRadius: 8, fontSize: 12.5, background: result.success ? C.accentLt : C.warnLt, color: result.success ? C.accentDk : C.warn }}>
                  {result.success ? "Saved — the dashboard will refresh the vision." : `Couldn’t save: ${result.message}`}
                </div>
              )}
            </>
          )}
        </div>

        {/* Nav */}
        <div style={{ display: "flex", justifyContent: "space-between", padding: "14px 22px", borderTop: `1px solid ${C.border}` }}>
          <button onClick={() => (step === 0 ? onClose() : setStep(step - 1))} style={{ padding: "9px 18px", borderRadius: 8, border: `1px solid ${C.border}`, background: "#fff", color: C.ink, fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>{step === 0 ? "Cancel" : "← Back"}</button>
          {step < 4 ? (
            <button onClick={() => setStep(step + 1)} style={{ padding: "9px 20px", borderRadius: 8, border: "none", background: oc.accent, color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Next →</button>
          ) : (
            <button onClick={submit} disabled={!canSubmit} style={{ padding: "9px 20px", borderRadius: 8, border: "none", background: canSubmit ? oc.accent : C.border, color: "#fff", fontWeight: 700, fontSize: 13, cursor: canSubmit ? "pointer" : "not-allowed", fontFamily: "inherit" }}>{submitting ? "Saving…" : result && result.success ? "Saved ✓" : "Save Vision"}</button>
          )}
        </div>
      </div>
    </div>
  );
}


export default function Dashboard() {
  const [view, setView] = useState("whatsnew");
  const [orgFilter, setOrgFilter] = useState("All");
  const [ownerFilter, setOwnerFilter] = useState("All");
  const [activityFilter, setActivityFilter] = useState("All"); // "All", "Active", "Inactive"
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(new Set());
  const [detailItem, setDetailItem] = useState(null);
  const [activeApproval, setActiveApproval] = useState(null);
  const [actionPending, setActionPending] = useState(null); // "approve"|"info"|"decline"|null
  const [actionComment, setActionComment] = useState("");
  const [actionResult, setActionResult] = useState(null); // {success, message}|null
  const [actingAs, setActingAs] = useState("David Comora"); // who is taking the action
  const [infoTooltip, setInfoTooltip] = useState(null); // workstream key showing tooltip
  const [openGuide, setOpenGuide] = useState(null); // which guide is expanded in the Guides tab
  const [activityOpen, setActivityOpen] = useState(false); // Activity (comment ticker) drawer open
  const [expandedTasks, setExpandedTasks] = useState(new Set()); // task keys showing detail
  const [expandedGoals, setExpandedGoals] = useState(new Set()); // goal keys showing detail (notes, meta, comments)
  const [expandedGoalWork, setExpandedGoalWork] = useState(new Set()); // goal keys showing the tagged contributing-work list
  const [createGoalOpen, setCreateGoalOpen] = useState(false); // "Create New Goal" modal (v43)
  const [tagWorkGoal, setTagWorkGoal] = useState(null);        // goal object whose "associate work" modal is open (v43)
  const [visions, setVisions] = useState({});                  // prefix -> vision object (v44)
  const [visionSel, setVisionSel] = useState(DASHBOARD_TEMPLATE.orgs[0]); // selected org in the masthead Vision bar
  const [visionsLoading, setVisionsLoading] = useState(true);
  const [newVisionOpen, setNewVisionOpen] = useState(false);   // New/Edit-Vision wizard open
  const [activeBlockedItem, setActiveBlockedItem] = useState(null); // blocked item detail
  const [remoteLinksByKey, setRemoteLinksByKey] = useState({}); // key -> {confluenceLinks, webLinks, loading, done} (lazy remote-link fetch)
  const [cohesionTimeByKey, setCohesionTimeByKey] = useState({}); // key -> {data, loading, done, error} (lazy Cohesion-time fetch, ENG-18)
  const [blockedComment, setBlockedComment] = useState("");
  const [blockedActionPending, setBlockedActionPending] = useState(false);
  const [blockedActionResult, setBlockedActionResult] = useState(null);

  // Live data state
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false); // background refresh while showing cached data
  const [loadMsg, setLoadMsg] = useState("Connecting to Jira...");
  const [epics, setEpics] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [goals, setGoals] = useState([]);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [highlights, setHighlights] = useState([]);
  const [cacheAge, setCacheAge] = useState(null); // ms since cache was written
  const [snapshotSaving, setSnapshotSaving] = useState(false);
  const [snapshotResult, setSnapshotResult] = useState(null); // {success, message, url}

  /* Live priority reorder (drag-and-drop on the portfolio) */
  const [reorderMode, setReorderMode] = useState(false);
  const [draftOrder, setDraftOrder] = useState(null);   // array of epic keys, the dragged order
  const [dragKey, setDragKey] = useState(null);          // key currently being dragged
  const [savingOrder, setSavingOrder] = useState(false);
  const [orderMsg, setOrderMsg] = useState(null);        // status line after save
  const [localExecOverride, setLocalExecOverride] = useState({}); // {key: rank} applied this session after a save

  /* ─── LOAD CACHED DATA (instant) ─────────────────── */
  const loadFromCache = useCallback(async () => {
    const cached = await cacheGet(CACHE_KEY);
    if (cached && cached.data && cached.timestamp) {
      const age = Date.now() - cached.timestamp;
      const { epics: cEpics, tasks: cTasks, highlights: cHighlights, goals: cGoals } = cached.data;
      if (cEpics?.length > 0 || cTasks?.length > 0) {
        setEpics(cEpics || []);
        setTasks(cTasks || []);
        setGoals(cGoals || []);
        setHighlights(cHighlights || []);
        setCacheAge(age);
        setLastRefresh(new Date(cached.timestamp));
        setLoading(false); // show cached data immediately
        return true;
      }
    }
    return false;
  }, []);

  /* ─── LOAD VISIONS (v44) ─────────────────────────────
     Paint from a small dedicated cache instantly, then refresh live from the three
     Confluence Vision pages and re-cache. */
  const loadVisions = useCallback(async () => {
    const cached = await cacheGet(VISION_CACHE_KEY);
    if (cached && cached.data && Object.keys(cached.data).length) {
      setVisions(cached.data);
      setVisionsLoading(false);
    }
    try {
      const v = await visionGetAll();
      if (v && Object.keys(v).length) {
        setVisions(v);
        await cacheSet(VISION_CACHE_KEY, v);
      }
    } catch (e) {
      console.warn("Vision load failed:", e);
    } finally {
      setVisionsLoading(false);
    }
  }, []);

  /* ─── FETCH LIVE DATA (parallel) ─────────────────── */
  const fetchData = useCallback(async (isBackground = false) => {
    if (isBackground) {
      setRefreshing(true);
    } else {
      setLoading(true);
      setLoadMsg("Connecting to Jira...");
    }
    setError(null);

    try {
      if (!isBackground) setLoadMsg("Fetching from Jira (4 queries in parallel)...");

      // Fire ALL queries simultaneously instead of sequentially
      const [epicRaw, taskRaw, highlightRaw, goalRaw] = await Promise.all([
        jiraSearchReliable(`project = ${PROJECT_KEY} AND issuetype = Epic ORDER BY key ASC`, 50),
        jiraSearchReliable(`project = ${PROJECT_KEY} AND issuetype not in (Epic, Goal) ORDER BY priority DESC, key ASC`, 100),
        jiraSearchReliable(`labels = "brief-highlight" ORDER BY updated DESC`, 20),
        jiraSearchReliable(`project = ${PROJECT_KEY} AND issuetype = Goal ORDER BY key ASC`, 30),
      ]);

      // Parse
      const parsedEpics = parseIssues(Array.isArray(epicRaw) ? epicRaw : []);
      const parsedTasks = parseIssues(Array.isArray(taskRaw) ? taskRaw : []);
      const parsedHighlights = parseIssues(Array.isArray(highlightRaw) ? highlightRaw : []);

      // Goal issues → goal objects. Each Goal issue carries its [GOAL] block in the
      // description and a goal-<slug> label that binds it to its contributing work.
      const parsedGoalIssues = parseIssues(Array.isArray(goalRaw) ? goalRaw : []);
      const parsedGoals = parsedGoalIssues.map(gi => {
        const parsed = parseGoalBlock(gi.description);
        if (!parsed) return null;
        const slug = (gi.labels || []).find(l => l.startsWith("goal-")) || null;
        return {
          key: gi.key,
          summary: gi.summary,
          slug,
          org: parsed.org || gi.org,
          goal: parsed,
          // Carry through for the goal detail panel + comment thread (goals are Jira issues).
          description: gi.description,
          recentComments: gi.recentComments,
          lastComment: gi.lastComment,
          totalComments: gi.totalComments,
          updated: gi.updated,
        };
      }).filter(Boolean);
      console.log(`[Dashboard] Parsed ${parsedGoals.length} goals`);

      console.log(`[Dashboard] Parsed: ${parsedEpics.length} epics, ${parsedTasks.length} tasks, ${parsedHighlights.length} highlights`);

      // FALLBACK: If epic query returned items but they weren't parsed as epics,
      // or if the epic query failed but tasks have parentKeys, reconstruct epics from task data
      let finalEpics = parsedEpics;
      if (parsedEpics.length === 0 && parsedTasks.length > 0) {
        console.log("[Dashboard] Epic parse returned 0 — attempting reconstruction from task parentKeys");
        
        // Strategy 1: Check if epicRaw has data that just didn't parse as an array
        if (epicRaw && !Array.isArray(epicRaw) && epicRaw.raw) {
          console.log("[Dashboard] Epic raw was not an array — MCP returned unstructured content");
        }

        // Strategy 2: Some tasks may actually BE epics if the MCP lumped everything together
        // Check if any "tasks" are actually epics based on issuetype
        const epicsInTasks = parsedTasks.filter(t => t.isEpic);
        if (epicsInTasks.length > 0) {
          console.log(`[Dashboard] Found ${epicsInTasks.length} epics misclassified in task results`);
          finalEpics = epicsInTasks;
        }

        // Strategy 3: Build synthetic epic entries from unique parentKeys in the tasks
        if (finalEpics.length === 0) {
          const parentKeys = [...new Set(parsedTasks.map(t => t.parentKey).filter(Boolean))];
          if (parentKeys.length > 0) {
            console.log(`[Dashboard] Building ${parentKeys.length} synthetic workstreams from parentKeys: ${parentKeys.join(", ")}`);
            finalEpics = parentKeys.map(pk => ({
              key: pk,
              summary: pk, // Will show as "LEAD-1" etc — not ideal but functional
              status: "in_progress",
              statusName: "In Progress",
              priority: "Medium",
              assignee: "—",
              labels: [],
              org: "CoStar",
              isEpic: true,
              parentKey: null,
              components: [],
              updated: null,
              created: null,
              description: "",
              lastComment: null,
            }));
          }
        }
      }

      // ALSO: Check if tasks have no parentKeys at all — might mean the MCP stripped parent info
      if (finalEpics.length === 0 && parsedTasks.length > 0) {
        const withParent = parsedTasks.filter(t => t.parentKey);
        const withoutParent = parsedTasks.filter(t => !t.parentKey);
        console.log(`[Dashboard] Tasks with parentKey: ${withParent.length}, without: ${withoutParent.length}`);
      }

      if (finalEpics.length === 0 && parsedTasks.length === 0) {
        setError("Received data but couldn't parse structured issues. The MCP returned unstructured content. Try refreshing.");
        console.log("Epic raw:", epicRaw);
        console.log("Task raw:", taskRaw);
      }

      const taskFetchFailed = !Array.isArray(taskRaw);

      setEpics(finalEpics);
      setTasks(parsedTasks.filter(t => !t.isEpic && !t.isGoal)); // exclude any epics/goals we pulled out
      setGoals(parsedGoals);
      setHighlights(parsedHighlights);
      setLastRefresh(new Date());
      setCacheAge(null);

      if (taskFetchFailed && finalEpics.length > 0) {
        // Workstreams came back but the task list didn't parse cleanly even after the retry.
        // Tell the user plainly rather than silently showing an empty board.
        setError("Workstreams loaded, but the task list didn't finish loading. Click Retry to pull the tasks.");
      }

      // Write to persistent cache ONLY on a healthy load — a partial fetch must never poison the
      // cache (otherwise the next open would show workstreams with 0 tasks until a manual refresh).
      if (Array.isArray(taskRaw) && parsedTasks.length > 0) {
        await cacheSet(CACHE_KEY, {
          epics: parsedEpics,
          tasks: parsedTasks,
          highlights: parsedHighlights,
          goals: parsedGoals,
        });
      }
    } catch (err) {
      setError(err.message || "Couldn't load from Jira — try again.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  /* ─── OPTIMISTIC UPDATE HELPERS ───────────────────
     After a write, update just the touched item in local state instead of reloading the
     whole board, then quietly reconcile that one issue against the server. Keeps comment
     and review actions instant; the heavy full fetch is reserved for Refresh / first load. */
  const patchItemByKey = useCallback((key, patchFn) => {
    setTasks(prev => prev.map(it => it.key === key ? { ...it, ...patchFn(it) } : it));
    setEpics(prev => prev.map(it => it.key === key ? { ...it, ...patchFn(it) } : it));
  }, []);

  const reconcileItem = useCallback(async (key) => {
    try {
      const raw = await jiraSearch(`key = ${key}`, 1);
      if (Array.isArray(raw) && raw.length) {
        const parsed = parseIssues(raw);
        const fresh = parsed && parsed[0];
        if (fresh && fresh.key === key) patchItemByKey(key, () => fresh);
      }
    } catch {
      /* leave the optimistic state in place if the single-issue refresh fails */
    }
  }, [patchItemByKey]);

  /* ─── STARTUP: cache first, then background refresh ─ */
  useEffect(() => {
    (async () => {
      const hadCache = await loadFromCache();
      if (hadCache) {
        // Show cached data immediately, refresh in background
        fetchData(true);
      } else {
        // No cache — full loading state
        fetchData(false);
      }
      loadVisions(); // v44 — independent of the Jira fetch
    })();
  }, [loadFromCache, fetchData, loadVisions]);

  /* ─── WRITE-BACK ACTION HANDLER ──────────────────── */
  const executeAction = useCallback(async (action, item) => {
    setActionPending(action);
    setActionResult(null);

    try {
      let transitionId, commentText;

      if (action === "reviewed") {
        transitionId = TRANSITIONS.REVIEWED;
        commentText = `👁️ *Reviewed via Executive Dashboard* — ${actingAs}${actionComment.trim() ? `\n\n${actionComment}` : ""}`;
      } else if (action === "info") {
        transitionId = TRANSITIONS.AWAITING_INFO;
        commentText = `❓ *More info requested via Executive Dashboard* — ${actingAs}${actionComment.trim() ? `\n\n${actionComment}` : ""}`;
      }

      // Step 1: Add comment
      if (commentText) {
        const commentResult = await jiraComment(item.key, commentText);
        if (!commentResult.success) {
          setActionResult({ success: false, message: `Comment failed: ${commentResult.message}` });
          setActionPending(null);
          return;
        }
      }

      // Step 2: Transition
      const transResult = await jiraTransition(item.key, transitionId);
      if (transResult.success) {
        setActionResult({
          success: true,
          message: action === "reviewed" ? `${item.key} marked Reviewed — back to the owner`
            : `${item.key} moved to Awaiting Info — owner to supply detail`
        });
        // Optimistic: move just this item to its new status locally + show the note,
        // then reconcile that one issue in the background. No full board reload.
        const newStatus = action === "reviewed" ? "reviewed" : "awaiting_info";
        patchItemByKey(item.key, (it) => {
          const c = { author: actingAs, body: commentText, created: new Date().toISOString() };
          return {
            status: newStatus,
            recentComments: [...(it.recentComments || []), c].slice(-4),
            lastComment: c,
            totalComments: (it.totalComments || 0) + 1,
            updated: c.created,
          };
        });
        reconcileItem(item.key);
        setTimeout(() => {
          setActiveApproval(null);
          setActionResult(null);
          setActionComment("");
        }, 2000);
      } else {
        setActionResult({ success: false, message: `Transition failed: ${transResult.message}` });
      }
    } catch (err) {
      setActionResult({ success: false, message: err.message });
    } finally {
      setActionPending(null);
    }
  }, [actionComment, actingAs, patchItemByKey, reconcileItem]);

  /* ─── BLOCKED: PROVIDE DIRECTION HANDLER ─────────── */
  const provideDirection = useCallback(async (item, unblock) => {
    if (!blockedComment.trim()) {
      setBlockedActionResult({ success: false, message: "Please add a comment with your direction" });
      return;
    }
    setBlockedActionPending(true);
    setBlockedActionResult(null);

    try {
      const prefix = unblock ? "🟢 *Direction provided — unblocked via Executive Dashboard*" : "💬 *Comment via Executive Dashboard*";
      const commentText = `${prefix} — ${actingAs}\n\n${blockedComment}`;

      // Step 1: Add comment
      const commentResult = await jiraComment(item.key, commentText);
      if (!commentResult.success) {
        setBlockedActionResult({ success: false, message: `Comment failed: ${commentResult.message}` });
        setBlockedActionPending(false);
        return;
      }

      // Step 2: Transition to In Progress if unblocking
      if (unblock) {
        const transResult = await jiraTransition(item.key, TRANSITIONS.IN_PROGRESS);
        if (!transResult.success) {
          setBlockedActionResult({ success: false, message: `Comment posted but transition failed: ${transResult.message}` });
          setBlockedActionPending(false);
          return;
        }
      }

      setBlockedActionResult({
        success: true,
        message: unblock ? `${item.key} unblocked and moved to In Progress` : `Comment added to ${item.key}`
      });

      // Optimistic: append the direction comment locally (and flip to In Progress if
      // unblocking), then reconcile this one issue. No full board reload.
      patchItemByKey(item.key, (it) => {
        const c = { author: actingAs, body: commentText, created: new Date().toISOString() };
        const patch = {
          recentComments: [...(it.recentComments || []), c].slice(-4),
          lastComment: c,
          totalComments: (it.totalComments || 0) + 1,
          updated: c.created,
        };
        if (unblock) patch.status = "in_progress";
        return patch;
      });
      reconcileItem(item.key);
      setTimeout(() => {
        setActiveBlockedItem(null);
        setBlockedActionResult(null);
        setBlockedComment("");
      }, 2000);
    } catch (err) {
      setBlockedActionResult({ success: false, message: err.message });
    } finally {
      setBlockedActionPending(false);
    }
  }, [blockedComment, actingAs, patchItemByKey, reconcileItem]);

  /* ─── ADD COMMENT FROM THREAD ──────────────────────── */
  const addThreadComment = useCallback(async (issueKey, commentText) => {
    const body = `💬 ${actingAs}: ${commentText}`;
    const result = await jiraComment(issueKey, body);
    if (!result.success) throw new Error(result.message);
    // Optimistic: drop the comment into the thread locally, then reconcile this one issue.
    patchItemByKey(issueKey, (it) => {
      const c = { author: actingAs, body, created: new Date().toISOString() };
      return {
        recentComments: [...(it.recentComments || []), c].slice(-4),
        lastComment: c,
        totalComments: (it.totalComments || 0) + 1,
        updated: c.created,
      };
    });
    reconcileItem(issueKey);
  }, [actingAs, patchItemByKey, reconcileItem]);

  /* ─── SAVE WEEKLY SNAPSHOT TO CONFLUENCE ────────── */
  const CONFLUENCE_BRIEFINGS_PARENT_ID = "45350913"; // Executive Briefings page
  const CONFLUENCE_SPACE_ID = "44924932"; // LEAD space

  const saveWeeklySnapshot = useCallback(async () => {
    setSnapshotSaving(true);
    setSnapshotResult(null);

    try {
      const snapDate = new Date();
      const dateStr = snapDate.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
      const shortDate = snapDate.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
      const timeStr = snapDate.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });

      // Build status counts
      const doneCount = tasks.filter(t => t.status === "done").length;
      const activeCount = tasks.filter(t => t.status === "in_progress").length;
      const blockedCount = tasks.filter(t => t.status === "blocked").length;
      const waitingCount = tasks.filter(t => t.status === "waiting").length;
      const backlogCount = tasks.filter(t => t.status === "backlog" || t.status === "selected").length;
      const doneWeek = tasks.filter(t => t.status === "done" && isThisWeek(t.updated));
      const blocked = tasks.filter(t => t.status === "blocked");
      const waiting = tasks.filter(t => t.status === "waiting");
      const inProg = tasks.filter(t => t.status === "in_progress");

      // Build HTML content for the Confluence page
      let html = `<div data-type="panel-info"><p><strong>Weekly Status Snapshot</strong> — captured ${dateStr} at ${timeStr} from the Executive Portfolio Dashboard. This is a point-in-time record; the live dashboard may show different data.</p></div>`;

      // Metrics summary
      html += `<h2>Portfolio Summary</h2>`;
      html += `<table><thead><tr><th>Metric</th><th>Count</th></tr></thead><tbody>`;
      html += `<tr><td>Total Workstreams</td><td>${epics.length}</td></tr>`;
      html += `<tr><td>Total Tasks</td><td>${tasks.length}</td></tr>`;
      html += `<tr><td><strong>Done This Week</strong></td><td><strong>${doneWeek.length}</strong></td></tr>`;
      html += `<tr><td>In Progress</td><td>${activeCount}</td></tr>`;
      html += `<tr><td>Blocked</td><td>${blockedCount}</td></tr>`;
      html += `<tr><td>Awaiting Review</td><td>${waitingCount}</td></tr>`;
      html += `<tr><td>Backlog / Selected</td><td>${backlogCount}</td></tr>`;
      html += `<tr><td>Done (Total)</td><td>${doneCount}</td></tr>`;
      html += `</tbody></table>`;

      // Completed this week
      html += `<h2>Completed This Week (${doneWeek.length})</h2>`;
      if (doneWeek.length === 0) {
        html += `<p><em>No items completed this week.</em></p>`;
      } else {
        html += `<table><thead><tr><th>Key</th><th>Summary</th><th>Assignee</th><th>Org</th></tr></thead><tbody>`;
        doneWeek.forEach(t => { html += `<tr><td>${t.key}</td><td>${t.summary}</td><td>${t.assignee}</td><td>${t.org}</td></tr>`; });
        html += `</tbody></table>`;
      }

      // In progress
      html += `<h2>In Progress (${inProg.length})</h2>`;
      if (inProg.length > 0) {
        html += `<table><thead><tr><th>Key</th><th>Summary</th><th>Assignee</th><th>Org</th><th>Priority</th></tr></thead><tbody>`;
        inProg.forEach(t => { html += `<tr><td>${t.key}</td><td>${t.summary}</td><td>${t.assignee}</td><td>${t.org}</td><td>${t.priority}</td></tr>`; });
        html += `</tbody></table>`;
      }

      // Blocked
      if (blocked.length > 0) {
        html += `<h2>Blocked (${blocked.length})</h2>`;
        html += `<table><thead><tr><th>Key</th><th>Summary</th><th>Assignee</th><th>Org</th><th>Last Comment</th></tr></thead><tbody>`;
        blocked.forEach(t => {
          const comment = t.lastComment ? `${t.lastComment.author}: ${t.lastComment.body.substring(0, 150)}` : "No context provided";
          html += `<tr><td>${t.key}</td><td>${t.summary}</td><td>${t.assignee}</td><td>${t.org}</td><td>${comment}</td></tr>`;
        });
        html += `</tbody></table>`;
      }

      // Awaiting Review
      if (waiting.length > 0) {
        html += `<h2>Awaiting Review (${waiting.length})</h2>`;
        html += `<table><thead><tr><th>Key</th><th>Summary</th><th>Assignee</th><th>Days Waiting</th><th>What's Needed</th></tr></thead><tbody>`;
        waiting.forEach(t => {
          const days = daysSince(t.updated);
          const ask = t.lastComment ? t.lastComment.body.substring(0, 150) : "No comment — check Jira";
          html += `<tr><td>${t.key}</td><td>${t.summary}</td><td>${t.assignee}</td><td>${days}d</td><td>${ask}</td></tr>`;
        });
        html += `</tbody></table>`;
      }

      // Team Highlights
      if (highlights.length > 0) {
        html += `<h2>Team Highlights (${highlights.length})</h2>`;
        html += `<table><thead><tr><th>Key</th><th>Summary</th><th>Status</th><th>Highlight</th></tr></thead><tbody>`;
        highlights.forEach(t => {
          const note = t.lastComment ? `${t.lastComment.author}: ${t.lastComment.body.substring(0, 150)}` : "Flagged, no comment";
          html += `<tr><td>${t.key}</td><td>${t.summary}</td><td>${t.statusName || t.status}</td><td>${note}</td></tr>`;
        });
        html += `</tbody></table>`;
      }

      // Workstream status — counts only (no synthetic %, matching the dashboard's Status Mix framing)
      html += `<h2>Workstream Status</h2>`;
      html += `<table><thead><tr><th>Workstream</th><th>Org</th><th>Priority</th><th>Done</th><th>Total</th></tr></thead><tbody>`;
      const _epicKeys = new Set(epics.map(x => x.key));
      const _taskByKey = {}; tasks.forEach(t => { _taskByKey[t.key] = t; });
      const _resolveEpic = (t) => {
        if (!t || !t.parentKey) return null;
        if (_epicKeys.has(t.parentKey)) return t.parentKey;
        const p = _taskByKey[t.parentKey];
        return (p && p.parentKey && _epicKeys.has(p.parentKey)) ? p.parentKey : null;
      };
      epics.forEach(e => {
        const children = tasks.filter(t => _resolveEpic(t) === e.key);
        const done = children.filter(c => c.status === "done").length;
        html += `<tr><td>${escHtml(e.summary)}</td><td>${escHtml(e.org)}</td><td>${escHtml(e.priority)}</td><td>${done}</td><td>${children.length}</td></tr>`;
      });
      html += `</tbody></table>`;

      // Workstream status narratives — the full description (Summary / Status / Next Steps) per
      // workstream, rendered faithfully. Epics keep this rich content in the description (the Next
      // Step field is task-level only), so we render the whole thing rather than slicing a section.
      html += `<h2>Workstream Status Narratives</h2>`;
      epics.forEach(e => {
        html += `<h3>${escHtml(e.summary)} — ${escHtml(e.org)} · ${escHtml(e.key)}</h3>`;
        if (typeof e.description === "string" && e.description.trim()) html += mdToHtml(e.description);
        else if (e.statusUpdate && e.statusUpdate.text) html += `<p>${escHtml(e.statusUpdate.text)}</p>`;
        else html += `<p><em>No status narrative set in Jira.</em></p>`;
      });

      html += `<p><em>Snapshot generated by the Executive Portfolio Dashboard at ${timeStr} on ${dateStr}.</em></p>`;

      // Create the Confluence page via MCP
      const pageTitle = `Weekly Status — ${shortDate}`;

      const result = await confluenceCreatePage({
        spaceId: CONFLUENCE_SPACE_ID,
        parentId: CONFLUENCE_BRIEFINGS_PARENT_ID,
        title: pageTitle,
        html,
      });

      if (result.success || result.pageId) {
        const url = result.url || `https://costarpartners.atlassian.net/wiki/spaces/LEAD/pages/${result.pageId}`;
        setSnapshotResult({ success: true, message: `Snapshot saved: "${pageTitle}"`, url });
      } else {
        setSnapshotResult({ success: false, message: result.error || "Failed to save snapshot. Try again." });
      }
    } catch (err) {
      setSnapshotResult({ success: false, message: `Error: ${err.message}` });
    } finally {
      setSnapshotSaving(false);
    }
  }, [tasks, epics, highlights]);


  /* ─── DERIVED DATA ────────────────────────────────── */
  const allItems = tasks;
  const totalBlocked = allItems.filter(i => i.status === "blocked").length;
  const totalActive = allItems.filter(i => i.status === "in_progress").length;
  const totalDone = allItems.filter(i => i.status === "done").length;
  const awaitingItems = allItems.filter(i => i.status === "waiting");
  const blockedItems = allItems.filter(i => i.status === "blocked");
  const inProgressItems = allItems.filter(i => i.status === "in_progress");
  const doneThisWeek = allItems.filter(i => i.status === "done" && isThisWeek(i.updated));

  // Owner-action queues — the mirror of "Awaiting Review." Reviewed = David gave his direction
  // (in a comment), the owner's turn. Awaiting Info = David needs more before he can review.
  const reviewedItems = allItems
    .filter(i => i.status === "reviewed")
    .sort((a, b) => daysSince(b.updated) - daysSince(a.updated));
  const awaitingInfoItems = allItems
    .filter(i => i.status === "awaiting_info")
    .sort((a, b) => daysSince(b.updated) - daysSince(a.updated));

  // Resolve each task to its owning Epic. Tasks under an Epic have parentKey = the Epic.
  // Sub-tasks have parentKey = a Task, so we take one extra hop (sub-task → parent Task →
  // that Task's Epic) and roll the sub-task up under the same workstream.
  const epicKeySet = useMemo(() => new Set(epics.map(e => e.key)), [epics]);
  const tasksByKey = useMemo(() => {
    const m = {};
    tasks.forEach(t => { m[t.key] = t; });
    return m;
  }, [tasks]);
  const resolveEpicKey = useCallback((t) => {
    if (!t || !t.parentKey) return null;
    if (epicKeySet.has(t.parentKey)) return t.parentKey;             // parent is an Epic
    const parent = tasksByKey[t.parentKey];                           // parent is a Task →
    if (parent && parent.parentKey && epicKeySet.has(parent.parentKey)) return parent.parentKey; // use that Task's Epic
    return null;                                                      // genuinely no Epic
  }, [epicKeySet, tasksByKey]);

  // Build workstreams from Epics + their child Tasks (sub-tasks rolled up to the Epic)
  const workstreams = useMemo(() => {
    return epics.map(epic => {
      const children = tasks.filter(t => resolveEpicKey(t) === epic.key);
      return {
        key: epic.key,
        name: epic.summary,
        org: epic.org,
        pri: epic.priority,
        owner: epic.assignee,
        components: epic.components,
        labels: epic.labels || [],
        description: epic.description,
        statusUpdate: extractStatusUpdate(epic.description),
        execPriority: epic.execPriority,
        recentComments: epic.recentComments,
        lastComment: epic.lastComment,
        totalComments: epic.totalComments,
        cohesionProjectId: epic.cohesionProjectId, // Epic-level ClockIn mapping (whole project)
        items: children,
        doneCount: children.filter(c => c.status === "done").length,
        totalCount: children.length,
      };
    });
  }, [epics, tasks, resolveEpicKey]);

  // Orphan tasks (no resolvable parent Epic, even after the sub-task hop)
  const orphanTasks = useMemo(() => {
    return tasks.filter(t => !resolveEpicKey(t));
  }, [epics, tasks, resolveEpicKey]);

  // Filtered workstreams — sorted by David's exec priority ranking (then priority, then key)
  const PRI_SORT = { High: 0, Medium: 1, Low: 2 };
  // David's ranked priorities → Epic keys. BACKSTOP ONLY once the Epic "Exec Priority"
  // field exists (see EXEC_PRIORITY_FIELD): used for any Epic that has no field value yet.
  // To change ordering today, edit a number here; once the field is live, set it in Jira instead.
  // Unranked epics fall below the ranked set (never hidden).
  const EXEC_PRIORITY_FALLBACK = { "LEAD-3": 1, "LEAD-4": 2, "LEAD-8": 3, "LEAD-1": 4, "LEAD-123": 5, "LEAD-109": 6, "LEAD-112": 7, "LEAD-111": 8 };
  // Resolved rank for a workstream: the Jira Exec Priority field if set, else the fallback map, else 999.
  // Resolved rank for a workstream: session override (post-save) → Jira Exec Priority field → fallback map → 999.
  const execRank = (ws) => {
    const o = ws && localExecOverride[ws.key];
    if (Number.isFinite(o)) return o;
    return Number.isFinite(ws && ws.execPriority) ? ws.execPriority : (EXEC_PRIORITY_FALLBACK[ws && ws.key] ?? 999);
  };
  // key → workstream lookup, for resolving the dragged order back to objects
  const wsByKey = useMemo(() => Object.fromEntries(workstreams.map(w => [w.key, w])), [workstreams]);

  // ── Activity feed (comment ticker) ───────────────────────────────────────
  // Flattens recent comments across exec-reportable workstreams and their tasks
  // into one running feed, newest first. Each entry carries its item + workstream
  // so it can open straight to the work. Scoped to exec-reportable items (the
  // dashboard's data set) — routine Jira chatter elsewhere never enters the feed.
  const activityFeed = useMemo(() => {
    const entries = [];
    const pushFrom = (item, wsName, isEpic) => {
      (item.recentComments || []).forEach((c, idx) => {
        if (!c || !c.body) return;
        entries.push({
          id: `${item.key}-${c.created || ""}-${idx}`,
          key: item.key, summary: item.summary, org: item.org,
          wsName, isEpic, item,
          author: c.author || "Team", body: c.body, created: c.created || null,
        });
      });
    };
    allItems.forEach(t => {
      const ek = resolveEpicKey(t) || t.parentKey;
      const wsName = (ek && wsByKey[ek]?.name) || "Unassigned";
      pushFrom(t, wsName, false);
    });
    workstreams.forEach(w => {
      pushFrom({ key: w.key, summary: w.name, org: w.org, recentComments: w.recentComments }, w.name, true);
    });
    entries.sort((a, b) => (b.created ? new Date(b.created).getTime() : 0) - (a.created ? new Date(a.created).getTime() : 0));
    return entries;
  }, [allItems, workstreams, wsByKey, resolveEpicKey]);

  // Comments in the last 7 days — drives the Activity badge.
  const activityRecentCount = useMemo(() => {
    const since = new Date(); since.setDate(since.getDate() - 7);
    return activityFeed.filter(e => e.created && new Date(e.created) >= since).length;
  }, [activityFeed]);

  // Open an item from the feed: land on Portfolio, expand its workstream + task,
  // close the drawer, and scroll the work into view.
  const openItemFromActivity = useCallback((entry) => {
    setActivityOpen(false);
    setView("portfolio");
    setDetailItem(null);
    const ek = entry.isEpic ? entry.key : (resolveEpicKey(entry.item) || entry.item.parentKey);
    if (ek) setExpanded(p => { const n = new Set(p); n.add(ek); return n; });
    if (!entry.isEpic) setExpandedTasks(p => { const n = new Set(p); n.add(entry.key); return n; });
    setTimeout(() => {
      const el = document.getElementById(`task-${entry.key}`) || document.getElementById(`ws-${entry.key}`);
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 120);
  }, [resolveEpicKey]);

  // Jump from a Goal's contributing-work row to that workstream on the Portfolio view.
  const openWorkstream = useCallback((epicKey) => {
    if (!epicKey) return;
    setView("portfolio");
    setDetailItem(null);
    setExpanded(p => { const n = new Set(p); n.add(epicKey); return n; });
    setTimeout(() => {
      const el = document.getElementById(`ws-${epicKey}`);
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 120);
  }, []);

  // Jump to a specific goal on the Goals view (used by the Portfolio goal chip).
  const openGoal = useCallback((goalKey) => {
    if (!goalKey) return;
    setView("goals");
    setTimeout(() => {
      const el = document.getElementById(`goal-${goalKey}`);
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 120);
  }, []);

  const filtered = useMemo(() => {
    let ws = workstreams;
    if (orgFilter !== "All") ws = ws.filter(w => w.org === orgFilter);
    if (activityFilter !== "All") {
      ws = ws.filter(w => {
        const hasActivity = w.items.some(i => hasRecentDiscussion(i)) ||
          w.items.some(i => ["in_progress", "waiting", "blocked"].includes(i.status));
        return activityFilter === "Active" ? hasActivity : !hasActivity;
      });
    }
    if (search) {
      const q = search.toLowerCase();
      ws = ws.filter(w =>
        w.name.toLowerCase().includes(q) ||
        w.items.some(i => i.summary.toLowerCase().includes(q) || i.assignee.toLowerCase().includes(q))
      );
    }
    if (ownerFilter !== "All") {
      const match = (name) => ownerFilter === "Unassigned"
        ? (!name || name === "Unassigned" || name === "—")
        : name === ownerFilter;
      ws = ws
        .map(w => {
          const items = w.items.filter(i => match(i.assignee));
          if (items.length === 0 && !match(w.owner)) return null;
          return { ...w, items, doneCount: items.filter(c => c.status === "done").length, totalCount: items.length };
        })
        .filter(Boolean);
    }
    return [...ws].sort((a, b) => {
      const ra = execRank(a), rb = execRank(b);
      if (ra !== rb) return ra - rb;
      const pa = PRI_SORT[a.pri] ?? 1, pb = PRI_SORT[b.pri] ?? 1;
      if (pa !== pb) return pa - pb;
      return String(a.key).localeCompare(String(b.key), undefined, { numeric: true });
    });
  }, [workstreams, orgFilter, activityFilter, search, ownerFilter, localExecOverride]);

  /* ── Live priority reorder ── */
  // Reorder is only meaningful on the full, unfiltered portfolio (a partial list can't express global order).
  const canReorder = orgFilter === "All" && activityFilter === "All" && ownerFilter === "All" && !search;
  // The list the portfolio renders: dragged draft while reordering, else the normal sorted list.
  const portfolioList = (reorderMode && draftOrder)
    ? draftOrder.map(k => wsByKey[k]).filter(Boolean)
    : filtered;
  const enterReorder = () => {
    setExpanded(new Set());            // collapse so rows are uniform height while dragging
    setDraftOrder(filtered.map(w => w.key));
    setOrderMsg(null);
    setReorderMode(true);
  };
  const cancelReorder = () => { setReorderMode(false); setDraftOrder(null); setDragKey(null); };
  const reorderDraft = (fromKey, overKey) => {
    setDraftOrder(prev => {
      if (!prev) return prev;
      const arr = [...prev];
      const from = arr.indexOf(fromKey), to = arr.indexOf(overKey);
      if (from < 0 || to < 0 || from === to) return prev;
      arr.splice(from, 1);
      arr.splice(to, 0, fromKey);
      return arr;
    });
  };
  const saveOrder = async () => {
    if (!draftOrder) return;
    setSavingOrder(true);
    setOrderMsg(null);
    // Renumber to sparse tens so future single inserts never need a cascade.
    const target = draftOrder.map((key, i) => ({ key, value: (i + 1) * 10 }));
    // Only write Epics whose value actually changes.
    const changed = target.filter(t => {
      const cur = Number.isFinite(localExecOverride[t.key]) ? localExecOverride[t.key] : wsByKey[t.key]?.execPriority;
      return cur !== t.value;
    });
    // Apply to the session immediately so the view reflects the new order even before refetch.
    setLocalExecOverride(prev => ({ ...prev, ...Object.fromEntries(target.map(t => [t.key, t.value])) }));
    if (!EXEC_PRIORITY_FIELD) {
      setSavingOrder(false); setReorderMode(false);
      setOrderMsg("Order updated for this session. (Exec Priority field not wired, so it isn't saved to Jira yet.)");
      return;
    }
    let ok = 0; const failures = [];
    for (const t of changed) {
      const r = await jiraSetExecPriority(t.key, t.value);
      if (r.success) ok++; else failures.push(t.key);
    }
    setSavingOrder(false);
    setReorderMode(false);
    if (!changed.length) setOrderMsg("No changes to save.");
    else if (failures.length) setOrderMsg(`Saved ${ok} of ${changed.length} to Jira. Couldn't save: ${failures.join(", ")} — try Reorder → Save again, or refresh.`);
    else setOrderMsg(`Priority order saved to Jira (${ok} updated). Others see it on their next dashboard load/refresh.`);
  };

  // Group in-progress by assignee
  const inProgressByAssignee = useMemo(() => {
    const map = {};
    inProgressItems.forEach(item => {
      const a = item.assignee;
      if (!map[a]) map[a] = [];
      map[a].push(item);
    });
    return Object.entries(map).sort((a, b) => b[1].length - a[1].length);
  }, [inProgressItems]);

  const toggle = id => setExpanded(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleTask = id => setExpandedTasks(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleGoal = id => setExpandedGoals(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleGoalWork = id => setExpandedGoalWork(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  /* ── LAZY REMOTE-LINK FETCH ──────────────────────────
     The bulk JQL search can't return remote links (web links / Confluence
     "mentioned in"), so we fetch them per issue the first time an item's panel
     opens, then merge them into whatever the search did return. */
  const ensureRemoteLinks = useCallback((key) => {
    if (!key) return;
    setRemoteLinksByKey(prev => {
      if (prev[key] && (prev[key].loading || prev[key].done)) return prev; // already fetched / in flight
      // kick off the fetch (outside the state updater's pure path)
      jiraGetRemoteLinks(key).then(res => {
        setRemoteLinksByKey(p => ({ ...p, [key]: { confluenceLinks: res.confluenceLinks || [], webLinks: res.webLinks || [], loading: false, done: true, error: !!res.error } }));
      });
      return { ...prev, [key]: { confluenceLinks: [], webLinks: [], loading: true, done: false } };
    });
  }, []);

  /* ── LAZY COHESION-TIME FETCH (ENG-18) ───────────────
     No Cohesion connector here, so this reads the cohesionGetTime stub
     (engineering swaps in the real backend on port). Fetch once, the
     first time a task panel with a cohesionProjectId opens — same
     keyed-map pattern as the remote links above. */
  const ensureCohesionTime = useCallback((key, projectId) => {
    if (!key || !projectId) return;
    setCohesionTimeByKey(prev => {
      if (prev[key] && (prev[key].loading || prev[key].done)) return prev; // already fetched / in flight
      cohesionGetTime(projectId).then(data => {
        setCohesionTimeByKey(p => ({ ...p, [key]: { data, loading: false, done: true, error: !data || !!data.error } }));
      }).catch(() => {
        setCohesionTimeByKey(p => ({ ...p, [key]: { data: null, loading: false, done: true, error: true } }));
      });
      return { ...prev, [key]: { data: null, loading: true, done: false, error: false } };
    });
  }, []);

  // Merge search-returned links with lazily-fetched remote links; de-dupe by URL.
  const docsFor = useCallback((item) => {
    const extra = (item && remoteLinksByKey[item.key]) || {};
    const dedupe = (arr) => { const seen = new Set(); return (arr || []).filter(l => l && l.url && !seen.has(l.url) && seen.add(l.url)); };
    return {
      confluenceLinks: dedupe([...((item && item.confluenceLinks) || []), ...((extra.confluenceLinks) || [])]),
      webLinks: dedupe([...((item && item.webLinks) || []), ...((extra.webLinks) || [])]),
      linksLoading: !!extra.loading,
    };
  }, [remoteLinksByKey]);

  // Fetch remote links when a detail surface opens.
  useEffect(() => { if (activeApproval?.key) ensureRemoteLinks(activeApproval.key); }, [activeApproval?.key, ensureRemoteLinks]);
  useEffect(() => { if (activeBlockedItem?.key) ensureRemoteLinks(activeBlockedItem.key); }, [activeBlockedItem?.key, ensureRemoteLinks]);
  useEffect(() => { if (detailItem?.key) ensureRemoteLinks(detailItem.key); }, [detailItem?.key, ensureRemoteLinks]);
  useEffect(() => { expandedTasks.forEach(k => ensureRemoteLinks(k)); }, [expandedTasks, ensureRemoteLinks]);

  // Fetch Cohesion time when a task panel (or the detail view) opens, if it's mapped.
  useEffect(() => { if (detailItem?.key && detailItem.cohesionProjectId) ensureCohesionTime(detailItem.key, detailItem.cohesionProjectId); }, [detailItem?.key, detailItem?.cohesionProjectId, ensureCohesionTime]);
  useEffect(() => { expandedTasks.forEach(k => { const t = tasksByKey[k]; if (t && t.cohesionProjectId) ensureCohesionTime(k, t.cohesionProjectId); }); }, [expandedTasks, tasksByKey, ensureCohesionTime]);
  // Workstream (Epic) level: an Epic mapped to a ClockIn project shows the whole
  // project's time at the top of its expanded body.
  useEffect(() => { epics.forEach(e => { if (expanded.has(e.key) && e.cohesionProjectId) ensureCohesionTime(e.key, e.cohesionProjectId); }); }, [expanded, epics, ensureCohesionTime]);

  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  return (
    <div style={{ fontFamily: "'Outfit',sans-serif", background: C.paper, color: C.ink, minHeight: "100vh" }}>
      <link href="https://fonts.googleapis.com/css2?family=Source+Serif+4:wght@400;600;700&family=Outfit:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      <style>{`
        @keyframes pulse { 0%,100%{opacity:.3;transform:scale(.8)} 50%{opacity:1;transform:scale(1)} }
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes fadeIn { from{opacity:0;transform:translateY(-4px)} to{opacity:1;transform:none} }
        @keyframes slideInRight { from{transform:translateX(100%)} to{transform:none} }
        
        /* ── RESPONSIVE GRID CLASSES ── */
        .ws-header, .ws-row { display: grid; grid-template-columns: 5px 1fr 80px 100px 120px 90px; gap: 10px; align-items: center; }
        .ws-skeleton { display: grid; grid-template-columns: 5px 1fr 80px 100px 120px 90px; gap: 10px; align-items: center; }
        .task-row { display: grid; grid-template-columns: minmax(0, 1fr) 110px 60px auto 20px; gap: 8px; align-items: center; }
        .task-row > :first-child { min-width: 0; }
        .metrics-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 10px; }
        .awaiting-row { display: grid; grid-template-columns: 1fr 80px 80px 60px; gap: 10px; align-items: center; }
        .blocked-row { display: grid; grid-template-columns: 1fr 80px 80px 60px; gap: 10px; align-items: center; }
        .team-row { display: grid; grid-template-columns: 1fr 80px 60px 20px; gap: 8px; align-items: center; }

        /* Mobile-only elements — hidden on desktop */
        .mobile-only { display: none; }

        @media (max-width: 768px) {
          /* Portfolio: switch to flex row */
          .ws-header { display: none; }
          .ws-row { display: flex; flex-wrap: wrap; gap: 6px; }
          .ws-row .ws-col-bar { width: 5px; align-self: stretch; border-radius: 2px; flex-shrink: 0; }
          .ws-row .ws-col-name { flex: 1; min-width: 0; }
          .ws-row .ws-col-priority, .ws-row .ws-col-progress, .ws-row .ws-col-mix { display: none; }
          .ws-row .ws-col-action { flex-shrink: 0; align-self: center; }

          /* Skeleton: simplified */
          .ws-skeleton { grid-template-columns: 5px 1fr 80px; }
          .ws-skeleton .sk-progress, .ws-skeleton .sk-mix, .ws-skeleton .sk-pri { display: none; }

          /* Tasks: compact */
          .task-row { grid-template-columns: minmax(0, 1fr) 90px auto 20px; }
          .task-row .task-col-pri { display: none; }

          /* Metrics: 3 across, then 2 */
          .metrics-grid { grid-template-columns: repeat(3, 1fr); }

          /* Awaiting/Blocked: name + age only */
          .awaiting-row { grid-template-columns: 1fr 50px; }
          .awaiting-row .aw-col-org, .awaiting-row .aw-col-pri { display: none; }
          .blocked-row { grid-template-columns: 1fr 50px; }
          .blocked-row .bl-col-org, .blocked-row .bl-col-pri { display: none; }

          /* Team: compact */
          .team-row { grid-template-columns: 1fr 60px 20px; }
          .team-row .tm-col-org { display: none; }

          /* Spacing */
          .dash-content { padding: 12px 14px 40px !important; }
          .dash-header-inner { padding: 14px 14px 0 !important; }

          /* Show mobile elements */
          .mobile-only { display: flex; }

          /* Guides tab: stack the two columns */
          .whatsnew-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* ── HEADER ── */}
      <div style={{ background: C.ink, color: "#fff", padding: "20px 28px 0", position: "sticky", top: 0, zIndex: 100, boxShadow: "0 2px 20px rgba(0,0,0,0.15)" }}>
        <div className="dash-header-inner" style={{ maxWidth: 1060, margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.14em", color: "#9c9a92", fontWeight: 500 }}>CoStar Partners · Leadership Priorities</div>
              <h1 style={{ fontFamily: "'Source Serif 4',Georgia,serif", fontSize: 22, fontWeight: 600, margin: "4px 0 0", color: "#fff" }}>Executive Portfolio Dashboard</h1>
            </div>
            <div style={{ textAlign: "right", fontSize: 11, color: "#9c9a92" }}>
              <div>{today}</div>
              <div style={{ marginTop: 2, display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end" }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: loading ? "#E8A02A" : refreshing ? "#4CAF50" : error ? "#B33B3B" : "#4CAF50", animation: refreshing ? "pulse 1.2s ease-in-out infinite" : "none" }} />
                {loading ? "Syncing..." : refreshing ? "Updating in background..." : error ? "Connection issue" : "Live from Jira"}
              </div>
              {lastRefresh && (
                <div style={{ marginTop: 2, fontSize: 10 }}>
                  {cacheAge ? `Showing cached data · ${Math.round(cacheAge / 60000)}m old` : `Last refresh: ${lastRefresh.toLocaleTimeString()}`}
                </div>
              )}
              <div style={{ marginTop: 2, fontSize: 10, opacity: 0.7 }}>Build {BUILD}</div>
            </div>
          </div>
          <VisionStrip visions={visions} sel={visionSel} onSel={setVisionSel} onNewVision={() => setNewVisionOpen(true)} loading={visionsLoading} />
          <div style={{ display: "flex", gap: 2, marginTop: 14, overflowX: "auto", alignItems: "center" }}>
            {[
              { key: "portfolio", label: "Portfolio" },
              { key: "goals", label: "Goals" },
              { key: "blocked", label: "Blocked", badge: totalBlocked },
              { key: "david", label: "Awaiting Review", badge: awaitingItems.length },
              { key: "reviewed", label: "Reviewed", badge: reviewedItems.length },
              { key: "awaitinginfo", label: "Awaiting Info", badge: awaitingInfoItems.length },
              { key: "meetings", label: "Meetings" },
              { key: "brief", label: "Weekly Status" },
              { key: "whatsnew", label: "Guides", dot: true },
            ].map(t => (
              <button key={t.key} onClick={() => { setView(t.key); setDetailItem(null); setActiveApproval(null); }} style={{
                padding: "8px 16px", borderRadius: "7px 7px 0 0", border: "none", cursor: "pointer",
                fontSize: 12, fontWeight: 600, fontFamily: "'Outfit',sans-serif", transition: "all 0.15s",
                background: view === t.key ? C.paper : "transparent", color: view === t.key ? C.ink : "#9c9a92",
                whiteSpace: "nowrap", position: "relative",
              }}>
                {t.label}
                {t.dot && view !== t.key && <span style={{ marginLeft: 5, display: "inline-block", width: 7, height: 7, borderRadius: "50%", background: C.accent, verticalAlign: "middle" }} />}
                {t.badge > 0 && <span style={{ marginLeft: 5, background: t.key === "reviewed" ? "#534AB7" : t.key === "awaitinginfo" ? "#B5651D" : t.key === "blocked" ? "#B33B3B" : "#E24B4A", color: "#fff", borderRadius: 8, padding: "1px 5px", fontSize: 10, fontWeight: 700 }}>{t.badge}</span>}
                {view === t.key && <span style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: C.accent, borderRadius: "3px 3px 0 0" }} />}
              </button>
            ))}
            <button onClick={() => setActivityOpen(true)} title="Recent comments across the portfolio" style={{
              marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6,
              padding: "5px 12px", borderRadius: 6, border: `1px solid rgba(255,255,255,0.2)`,
              background: activityOpen ? "rgba(255,255,255,0.12)" : "transparent", color: "#fff",
              fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "'Outfit',sans-serif", whiteSpace: "nowrap",
            }}>
              <span style={{ fontSize: 15, lineHeight: 1 }}>🔔</span>
              {activityRecentCount > 0 && <span style={{ background: C.accent, color: "#fff", borderRadius: 9, padding: "1px 6px", fontSize: 10, fontWeight: 700 }}>{activityRecentCount}</span>}
            </button>
            <button onClick={() => { fetchData(false); loadVisions(); }} disabled={loading || refreshing} title="Refresh — re-pull live from Jira" style={{
              marginLeft: 6, padding: "5px 11px", borderRadius: 6, border: `1px solid rgba(255,255,255,0.2)`,
              background: "transparent", color: "#9c9a92", fontSize: 14, fontWeight: 600, cursor: (loading || refreshing) ? "default" : "pointer",
              fontFamily: "'Outfit',sans-serif", opacity: (loading || refreshing) ? 0.5 : 1,
            }}>
              <span style={{ display: "inline-block", animation: (loading || refreshing) ? "spin 1s linear infinite" : "none" }}>⟳</span>
            </button>
          </div>
        </div>
      </div>

      {/* ── CONTENT ── */}
      <div className="dash-content" style={{ maxWidth: 1060, margin: "0 auto", padding: "20px 28px 50px" }}>

        {/* ── METRICS ── */}
        <div className="metrics-grid" style={{ marginBottom: 18 }}>
          {[
            { l: "Workstreams", v: epics.length, s: `${workstreams.filter(w => w.totalCount > 0).length} with tasks` },
            { l: "Total Tasks", v: tasks.length, s: `${totalActive} active` },
            { l: "Done This Week", v: doneThisWeek.length, s: "completed items", a: C.accent },
            { l: "Blocked", v: totalBlocked, s: "awaiting action", a: "#B33B3B" },
            { l: "Awaiting Review", v: awaitingItems.length, s: "in review queue", a: C.gold },
          ].map((m, i) => {
            const hasData = epics.length > 0 || tasks.length > 0;
            const showPlaceholder = loading && !hasData;
            return (
              <div key={i} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px", opacity: refreshing ? 0.7 : 1, transition: "opacity 0.3s" }}>
                <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", color: C.muted, fontWeight: 500 }}>{m.l}</div>
                <div style={{ fontSize: 28, fontWeight: 600, color: showPlaceholder ? C.border : (m.a || C.ink), lineHeight: 1.2, marginTop: 2 }}>{showPlaceholder ? "—" : m.v}</div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>{showPlaceholder ? "loading..." : m.s}</div>
              </div>
            );
          })}
        </div>

        {/* ── LOADING STATE — Skeleton UI ── */}
        {loading && (
          <div style={{ animation: "fadeIn 0.2s ease" }}>
            <div style={{ fontSize: 13, color: C.muted, textAlign: "center", marginBottom: 14, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              <span style={{ display: "flex", gap: 4 }}>{[0,1,2].map(i => <span key={i} style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: C.accent, animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite` }} />)}</span>
              {loadMsg}
            </div>
            {/* Skeleton workstream rows */}
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
              <div className="ws-header" style={{ padding: "7px 14px", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.07em", color: C.muted, fontWeight: 600, borderBottom: `1px solid ${C.border}`, background: C.paper }}>
                <div /><div>Workstream</div><div className="ws-col-priority">Priority</div><div className="ws-col-progress">Progress</div><div className="ws-col-mix">Status Mix</div><div />
              </div>
              {[0,1,2,3,4,5,6].map(i => (
                <div key={i} className="ws-skeleton" style={{ padding: "14px 14px", borderBottom: `1px solid ${C.border}` }}>
                  <div style={{ width: 5, height: 28, borderRadius: 2, background: C.border, opacity: 0.5 }} />
                  <div>
                    <div style={{ height: 14, width: `${60 + (i * 7) % 30}%`, background: C.border, borderRadius: 4, opacity: 0.4 }} />
                    <div style={{ height: 10, width: "40%", background: C.border, borderRadius: 3, opacity: 0.25, marginTop: 6 }} />
                  </div>
                  <div className="sk-pri" style={{ height: 18, width: 50, background: C.border, borderRadius: 4, opacity: 0.3 }} />
                  <div className="sk-progress" style={{ height: 12, width: 60, background: C.border, borderRadius: 4, opacity: 0.25 }} />
                  <div className="sk-mix" style={{ display: "flex", gap: 2 }}>
                    <div style={{ height: 6, flex: 3, background: C.border, borderRadius: 3, opacity: 0.3 }} />
                    <div style={{ height: 6, flex: 1, background: C.border, borderRadius: 3, opacity: 0.2 }} />
                  </div>
                  <div style={{ height: 24, width: 70, background: C.border, borderRadius: 5, opacity: 0.2 }} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── BACKGROUND REFRESH BANNER ── */}
        {refreshing && !loading && (
          <div style={{
            display: "flex", alignItems: "center", gap: 8, padding: "8px 14px", marginBottom: 12,
            background: C.accentLt, border: `1px solid ${C.accent}33`, borderRadius: 8, fontSize: 12, color: C.accent, fontWeight: 500,
            animation: "fadeIn 0.2s ease",
          }}>
            <span style={{ display: "flex", gap: 3 }}>{[0,1,2].map(i => <span key={i} style={{ display: "inline-block", width: 5, height: 5, borderRadius: "50%", background: C.accent, animation: `pulse 1.2s ease-in-out ${i * 0.15}s infinite` }} />)}</span>
            Refreshing data from Jira — you're viewing cached data while this updates
          </div>
        )}

        {/* ── ERROR STATE ── */}
        {error && !loading && (
          <div style={{ background: C.warnLt, border: `1px solid ${C.warn}`, borderRadius: 10, padding: "18px 22px", marginBottom: 18 }}>
            <div style={{ fontWeight: 600, color: C.warn, fontSize: 14, marginBottom: 6 }}>Connection Issue</div>
            <div style={{ fontSize: 13, color: "#5a3a2a", lineHeight: 1.6 }}>{error}</div>
            <button onClick={() => fetchData(false)} style={{ marginTop: 10, padding: "6px 16px", borderRadius: 6, border: "none", background: C.warn, color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Retry</button>
          </div>
        )}

        {/* ╔══════════════ PORTFOLIO VIEW ══════════════╗ */}
        {!loading && view === "whatsnew" && (() => {
          const activeWs = workstreams.filter(ws => ws.items.some(i => hasRecentDiscussion(i))).length;
          const touched = allItems.filter(i => daysSince(i.updated) <= 3).length;
          const changes = [
            {
              n: "01", color: C.accent,
              title: "Everything in one place, organized by workstream",
              body: `The board is organized into ${epics.length} workstreams across all three organizations. Each opens to show its work underneath — drill from a workstream into the tasks beneath it. ${allItems.length} items in total, all reachable, none scattered across other tools.`,
              tie: "Why it matters: the whole portfolio in one organized view, instead of work spread across separate documents and tools.",
              cta: "See the portfolio", act: () => { setView("portfolio"); setActivityFilter("All"); },
            },
            {
              n: "02", color: C.blue,
              title: "The whole conversation, not just the last note",
              body: "Open any item and you see the recent discussion thread in context — who said what, when — instead of a single stray comment. The full history is one click into Jira if you want it.",
              tie: "Why it matters: the thinking behind a workstream stays with the work and is easy to follow.",
              cta: "Open the portfolio", act: () => { setView("portfolio"); setActivityFilter("All"); },
            },
            {
              n: "03", color: C.gold,
              title: "See what's actually moving at a glance",
              body: `Workstreams and tasks carry an activity marker for the last seven days, and a one-tap filter separates what's active from what's quiet. Right now ${activeWs} workstream${activeWs === 1 ? " has" : "s have"} live discussion.`,
              tie: "Why it matters: active work is distinguishable from stale entries at a glance.",
              cta: "Show active work", act: () => { setView("portfolio"); setActivityFilter("Active"); },
            },
            {
              n: "04", color: C.accent,
              title: "Comment and decide without leaving this page",
              body: "Add a note to any item directly here and it posts straight to Jira under your name — no app to open, no syntax to learn. The team is notified in the tools they already live in.",
              tie: "Why it matters: review is frictionless, so it actually gets used — and decisions land in the system of record.",
              cta: "Try it in the review queue", act: () => { setView("david"); },
            },
            {
              n: "05", color: C.gold,
              title: "Every review says what's being asked — and whether it can wait",
              body: `Each item handed up for review carries a Review Ask — exactly what to look at — and a Review Type. A Decision holds the work until you act; an Acknowledge is for your awareness unless you flag something. ${awaitingItems.length} item${awaitingItems.length === 1 ? " is" : "s are"} awaiting review now.`,
              tie: "Why it matters: you know what's being asked of you, and what's blocked on you, before you open it.",
              cta: "Open your queue", act: () => { setView("david"); },
            },
            {
              n: "06", color: C.blue,
              title: "A running feed of every new comment — read it like a news update",
              body: `The Activity button (top right) opens a panel that gathers the latest comments from across the portfolio into one feed, newest first — a quick read of what's moving without opening each item. Every entry shows who said it, when, and which workstream and item it belongs to, and tapping it opens straight to that work so a comment that stands out is one tap from acting on it. The feed is scoped to exec-reportable items, so routine Jira chatter elsewhere never crowds it.${activityRecentCount > 0 ? ` ${activityRecentCount} comment${activityRecentCount === 1 ? "" : "s"} landed in the last seven days.` : ""}`,
              tie: "Why it matters: a single glance tells you what's happened since you last looked — and routes you to the item if you want to weigh in.",
              cta: "Open Activity", act: () => { setActivityOpen(true); },
            },
            {
              n: "07", color: C.accent,
              title: "Portfolio goals — the outcomes, not just the tasks",
              body: `A new Goals tab groups the portfolio's outcomes by organization — One OPAL, One CoStar, One Patter. Each goal shows its success metric and the work tagged to it, so direction and execution read together on one screen. ${goals.length} goal${goals.length === 1 ? "" : "s"} tracked now.`,
              tie: "Why it matters: you can see whether the work is moving the outcomes that matter — not just that tasks are closing.",
              cta: "See the goals", act: () => { setView("goals"); },
            },
            {
              n: "08", color: C.blue,
              title: "Time from Cohesion, on the task that owns it",
              body: "Where a task is mapped to a Cohesion project, opening it now shows that project's time — hours logged against budget, how much of the budget is consumed, and when time was last entered — right alongside the work. It's showing sample figures for now; the live Cohesion connection follows when this ports to the web host.",
              tie: "Why it matters: effort and budget sit next to the work they belong to, so a workstream running hot is visible without opening another tool.",
              cta: "See the portfolio", act: () => { setView("portfolio"); setActivityFilter("All"); },
            },
          ];
          const refined = [
            "Lives at a web address behind CoStar sign-in — nothing to install, and every item shows now that the old 100-item cap is gone.",
            "Reads cleanly on iPad and phone, not just desktop.",
            "Filter the portfolio by owner to see what any one person is carrying.",
            "Weekly Status can be saved to Confluence as a dated, searchable record.",
          ];
          const next = [
            "Cohesion time shown live from the source — the view is in place now against sample figures, with the real connection to follow when it ports to the web host.",
            "Goals that read a live current value against each target — not just the work completed toward them — so progress on the outcome shows directly.",
            "Meeting transcripts and their action items flowing straight into the right workstream — so a decision made in a call lands in the system without anyone re-typing it.",
            "Activity feed: highlighting the comments that matter most — flagging ones that mention you or land on an item awaiting your review, with a “since you last looked” marker.",
          ];
          return (
          <div style={{ animation: "fadeIn 0.25s ease" }}>
            {/* Header */}
            <div style={{ background: C.ink, borderRadius: "10px 10px 0 0", padding: "18px 22px", color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.14em", color: "#9c9a92" }}>CoStar Partners · Unified Work Management</div>
                <div style={{ fontFamily: "'Source Serif 4',Georgia,serif", fontSize: 22, fontWeight: 600, marginTop: 4 }}>Guides</div>
                <div style={{ fontSize: 12, color: "#9c9a92", marginTop: 3 }}>What this dashboard does for you, how to use it, and how it got here · {touched} item{touched === 1 ? "" : "s"} updated in the last few days</div>
              </div>
              <a href={GUIDE_URL} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none", flexShrink: 0 }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: "#fff", background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 6, padding: "7px 12px", whiteSpace: "nowrap" }}>📖 Open full guide ↗</span>
              </a>
            </div>

            {/* Executive summary band */}
            <div style={{ background: C.accentLt, borderLeft: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, padding: "16px 22px" }}>
              <div style={{ fontFamily: "'Source Serif 4',Georgia,serif", fontSize: 15, lineHeight: 1.55, color: C.accentDk }}>
                This dashboard closes the exact gaps the Loop document exposed — seeing the <strong>status</strong>, the <strong>content</strong>, and the <strong>conversation</strong> behind each workstream in one place — and adds a clear lane for the decisions waiting on you. The capabilities below are worth a minute; everything else is detail. Below them you'll find the <strong>guides</strong> and the dashboard's <strong>release history</strong>.
              </div>
            </div>

            {/* Where this fits — positioning callout (answers David's system-of-record question) */}
            <div style={{ background: C.blueLt, borderLeft: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, padding: "14px 22px", display: "flex", gap: 14, alignItems: "flex-start" }}>
              <div style={{ width: 22, height: 22, borderRadius: "50%", background: C.blue, color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, flexShrink: 0, marginTop: 1 }}>i</div>
              <div>
                <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.06em", color: C.blue, fontWeight: 700, marginBottom: 4 }}>Where this fits</div>
                <div style={{ fontSize: 13, lineHeight: 1.55, color: "#3a4654" }}>
                  This dashboard is where you <strong>see</strong> status, content, and the conversation across the portfolio. Time is entered and owned in the time and PSA systems — for tasks mapped to a Cohesion project, the dashboard now <strong>surfaces</strong> that project's time read-only, next to the work. A full PSA tool remains the future system of record for projects and time; this is the visibility layer that gets us there.
                </div>
              </div>
            </div>

            {/* Headline changes */}
            <div style={{ background: C.card, borderLeft: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, padding: "18px 22px" }}>
              <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.06em", color: C.ink, fontWeight: 700, marginBottom: 14 }}>The changes that matter to you</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {changes.map(ch => (
                  <div key={ch.n} style={{ display: "flex", gap: 14, padding: "14px 16px", border: `1px solid ${C.border}`, borderRadius: 9, background: C.paper }}>
                    <div style={{ fontFamily: "'Source Serif 4',Georgia,serif", fontSize: 22, fontWeight: 600, color: ch.color, minWidth: 30, lineHeight: 1.2 }}>{ch.n}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: C.ink, marginBottom: 4 }}>{ch.title}</div>
                      <div style={{ fontSize: 13, lineHeight: 1.55, color: "#4a4842" }}>{ch.body}</div>
                      <div style={{ fontSize: 12, color: C.muted, marginTop: 7, fontStyle: "italic", borderLeft: `2px solid ${ch.color}`, paddingLeft: 8 }}>{ch.tie}</div>
                      <button onClick={ch.act} style={{ marginTop: 10, padding: "6px 14px", borderRadius: 6, border: "none", cursor: "pointer", background: ch.color, color: "#fff", fontSize: 12, fontWeight: 600, fontFamily: "'Outfit',sans-serif" }}>{ch.cta} →</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Also refined + Coming next, side by side */}
            <div className="whatsnew-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0, borderLeft: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, borderRadius: "0 0 10px 10px", overflow: "hidden" }}>
              <div style={{ background: C.card, padding: "18px 22px", borderRight: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.06em", color: C.blue, fontWeight: 700, marginBottom: 10 }}>Also refined</div>
                {refined.map((r, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, fontSize: 12.5, lineHeight: 1.5, color: "#4a4842", padding: "5px 0", borderBottom: i < refined.length - 1 ? "1px solid rgba(0,0,0,0.04)" : "none" }}>
                    <span style={{ color: C.blue, fontWeight: 700 }}>·</span><span>{r}</span>
                  </div>
                ))}
              </div>
              <div style={{ background: "rgba(255,248,225,0.35)", padding: "18px 22px" }}>
                <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.06em", color: C.gold, fontWeight: 700, marginBottom: 10 }}>Coming next</div>
                {next.map((r, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, fontSize: 12.5, lineHeight: 1.5, color: "#4a4842", padding: "5px 0", borderBottom: i < next.length - 1 ? "1px solid rgba(0,0,0,0.04)" : "none" }}>
                    <span style={{ color: C.gold, fontWeight: 700 }}>→</span><span>{r}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* ── GUIDES (two labeled groups) ──────────── */}
            <div style={{ background: C.card, borderLeft: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, padding: "18px 22px" }}>
              <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.06em", color: C.ink, fontWeight: 700, marginBottom: 14 }}>Guides</div>
              
              <div style={{ fontSize: 11.5, textTransform: "uppercase", letterSpacing: "0.06em", color: C.blue, fontWeight: 700, marginTop: 4, marginBottom: 2 }}>Using the dashboard & your work</div>
              <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 12 }}>How to see, action, and review work — and the full dashboard reference.</div>

                {/* Creating a Goal — links out to the Confluence job aide (content lives in Confluence, no rebuild to update). */}
                <a href={GOAL_GUIDE_URL} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none", display: "block" }}>
                  <div style={{ border: `1px solid ${C.border}`, borderRadius: 9, marginBottom: 10, overflow: "hidden", background: C.paper }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", background: C.card }}>
                      <span style={{ fontSize: 18, lineHeight: 1 }}>🎯</span>
                      <span style={{ flex: 1 }}>
                        <span style={{ display: "block", fontSize: 14.5, fontWeight: 700, color: C.ink }}>Creating a Goal</span>
                        <span style={{ display: "block", fontSize: 12.5, color: C.muted, marginTop: 2 }}>Manager job aide — every field in the New Goal form, a worked example, and how to tag work to a goal.</span>
                      </span>
                      <span style={{ fontSize: 11, fontWeight: 600, color: C.accent, whiteSpace: "nowrap" }}>Read ↗</span>
                    </div>
                  </div>
                </a>
              
                {(() => {
                  const gid = "lifecycle";
                  const isOpen = openGuide === gid;
                  return (
                    <div style={{ border: `1px solid ${C.border}`, borderRadius: 9, marginBottom: 10, overflow: "hidden", background: C.paper }}>
                      <button onClick={() => setOpenGuide(isOpen ? null : gid)} style={{
                        width: "100%", textAlign: "left", display: "flex", alignItems: "center", gap: 12,
                        padding: "14px 16px", border: "none", cursor: "pointer", background: isOpen ? C.accentLt : C.card,
                        fontFamily: "'Outfit',sans-serif",
                      }}>
                        <span style={{ fontSize: 18, color: C.accent, transform: isOpen ? "rotate(90deg)" : "none", transition: "transform 0.15s", lineHeight: 1 }}>▸</span>
                        <span style={{ flex: 1 }}>
                          <span style={{ display: "block", fontSize: 14.5, fontWeight: 700, color: C.ink }}>How to Manage Work Items from Creation to Close</span>
                          <span style={{ display: "block", fontSize: 12.5, color: C.muted, marginTop: 2 }}>The full lifecycle — Backlog to Done — and the review flow, in Jira and on the dashboard.</span>
                        </span>
                        <span style={{ fontSize: 11, fontWeight: 600, color: C.accent, whiteSpace: "nowrap" }}>{isOpen ? "Hide" : "Read"}</span>
                      </button>
                      {isOpen && (
                        <div style={{ borderTop: `1px solid ${C.border}`, maxHeight: 620, overflowY: "auto" }}>
                          <style>{`.guide-lifecycle{
    --action:#003087; --risk:#C0392B; --ink:#1C1C1C; --pagebg:#F4F7FB;
    --t1fill:#D6EAF8; --border:#C8D8EC; --secondary:#5A6B7B;
    --info-h:#0E7490; --info-fill:#E0F4F7; --rowalt:#EBF3FB; --green:#2E7D32;
    --serif:Georgia,'Times New Roman',serif;
    --sans:Calibri,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;
  }.guide-lifecycle *{box-sizing:border-box}.guide-lifecycle{margin:0;background:var(--pagebg);color:var(--ink);font-family:var(--sans);
    font-size:15px;line-height:1.5;-webkit-font-smoothing:antialiased}.guide-lifecycle .wrap{max-width:760px;margin:0 auto;padding:0 20px 56px}.guide-lifecycle header.band{background:var(--action);color:#fff;margin:0 -20px 0;padding:16px 20px;
    position:sticky;top:0;z-index:5}.guide-lifecycle .wordmark{font-family:var(--serif);font-weight:700;font-size:18px;letter-spacing:.2px}.guide-lifecycle .wordmark .p{color:#9DC3F0;font-weight:400}.guide-lifecycle .kicker{font-size:10px;letter-spacing:.16em;text-transform:uppercase;color:#BBD4F2;margin-top:2px}.guide-lifecycle h1{font-family:var(--serif);font-weight:700;font-size:27px;line-height:1.2;color:var(--ink);
    margin:26px 0 4px}.guide-lifecycle .subtitle{color:var(--secondary);font-size:14px;margin:0 0 20px}.guide-lifecycle h2{font-family:var(--serif);font-weight:700;font-size:17px;color:var(--ink);
    margin:30px 0 4px;padding-bottom:5px;border-bottom:2px solid var(--action)}.guide-lifecycle .orient{color:var(--secondary);font-size:13.5px;margin:6px 0 14px}.guide-lifecycle .bottomline{border:1px solid var(--border);border-radius:8px;overflow:hidden;margin:6px 0 8px}.guide-lifecycle .bottomline .h{background:var(--action);color:#fff;font-weight:700;font-size:11px;
    letter-spacing:.14em;text-transform:uppercase;padding:8px 14px}.guide-lifecycle .bottomline .b{background:var(--t1fill);padding:12px 14px;font-size:14.5px;line-height:1.55}.guide-lifecycle .figure{background:#fff;border:1px solid var(--border);border-radius:8px;padding:14px 10px 6px;margin:4px 0 6px}.guide-lifecycle .figcap{color:var(--secondary);font-size:12px;text-align:center;margin:2px 0 2px}.guide-lifecycle dl.states{margin:4px 0 0}.guide-lifecycle dl.states>div{padding:9px 0;border-bottom:1px solid var(--border);display:flex;gap:12px;align-items:baseline}.guide-lifecycle dl.states>div:last-child{border-bottom:none}.guide-lifecycle dl.states dt{flex:0 0 132px;font-weight:700;color:var(--action);font-size:14px}.guide-lifecycle dl.states dd{margin:0;font-size:14px}.guide-lifecycle .pending{color:var(--info-h);font-weight:700;font-size:11px;letter-spacing:.06em;
    text-transform:uppercase;margin-left:6px}.guide-lifecycle .pillrow{display:flex;flex-wrap:wrap;gap:10px;margin:6px 0 12px}.guide-lifecycle .pill{flex:1 1 220px;border:1px solid var(--border);border-radius:8px;padding:10px 12px;background:#fff}.guide-lifecycle .pill .lbl{font-weight:700;color:var(--action);font-size:13px;margin-bottom:2px}.guide-lifecycle .pill .d{font-size:13.5px;color:var(--ink)}.guide-lifecycle table{width:100%;border-collapse:collapse;margin:6px 0 4px;font-size:13.5px}.guide-lifecycle th{background:var(--ink);color:#fff;text-align:left;font-size:11px;letter-spacing:.04em;
    text-transform:uppercase;font-weight:700;padding:8px 10px}.guide-lifecycle td{padding:9px 10px;border-bottom:1px solid var(--border);vertical-align:top}.guide-lifecycle tbody tr:nth-child(even){background:var(--rowalt)}.guide-lifecycle .info{background:var(--info-fill);border:1px solid #Bfe3ea;border-left:4px solid var(--info-h);
    border-radius:0 8px 8px 0;padding:11px 14px;margin:14px 0 0;font-size:13.5px}.guide-lifecycle .info .h{color:var(--info-h);font-weight:700;font-size:11px;letter-spacing:.1em;
    text-transform:uppercase;margin-bottom:4px}.guide-lifecycle footer{margin-top:30px;padding-top:14px;border-top:1px solid var(--border);
    color:var(--secondary);font-size:12px}.guide-lifecycle footer b{color:var(--ink)}.guide-lifecycle .svgbox{width:100%;height:auto;display:block}.guide-lifecycle text{font-family:var(--sans)}
  @media (max-width:520px){.guide-lifecycle dl.states>div{flex-direction:column;gap:2px}.guide-lifecycle dl.states dt{flex-basis:auto}
  }
`}</style>
                          <div className="guide-lifecycle" dangerouslySetInnerHTML={{ __html: `

  <h1>How to Manage Work Items from Creation to Close</h1>
  <p class="subtitle">One process for the leadership team — from Backlog to Done, in Jira and on the Executive Portfolio Dashboard.</p>

  <div class="bottomline">
    <div class="h">Bottom line</div>
    <div class="b">Every work item lives in <b>Jira</b> and moves through a defined path from Backlog to Done (or Archived). You can drive it from native Jira <b>or</b> the Executive Portfolio Dashboard — it is the same item in the same state, because both read and write Jira's status field directly. An item reaches David only when its owner sends it to <b>Awaiting Review</b>.</div>
  </div>

  <h2>The lifecycle at a glance</h2>
  <p class="orient">In Progress is the hub. Work flows in from Backlog, can step out to Blocked or Awaiting Review and back, and leaves as either Done or Archived.</p>
  <div class="figure">
    <svg class="svgbox" viewBox="0 0 680 410" role="img" aria-labelledby="t1 d1">
      <title id="t1">Work item lifecycle with In Progress as the hub</title>
      <desc id="d1">In Progress sits at the center. Backlog is above it with a two-way arrow. Blocked and Awaiting Review hang off the sides with two-way arrows. Done and Archived are the two terminal states below.</desc>
      <defs>
        <marker id="ar" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
          <path d="M2 1L8 5L2 9" fill="none" stroke="#5A6B7B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </marker>
      </defs>
      <!-- Backlog -->
      <rect x="255" y="60" width="170" height="56" rx="8" fill="#EBF3FB" stroke="#5A6B7B" stroke-width="1"/>
      <text x="340" y="83" text-anchor="middle" font-size="15" font-weight="700" fill="#1C1C1C">Backlog</text>
      <text x="340" y="101" text-anchor="middle" font-size="12.5" fill="#5A6B7B">Not yet started</text>
      <!-- In Progress -->
      <rect x="255" y="170" width="170" height="56" rx="8" fill="#D6EAF8" stroke="#003087" stroke-width="1.2"/>
      <text x="340" y="193" text-anchor="middle" font-size="15" font-weight="700" fill="#003087">In Progress</text>
      <text x="340" y="211" text-anchor="middle" font-size="12.5" fill="#1A5276">Owner working · the hub</text>
      <!-- Blocked -->
      <rect x="40" y="170" width="140" height="56" rx="8" fill="#FBEAE8" stroke="#C0392B" stroke-width="1"/>
      <text x="110" y="193" text-anchor="middle" font-size="15" font-weight="700" fill="#C0392B">Blocked</text>
      <text x="110" y="211" text-anchor="middle" font-size="12.5" fill="#8a2c20">Can't advance</text>
      <!-- Awaiting Review -->
      <rect x="470" y="170" width="170" height="56" rx="8" fill="#E0F4F7" stroke="#0E7490" stroke-width="1"/>
      <text x="555" y="193" text-anchor="middle" font-size="15" font-weight="700" fill="#0E7490">Awaiting Review</text>
      <text x="555" y="211" text-anchor="middle" font-size="12.5" fill="#0b5563">Sent for review</text>
      <!-- Done -->
      <rect x="190" y="290" width="150" height="56" rx="8" fill="#E6F2E6" stroke="#2E7D32" stroke-width="1"/>
      <text x="265" y="313" text-anchor="middle" font-size="15" font-weight="700" fill="#2E7D32">Done</text>
      <text x="265" y="331" text-anchor="middle" font-size="12.5" fill="#1f5a23">Complete</text>
      <!-- Archived -->
      <rect x="360" y="290" width="150" height="56" rx="8" fill="#EBF3FB" stroke="#5A6B7B" stroke-width="1"/>
      <text x="435" y="313" text-anchor="middle" font-size="15" font-weight="700" fill="#1C1C1C">Archived</text>
      <text x="435" y="331" text-anchor="middle" font-size="12.5" fill="#5A6B7B">No longer needed</text>
      <!-- arrows -->
      <line x1="340" y1="116" x2="340" y2="170" stroke="#5A6B7B" stroke-width="1.5" marker-start="url(#ar)" marker-end="url(#ar)"/>
      <line x1="180" y1="198" x2="255" y2="198" stroke="#5A6B7B" stroke-width="1.5" marker-start="url(#ar)" marker-end="url(#ar)"/>
      <line x1="425" y1="198" x2="470" y2="198" stroke="#5A6B7B" stroke-width="1.5" marker-start="url(#ar)" marker-end="url(#ar)"/>
      <line x1="305" y1="226" x2="270" y2="288" stroke="#5A6B7B" stroke-width="1.5" marker-end="url(#ar)"/>
      <line x1="385" y1="226" x2="430" y2="288" stroke="#5A6B7B" stroke-width="1.5" marker-end="url(#ar)"/>
      <!-- legend -->
      <rect x="60" y="372" width="12" height="12" rx="3" fill="#EBF3FB" stroke="#5A6B7B"/>
      <text x="78" y="382" font-size="12" fill="#5A6B7B">Queue / closed</text>
      <rect x="195" y="372" width="12" height="12" rx="3" fill="#D6EAF8" stroke="#003087"/>
      <text x="213" y="382" font-size="12" fill="#5A6B7B">In progress</text>
      <rect x="315" y="372" width="12" height="12" rx="3" fill="#E0F4F7" stroke="#0E7490"/>
      <text x="333" y="382" font-size="12" fill="#5A6B7B">Awaiting review</text>
      <rect x="455" y="372" width="12" height="12" rx="3" fill="#FBEAE8" stroke="#C0392B"/>
      <text x="473" y="382" font-size="12" fill="#5A6B7B">Blocked</text>
      <rect x="555" y="372" width="12" height="12" rx="3" fill="#E6F2E6" stroke="#2E7D32"/>
      <text x="573" y="382" font-size="12" fill="#5A6B7B">Done</text>
    </svg>
    <p class="figcap">The Backlog ↔ In Progress arrow is two-way: an active item that gets deprioritized drops back to Backlog rather than disappearing.</p>
  </div>

  <h2>What each state means</h2>
  <p class="orient">Eight states, each a Jira status. One line each.</p>
  <dl class="states">
    <div><dt>Backlog</dt><dd>Captured but not yet started. New items — and anything deprioritized out of active work — wait here.</dd></div>
    <div><dt>In Progress</dt><dd>A leadership-team member is actively working the item. This is the hub; everything passes through here.</dd></div>
    <div><dt>Blocked</dt><dd>Can't advance because of a business, technical, or other blocker. Returns to In Progress once cleared.</dd></div>
    <div><dt>Awaiting Review</dt><dd>The owner has handed the item to David — it's his turn (the chess clock is now on him).</dd></div>
    <div><dt>Reviewed</dt><dd>David has given direction in a comment. The clock returns to the owner, who takes the next step.</dd></div>
    <div><dt>Awaiting Info</dt><dd>David needs more detail before he can decide. The owner supplies it, and the item returns to Awaiting Review.</dd></div>
    <div><dt>Done</dt><dd>The owner marks the item complete. Review actions never set Done — completion is always the owner's call.</dd></div>
    <div><dt>Archived</dt><dd>Deemed no longer necessary: not done, and not sent back to Backlog. <span class="pending">Pending Jira setup</span></dd></div>
  </dl>
  <div class="info">
    <div class="h">Note</div>
    The legacy <b>Selected for Development</b> status is being retired — it isn't used in leadership work management, and the dashboard already treats it as Backlog.
  </div>

  <h2>The review flow — the chess clock</h2>
  <p class="orient">Only items an owner sends to Awaiting Review reach David. From there, two dispositions hand the clock back to the owner.</p>
  <div class="figure">
    <svg class="svgbox" viewBox="0 0 680 400" role="img" aria-labelledby="t2 d2">
      <title id="t2">The review loop and its two dispositions</title>
      <desc id="d2">From Awaiting Review, David chooses one of two dispositions. Reviewed means direction was given and the item returns to the owner in In Progress. Awaiting Info means more detail is needed, and the item loops back to Awaiting Review once the owner supplies it.</desc>
      <defs>
        <marker id="ar2" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
          <path d="M2 1L8 5L2 9" fill="none" stroke="#5A6B7B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </marker>
      </defs>
      <!-- Awaiting Review -->
      <rect x="255" y="50" width="170" height="56" rx="8" fill="#E0F4F7" stroke="#0E7490" stroke-width="1"/>
      <text x="340" y="73" text-anchor="middle" font-size="15" font-weight="700" fill="#0E7490">Awaiting Review</text>
      <text x="340" y="91" text-anchor="middle" font-size="12.5" fill="#0b5563">David's turn</text>
      <!-- Reviewed -->
      <rect x="90" y="180" width="180" height="56" rx="8" fill="#E3EDF4" stroke="#1A5276" stroke-width="1"/>
      <text x="180" y="203" text-anchor="middle" font-size="15" font-weight="700" fill="#1A5276">Reviewed</text>
      <text x="180" y="221" text-anchor="middle" font-size="12.5" fill="#1A5276">David gave direction</text>
      <!-- Awaiting Info -->
      <rect x="410" y="180" width="180" height="56" rx="8" fill="#E0F4F7" stroke="#0E7490" stroke-width="1"/>
      <text x="500" y="203" text-anchor="middle" font-size="15" font-weight="700" fill="#0E7490">Awaiting Info</text>
      <text x="500" y="221" text-anchor="middle" font-size="12.5" fill="#0b5563">Needs more detail</text>
      <!-- Back to In Progress -->
      <rect x="90" y="300" width="180" height="56" rx="8" fill="#D6EAF8" stroke="#003087" stroke-width="1.2"/>
      <text x="180" y="323" text-anchor="middle" font-size="15" font-weight="700" fill="#003087">Back to In Progress</text>
      <text x="180" y="341" text-anchor="middle" font-size="12.5" fill="#1A5276">Owner executes</text>
      <!-- arrows -->
      <line x1="300" y1="106" x2="190" y2="178" stroke="#5A6B7B" stroke-width="1.5" marker-end="url(#ar2)"/>
      <line x1="380" y1="106" x2="490" y2="178" stroke="#5A6B7B" stroke-width="1.5" marker-end="url(#ar2)"/>
      <line x1="180" y1="236" x2="180" y2="298" stroke="#5A6B7B" stroke-width="1.5" marker-end="url(#ar2)"/>
      <path d="M590 200 L620 200 L620 78 L425 78" fill="none" stroke="#5A6B7B" stroke-width="1.5" marker-end="url(#ar2)"/>
      <text x="540" y="70" text-anchor="middle" font-size="12" fill="#5A6B7B">info supplied</text>
    </svg>
    <p class="figcap">Reviewed → owner executes (then Done, Archived, or back to Backlog). Awaiting Info → owner supplies detail → re-submitted for review.</p>
  </div>

  <p class="orient">Two fields in Jira carry the request, set by the owner before sending:</p>
  <div class="pillrow">
    <div class="pill"><div class="lbl">Review Type</div><div class="d"><b>Decision</b> — David must choose or approve a direction. <b>Acknowledge</b> — David only needs to confirm he has seen it.</div></div>
    <div class="pill"><div class="lbl">Review Ask</div><div class="d">One line stating exactly what the owner needs from David — the question, the approval, or the outcome they're after.</div></div>
  </div>

  <h2>Same item, either surface</h2>
  <p class="orient">Native Jira and the Dashboard drive the identical state, because both write Jira's status field. Drive it whichever way suits you.</p>
  <table>
    <thead><tr><th>To…</th><th>In native Jira</th><th>On the Dashboard</th></tr></thead>
    <tbody>
      <tr><td>Send for review</td><td>Move the card to the <b>Awaiting Review</b> column</td><td>Owner sets it; appears in the review queue</td></tr>
      <tr><td>Give direction</td><td>Move to <b>Reviewed</b> and add a comment</td><td><b>Reviewed</b> button + comment</td></tr>
      <tr><td>Request more info</td><td>Move to <b>Awaiting Info</b></td><td><b>Request Info</b> button</td></tr>
      <tr><td>Clear a blocker</td><td>Move to <b>In Progress</b></td><td>Unblock action</td></tr>
      <tr><td>Complete</td><td>Move to <b>Done</b></td><td>Owner sets it</td></tr>
    </tbody>
  </table>
  <div class="info">
    <div class="h">Pending alignment</div>
    The Dashboard's review tab is currently labeled <b>“Awaiting David.”</b> The underlying Jira status is <b>“Awaiting Review,”</b> and the two are the same state. A pending Dashboard update will rename the tab to “Awaiting Review” so the wording matches Jira and stays generic for teams that don't have a single reviewer.
  </div>

  <footer>
    <b>How to Manage Work Items from Creation to Close</b> · v1 · June 22, 2026 · CoStar Partners — Unified Work Management.<br>
    <b>Pending configuration (not yet live):</b> add the <b>Archived</b> status to the Jira workflow · remove <b>Selected for Development</b> · rename the Dashboard tab to <b>Awaiting Review</b>.
  </footer>

` }} />
                        </div>
                      )}
                    </div>
                  );
                })()}
                {(() => {
                  const gid = "bystage";
                  const isOpen = openGuide === gid;
                  return (
                    <div style={{ border: `1px solid ${C.border}`, borderRadius: 9, marginBottom: 10, overflow: "hidden", background: C.paper }}>
                      <button onClick={() => setOpenGuide(isOpen ? null : gid)} style={{
                        width: "100%", textAlign: "left", display: "flex", alignItems: "center", gap: 12,
                        padding: "14px 16px", border: "none", cursor: "pointer", background: isOpen ? C.accentLt : C.card,
                        fontFamily: "'Outfit',sans-serif",
                      }}>
                        <span style={{ fontSize: 18, color: C.accent, transform: isOpen ? "rotate(90deg)" : "none", transition: "transform 0.15s", lineHeight: 1 }}>▸</span>
                        <span style={{ flex: 1 }}>
                          <span style={{ display: "block", fontSize: 14.5, fontWeight: 700, color: C.ink }}>See and Action My Work by Stage</span>
                          <span style={{ display: "block", fontSize: 12.5, color: C.muted, marginTop: 2 }}>Three lenses on the same Jira data — Board, List, or just ask Claude.</span>
                        </span>
                        <span style={{ fontSize: 11, fontWeight: 600, color: C.accent, whiteSpace: "nowrap" }}>{isOpen ? "Hide" : "Read"}</span>
                      </button>
                      {isOpen && (
                        <div style={{ borderTop: `1px solid ${C.border}`, maxHeight: 620, overflowY: "auto" }}>
                          <style>{`.guide-bystage{
    --action:#003087; --risk:#C0392B; --ink:#1C1C1C; --pagebg:#F4F7FB;
    --t1fill:#D6EAF8; --border:#C8D8EC; --secondary:#5A6B7B;
    --info-h:#0E7490; --info-fill:#E0F4F7; --rowalt:#EBF3FB; --green:#2E7D32; --steel:#1A5276;
    --serif:Georgia,'Times New Roman',serif;
    --sans:Calibri,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;
    --mono:'Cascadia Mono',Consolas,'SF Mono',Menlo,monospace;
  }.guide-bystage *{box-sizing:border-box}.guide-bystage{margin:0;background:var(--pagebg);color:var(--ink);font-family:var(--sans);
    font-size:15px;line-height:1.5;-webkit-font-smoothing:antialiased}.guide-bystage .wrap{max-width:760px;margin:0 auto;padding:0 20px 56px}.guide-bystage header.band{background:var(--action);color:#fff;margin:0 -20px 0;padding:16px 20px;
    position:sticky;top:0;z-index:5}.guide-bystage .wordmark{font-family:var(--serif);font-weight:700;font-size:18px;letter-spacing:.2px}.guide-bystage .wordmark .p{color:#9DC3F0;font-weight:400}.guide-bystage .kicker{font-size:10px;letter-spacing:.16em;text-transform:uppercase;color:#BBD4F2;margin-top:2px}.guide-bystage h1{font-family:var(--serif);font-weight:700;font-size:26px;line-height:1.2;color:var(--ink);margin:26px 0 4px}.guide-bystage .subtitle{color:var(--secondary);font-size:14px;margin:0 0 20px}.guide-bystage h2{font-family:var(--serif);font-weight:700;font-size:17px;color:var(--ink);
    margin:30px 0 4px;padding-bottom:5px;border-bottom:2px solid var(--action)}.guide-bystage .orient{color:var(--secondary);font-size:13.5px;margin:6px 0 14px}.guide-bystage .bottomline{border:1px solid var(--border);border-radius:8px;overflow:hidden;margin:6px 0 8px}.guide-bystage .bottomline .h{background:var(--action);color:#fff;font-weight:700;font-size:11px;
    letter-spacing:.14em;text-transform:uppercase;padding:8px 14px}.guide-bystage .bottomline .b{background:var(--t1fill);padding:12px 14px;font-size:14.5px;line-height:1.55}.guide-bystage .figure{background:#fff;border:1px solid var(--border);border-radius:8px;padding:14px 10px 6px;margin:4px 0 6px}.guide-bystage .figcap{color:var(--secondary);font-size:12px;text-align:center;margin:2px 0 2px}.guide-bystage table{width:100%;border-collapse:collapse;margin:6px 0 4px;font-size:13px}.guide-bystage th{background:var(--ink);color:#fff;text-align:left;font-size:11px;letter-spacing:.04em;
    text-transform:uppercase;font-weight:700;padding:8px 9px}.guide-bystage td{padding:9px 9px;border-bottom:1px solid var(--border);vertical-align:top}.guide-bystage td.stage{font-weight:700;color:var(--action);white-space:nowrap}.guide-bystage tbody tr:nth-child(even){background:var(--rowalt)}.guide-bystage code{font-family:var(--mono);font-size:12px;background:#EEF3FA;border:1px solid var(--border);
    border-radius:4px;padding:1px 5px;color:var(--steel)}.guide-bystage .prompts{list-style:none;padding:0;margin:6px 0 0}.guide-bystage .prompts li{border:1px solid var(--border);border-radius:8px;padding:10px 12px;margin:0 0 8px;background:#fff}.guide-bystage .prompts .say{font-size:14px;color:var(--ink)}.guide-bystage .prompts .say::before{content:"“";color:var(--secondary)}.guide-bystage .prompts .say::after{content:"”";color:var(--secondary)}.guide-bystage .prompts .jql{display:block;margin-top:4px;color:var(--secondary);font-size:12px}.guide-bystage .info{background:var(--info-fill);border:1px solid #Bfe3ea;border-left:4px solid var(--info-h);
    border-radius:0 8px 8px 0;padding:11px 14px;margin:14px 0 0;font-size:13.5px}.guide-bystage .info .h{color:var(--info-h);font-weight:700;font-size:11px;letter-spacing:.1em;text-transform:uppercase;margin-bottom:4px}.guide-bystage footer{margin-top:30px;padding-top:14px;border-top:1px solid var(--border);color:var(--secondary);font-size:12px}.guide-bystage footer b{color:var(--ink)}.guide-bystage .svgbox{width:100%;height:auto;display:block}.guide-bystage text{font-family:var(--sans)}
`}</style>
                          <div className="guide-bystage" dangerouslySetInnerHTML={{ __html: `

  <h1>How Do I — See and Action My Work by Stage</h1>
  <p class="subtitle">Three lenses on the same Jira data — Board, List, or Claude. Same items, same stages; pick the view that fits the moment.</p>

  <div class="bottomline">
    <div class="h">Bottom line</div>
    <div class="b">Your work is the <b>same set of Jira items</b> whether you look at the <b>Board</b>, the <b>List</b>, or just <b>ask Claude</b>. Filter to your own items with the board's <b>“Assigned to me”</b> quick filter (the JQL behind it is <code>assignee = currentUser()</code>), and every stage from Backlog to Done is one toggle, group, or prompt away. And you don't just <b>see</b> it through Claude — you <b>act</b> on it: ask Claude to update, transition, comment, or send an item for review, and it does the Jira work for you. Opening Jira stays an option; the one exception is <b>uploading documents</b>.</div>
  </div>

  <h2>Three lenses, one source</h2>
  <p class="orient">Jira is the system of record. Choose how you want to see it.</p>
  <div class="figure">
    <svg class="svgbox" viewBox="0 0 680 260" role="img" aria-labelledby="tl1 dl1">
      <title id="tl1">Three lenses on the same Jira data</title>
      <desc id="dl1">Your items in Jira are the single source. Three lenses read from it: the Board (drag by column), the List (group by status), and Claude prompts (ask in plain language).</desc>
      <defs>
        <marker id="ar" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
          <path d="M2 1L8 5L2 9" fill="none" stroke="#5A6B7B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </marker>
      </defs>
      <rect x="240" y="40" width="200" height="56" rx="8" fill="#EBF3FB" stroke="#5A6B7B" stroke-width="1"/>
      <text x="340" y="63" text-anchor="middle" font-size="15" font-weight="700" fill="#1C1C1C">Your items in Jira</text>
      <text x="340" y="81" text-anchor="middle" font-size="12.5" fill="#5A6B7B">System of record · by stage</text>

      <rect x="40" y="170" width="180" height="56" rx="8" fill="#D6EAF8" stroke="#003087" stroke-width="1"/>
      <text x="130" y="193" text-anchor="middle" font-size="15" font-weight="700" fill="#003087">Board</text>
      <text x="130" y="211" text-anchor="middle" font-size="12.5" fill="#1A5276">Drag by column</text>

      <rect x="250" y="170" width="180" height="56" rx="8" fill="#E0F4F7" stroke="#0E7490" stroke-width="1"/>
      <text x="340" y="193" text-anchor="middle" font-size="15" font-weight="700" fill="#0E7490">List</text>
      <text x="340" y="211" text-anchor="middle" font-size="12.5" fill="#0b5563">Group by status</text>

      <rect x="460" y="170" width="180" height="56" rx="8" fill="#E3EDF4" stroke="#1A5276" stroke-width="1"/>
      <text x="550" y="193" text-anchor="middle" font-size="15" font-weight="700" fill="#1A5276">Ask Claude</text>
      <text x="550" y="211" text-anchor="middle" font-size="12.5" fill="#1A5276">See and update</text>

      <line x1="300" y1="96" x2="140" y2="168" stroke="#5A6B7B" stroke-width="1.5" marker-end="url(#ar)"/>
      <line x1="340" y1="96" x2="340" y2="168" stroke="#5A6B7B" stroke-width="1.5" marker-end="url(#ar)"/>
      <line x1="380" y1="96" x2="540" y2="168" stroke="#5A6B7B" stroke-width="1.5" marker-end="url(#ar)"/>
    </svg>
    <p class="figcap">Same items, same stages — the Board and List are filtered views; Claude both reads and writes the same Jira data, so you can act without leaving the chat.</p>
  </div>

  <h2>Action by stage</h2>
  <p class="orient">For each stage: what you do there, and how to surface it in each lens (filtered to you).</p>
  <table>
    <thead><tr><th>Stage</th><th>What you do</th><th>Board / List</th><th>Ask Claude</th></tr></thead>
    <tbody>
      <tr><td class="stage">Backlog</td><td>Plan and prioritize what's next.</td><td>Backlog column / group</td><td>“Show me my open items”</td></tr>
      <tr><td class="stage">In Progress</td><td>Do the work; keep status current.</td><td>In Progress column / group</td><td>“Show me what I'm actively working”</td></tr>
      <tr><td class="stage">Blocked</td><td>Flag the blocker and escalate.</td><td>Blocked column / group</td><td>“Show me my blocked items”</td></tr>
      <tr><td class="stage">Awaiting Review</td><td>Hand to David with Review Type + Review Ask set.</td><td>Awaiting Review column / group</td><td>“Show me my items awaiting David's review”</td></tr>
      <tr><td class="stage">Reviewed / Awaiting Info</td><td>Act on David's direction, or supply the detail he needs.</td><td>Reviewed &amp; Awaiting Info columns / group</td><td>“Show me my items David has acted on”</td></tr>
    </tbody>
  </table>
  <div class="info">
    <div class="h">For David — the review lens</div>
    Board: toggle the team filter off and read the <b>Awaiting Review</b> column. Claude: “Show me everything awaiting my review” — optionally split by <b>Review Type</b> (Decision vs. Acknowledge).
  </div>

  <h2>Filter to your own work</h2>
  <p class="orient">The board already has the filter you need — no setup required.</p>
  <div class="info">
    <div class="h">Use the existing quick filter</div>
    On the LEAD board, open <b>Quick filters</b> and toggle <b>“Assigned to me”</b> — the board then shows only your cards, arranged by stage column. It resolves per person, so the same filter works for everyone. In the <b>List</b>, filter Assignee = you and group by Status for the same by-stage cut.
  </div>

  <h2>Prompt library</h2>
  <p class="orient">Copy-paste into Claude. First tell Claude the board once — <em>“Use the CoStar Leadership (LEAD) project”</em> — then each prompt resolves to your items on that board. The query each maps to is shown for reference; say <em>“across all my projects”</em> to widen beyond LEAD.</p>
  <ul class="prompts">
    <li><span class="say">Show me all my open work items</span><code class="jql">project = LEAD AND assignee = currentUser() AND statusCategory != Done</code></li>
    <li><span class="say">Show me what I'm actively working</span><code class="jql">project = LEAD AND assignee = currentUser() AND status = "In Progress"</code></li>
    <li><span class="say">Show me my blocked items</span><code class="jql">project = LEAD AND assignee = currentUser() AND status = "Blocked"</code></li>
    <li><span class="say">Show me my items awaiting David's review</span><code class="jql">project = LEAD AND assignee = currentUser() AND status = "Awaiting Review"</code></li>
    <li><span class="say">Show me my items David has acted on</span><code class="jql">project = LEAD AND assignee = currentUser() AND status in ("Reviewed", "Awaiting Info")</code></li>
    <li><span class="say">Show me everything awaiting my review</span><code class="jql">project = LEAD AND status = "Awaiting Review"</code> <span style="color:var(--secondary)">— David's lens; optionally split by Review Type (Decision / Acknowledge)</span></li>
  </ul>

  <h2>Act on it — Claude does the Jira work for you</h2>
  <p class="orient">You don't have to open Jira to make changes. Name the item and the change; Claude proposes it, you confirm, then it writes to Jira as you.</p>
  <ul class="prompts">
    <li><span class="say">Send LEAD-123 to Awaiting Review — Review Type: Decision; Review Ask: “Approve for rollout.”</span><code class="jql">status → Awaiting Review · sets Review Type + Review Ask</code></li>
    <li><span class="say">LEAD-123: move back to In Progress and comment “Incorporating the feedback; targeting Friday.”</span><code class="jql">status → In Progress · adds comment</code></li>
    <li><span class="say">Unblock LEAD-123 and move it to In Progress</span><code class="jql">status → In Progress</code></li>
    <li><span class="say">Comment on LEAD-123: draft complete, holding for dates</span><code class="jql">adds comment</code></li>
    <li><span class="say">Mark LEAD-123 Done</span><code class="jql">status → Done · owner-only</code></li>
  </ul>
  <p class="orient">Claude always shows the exact change and waits for your yes before writing, and the update posts under your own account. Prefer to do it in Jira? Open the item from its link — always an option, just not required.</p>
  <div class="info">
    <div class="h">One exception — uploading documents</div>
    Claude can set fields, change status, and add comments, but it <b>can't attach files</b> to a Jira item. To add a document, open the item in Jira and attach it there.
  </div>

  <footer>
    <b>How Do I — See and Action My Work by Stage</b> · v1 · June 22, 2026 · CoStar Partners, Unified Work Management.<br>
    Companion to <b>How Do I — Manage Work Items from Creation to Close</b> (the lifecycle and review-flow 1-pager).
  </footer>

` }} />
                        </div>
                      )}
                    </div>
                  );
                })()}
                {(() => {
                  const gid = "dashdoc";
                  const isOpen = openGuide === gid;
                  return (
                    <div style={{ border: `1px solid ${C.border}`, borderRadius: 9, marginBottom: 10, overflow: "hidden", background: C.paper }}>
                      <button onClick={() => setOpenGuide(isOpen ? null : gid)} style={{
                        width: "100%", textAlign: "left", display: "flex", alignItems: "center", gap: 12,
                        padding: "14px 16px", border: "none", cursor: "pointer", background: isOpen ? C.accentLt : C.card,
                        fontFamily: "'Outfit',sans-serif",
                      }}>
                        <span style={{ fontSize: 18, color: C.accent, transform: isOpen ? "rotate(90deg)" : "none", transition: "transform 0.15s", lineHeight: 1 }}>▸</span>
                        <span style={{ flex: 1 }}>
                          <span style={{ display: "block", fontSize: 14.5, fontWeight: 700, color: C.ink }}>Executive Dashboard — What It Is, How It Works, Why It Matters</span>
                          <span style={{ display: "block", fontSize: 12.5, color: C.muted, marginTop: 2 }}>The full reference: what's in it, the architecture, the management case, and how to use it.</span>
                        </span>
                        <span style={{ fontSize: 11, fontWeight: 600, color: C.accent, whiteSpace: "nowrap" }}>{isOpen ? "Hide" : "Read"}</span>
                      </button>
                      {isOpen && (
                        <div style={{ borderTop: `1px solid ${C.border}`, maxHeight: 620, overflowY: "auto" }}>
                          <style>{`.guide-dashdoc{
    --action:#003087; --risk:#C0392B; --ink:#1C1C1C; --page:#F4F7FB;
    --t1:#D6EAF8; --border:#C8D8EC; --secondary:#5A6B7B; --analytical:#1A5276;
    --info-h:#0E7490; --info-f:#E0F4F7; --row-alt:#EBF3FB; --confirm:#2E7D32; --card:#FFFFFF;
    --serif:Georgia,'Times New Roman',serif;
    --sans:Calibri,'Segoe UI',Helvetica,Arial,sans-serif;
  }.guide-dashdoc *{box-sizing:border-box;}.guide-dashdoc{scroll-behavior:smooth;}.guide-dashdoc{margin:0;background:var(--page);color:var(--ink);font-family:var(--sans);font-size:16px;line-height:1.5;-webkit-font-smoothing:antialiased;}.guide-dashdoc a{color:var(--action);}.guide-dashdoc .masthead{background:var(--ink);color:#fff;}.guide-dashdoc .masthead-inner{max-width:980px;margin:0 auto;padding:18px 24px;}.guide-dashdoc .brand-row{display:flex;justify-content:space-between;align-items:flex-start;gap:16px;flex-wrap:wrap;}.guide-dashdoc .wordmark{display:flex;align-items:center;gap:10px;}.guide-dashdoc .wordmark .mark{width:30px;height:30px;border:2px solid #fff;border-radius:6px;display:flex;align-items:center;justify-content:center;font-family:var(--serif);font-weight:bold;font-size:16px;color:#fff;}.guide-dashdoc .wordmark .name{font-family:var(--serif);font-weight:bold;font-size:17px;letter-spacing:.01em;}.guide-dashdoc .wordmark .name span{color:#9FC0E8;}.guide-dashdoc .doc-meta{text-align:right;font-size:12px;color:#9FB2C6;line-height:1.5;}.guide-dashdoc .doc-label{font-size:11px;text-transform:uppercase;letter-spacing:.14em;color:#9FC0E8;margin-top:14px;}.guide-dashdoc .doc-title{font-family:var(--serif);font-weight:bold;font-size:26px;margin:4px 0 2px;color:#fff;}.guide-dashdoc .doc-sub{font-size:14px;color:#9FB2C6;margin:0;}.guide-dashdoc .content{max-width:980px;margin:0 auto;padding:30px 24px 60px;}.guide-dashdoc .sec{margin:0 0 14px;padding:0 0 8px;}.guide-dashdoc .sec + .sec{margin-top:34px;padding-top:28px;border-top:2px solid var(--border);}.guide-dashdoc h2{font-family:var(--serif);font-weight:bold;font-size:24px;margin:0 0 6px;color:var(--ink);}.guide-dashdoc .lead{color:var(--secondary);font-size:15px;margin:0 0 18px;}.guide-dashdoc h3{font-family:var(--serif);font-weight:bold;font-size:18px;margin:24px 0 8px;color:var(--ink);}.guide-dashdoc p{margin:0 0 12px;}.guide-dashdoc .bottomline{border:1px solid var(--border);border-radius:10px;overflow:hidden;margin:0 0 24px;box-shadow:0 1px 4px rgba(0,0,0,.04);}.guide-dashdoc .bottomline .bl-head{background:var(--action);color:#fff;font-weight:700;font-size:12px;letter-spacing:.12em;text-transform:uppercase;padding:9px 18px;}.guide-dashdoc .bottomline .bl-body{background:var(--t1);color:var(--ink);padding:16px 18px;font-size:15.5px;line-height:1.55;}.guide-dashdoc .card{background:var(--card);border:1px solid var(--border);border-radius:10px;padding:16px 18px;margin:0 0 14px;box-shadow:0 1px 4px rgba(0,0,0,.04);}.guide-dashdoc .card.accent{border-left:4px solid var(--action);}.guide-dashdoc .callout{display:flex;gap:12px;align-items:flex-start;border-radius:10px;padding:14px 16px;margin:0 0 16px;font-size:14.5px;}.guide-dashdoc .callout .ico{flex:0 0 auto;width:22px;height:22px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:13px;color:#fff;font-weight:700;}.guide-dashdoc .callout.value{background:#E7F4E9;border:1px solid #BFE0C4;}.guide-dashdoc .callout.value .ico{background:var(--confirm);}.guide-dashdoc .callout.note{background:var(--info-f);border:1px solid #BEE3EA;}.guide-dashdoc .callout.note .ico{background:var(--info-h);}.guide-dashdoc details{background:var(--card);border:1px solid var(--border);border-radius:10px;margin:0 0 12px;overflow:hidden;box-shadow:0 1px 3px rgba(0,0,0,.04);}.guide-dashdoc details[open]{box-shadow:0 2px 8px rgba(0,0,0,.07);}.guide-dashdoc summary{cursor:pointer;list-style:none;padding:14px 18px;display:flex;align-items:center;gap:12px;font-weight:700;font-size:15px;color:var(--ink);user-select:none;}.guide-dashdoc summary::-webkit-details-marker{display:none;}.guide-dashdoc summary .chev{flex:0 0 auto;width:18px;height:18px;border-radius:4px;display:flex;align-items:center;justify-content:center;color:var(--action);font-size:13px;transition:transform .2s;}.guide-dashdoc details[open] summary .chev{transform:rotate(90deg);}.guide-dashdoc summary .tag{margin-left:auto;font-size:9.5px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:var(--action);background:var(--t1);border-radius:20px;padding:3px 10px;}.guide-dashdoc .det-body{padding:2px 18px 18px 48px;font-size:14.5px;color:var(--ink);}.guide-dashdoc .det-body p:last-child{margin-bottom:0;}.guide-dashdoc table{width:100%;border-collapse:collapse;margin:6px 0 18px;font-size:14px;}.guide-dashdoc thead th{background:var(--ink);color:#fff;font-size:11.5px;font-weight:700;text-transform:uppercase;letter-spacing:.04em;text-align:left;padding:9px 12px;}.guide-dashdoc tbody td{padding:10px 12px;border-bottom:1px solid var(--border);vertical-align:top;}.guide-dashdoc tbody tr:nth-child(even){background:var(--row-alt);}.guide-dashdoc footer{max-width:980px;margin:0 auto;padding:22px 24px 50px;color:var(--secondary);font-size:12px;border-top:1px solid var(--border);}
  @media print{.guide-dashdoc *{animation:none !important;transition:none !important;}.guide-dashdoc .masthead{position:static;}.guide-dashdoc details .det-body{display:block !important;}.guide-dashdoc details, .guide-dashdoc .card, .guide-dashdoc .callout, .guide-dashdoc .bottomline, .guide-dashdoc table{break-inside:avoid;}.guide-dashdoc{background:#fff;}}
  @media (max-width:640px){.guide-dashdoc .doc-title{font-size:22px;}.guide-dashdoc .det-body{padding-left:18px;}}
`}</style>
                          <div className="guide-dashdoc" dangerouslySetInnerHTML={{ __html: `

<div class="masthead">
  <div class="masthead-inner">
    <div class="brand-row">
      <div class="wordmark">
        <div class="mark">C</div>
        <div class="name">CoStar<span>&nbsp;Partners</span></div>
      </div>
      <div class="doc-meta">
        Prepared by Eric Baughman<br>
        June 23, 2026 · v2<br>
        Unified Work Management Pilot
      </div>
    </div>
    <div class="doc-label">Executive Dashboard</div>
    <div class="doc-title">What It Is, How It Works, Why It Matters</div>
    <div class="doc-sub">The executive consumption layer of CoStar's Unified Work Management — now live on the web.</div>
  </div>
</div>

<div class="content">

  <!-- OVERVIEW -->
  <section class="sec">
    <h2>Overview</h2>
    <p class="lead">A one-screen read of this document.</p>
    <div class="bottomline">
      <div class="bl-head">Bottom Line</div>
      <div class="bl-body">The Executive Dashboard is the executive consumption layer of CoStar's Unified Work Management pilot. It reads live from Jira — the single system of record — and presents the full portfolio in one navigable view, with the priority order, status, next steps, supporting documents, and an in-context review workflow that posts decisions straight back to Jira. It is now hosted on the web behind CoStar single sign-on, so executives reach it at a stable address with nothing to set up. It replaces the recurring manual burden of rebuilding executive reports every cycle with one system that is always current.</div>
    </div>
    <h3>What this is</h3>
    <p>CoStar Partners runs work across OPAL Fuels, Patter, and CoStar — dozens of initiatives, owners, and supporting documents spread across multiple tools. The Executive Dashboard pulls that work into a single live view built for executive decision-making rather than day-to-day task management.</p>
    <p>It is organized the way an executive thinks about the portfolio: by initiative and by lane, with each item carrying a consistent Summary / Status / Next Steps framing, a priority order the executive controls, and links to the canonical documents rather than copies. Reviewing an item — approving it, asking a question, or recording a decision — happens in the same place and is captured permanently in Jira.</p>
    <div class="callout value"><span class="ico">&#10003;</span><div><span style="font-weight:700;">The core shift:</span> one integrated system of record (Jira&nbsp;+&nbsp;Confluence) read by a live dashboard — instead of two surfaces kept in sync by hand every week.</div></div>
  </section>

  <!-- WHAT IT INCLUDES -->
  <section class="sec">
    <h2>What It Includes</h2>
    <p class="lead">The views and capabilities in the dashboard today. Expand any item for detail.</p>
    <details><summary><span class="chev">&#9656;</span>Portfolio view — the whole picture on one screen<span class="tag">Core</span></summary><div class="det-body"><p>Every executive-reportable initiative across OPAL, Patter, and CoStar appears in a single live view, grouped into portfolios and lanes. Each line shows status, owner, and the consistent Summary / Status / Next Steps framing — so the executive can scan the entire program in one place rather than across separate documents and tools.</p></div></details>
    <details><summary><span class="chev">&#9656;</span>Consistent Summary / Status / Next Steps on every item<span class="tag">Core</span></summary><div class="det-body"><p>Every item is read the same way: a short Summary of what it is, a Current Status, and the Next Steps — with dates where they exist. The framing is uniform across all three organizations, which removes the "what am I looking at?" friction of inconsistent reporting and lets the executive compare like with like.</p></div></details>
    <details><summary><span class="chev">&#9656;</span>Executive-controlled priority order<span class="tag">Core</span></summary><div class="det-body"><p>The executive can reorder initiatives by dragging them into the priority sequence they want. That order is written straight back to Jira, so the executive's priorities become part of the system of record and persist for everyone — not a note that lives only in one meeting.</p></div></details>
    <details><summary><span class="chev">&#9656;</span>The review flow — what's being asked, and whether it can wait<span class="tag">Core</span></summary><div class="det-body"><p>Each item handed up for review carries a <strong>Review Ask</strong> (exactly what to look at) and a <strong>Review Type</strong>. A <strong>Decision</strong> holds the work until the executive acts; an <strong>Acknowledge</strong> is for awareness unless the executive flags something. The chess-clock flow moves an item to Awaiting Review when it is the executive's turn, then to Reviewed or back as Awaiting Info — so whose turn it is, and what is being asked, are always explicit.</p></div></details>
    <details><summary><span class="chev">&#9656;</span>In-context review &amp; decisions, posted to Jira<span class="tag">Core</span></summary><div class="det-body"><p>Reviewing an item happens inside the dashboard. The executive can post a substantive comment, mark an item Reviewed, or send it back with Request Info. Those actions write to Jira as the executive, and the item moves to the right place in the queue. The decision and the executive's own words are captured permanently against the work — not stranded in a chat or a meeting doc.</p></div></details>
    <details><summary><span class="chev">&#9656;</span>Progressive disclosure — summary first, detail on demand<span class="tag">Core</span></summary><div class="det-body"><p>The dashboard opens at the summary level and expands to detail only when asked. An executive can stay at the portfolio altitude and drill into a single initiative's full status, comments, and links without being buried in detail by default.</p></div></details>
    <details><summary><span class="chev">&#9656;</span>Status grouping by lane &amp; live counts<span class="tag">Core</span></summary><div class="det-body"><p>Work is grouped by organization and lane, with live counts (in progress, blocked, awaiting review, done). Because the counts are read from Jira when the page loads, they reflect reality at the moment of viewing rather than whatever was true when a report was last assembled by hand.</p></div></details>
    <details><summary><span class="chev">&#9656;</span>Links to canonical documents — not copies<span class="tag">Core</span></summary><div class="det-body"><p>Supporting briefs and decision documents are linked to their canonical home (Confluence renders in the browser; Jira holds the work item). Executives follow a link to the live document instead of reading a stale copy pasted into a report.</p></div></details>
    <details><summary><span class="chev">&#9656;</span>Owner filter &amp; search<span class="tag">Core</span></summary><div class="det-body"><p>The portfolio can be filtered by owner to see what any one person is carrying, and searched to jump directly to an initiative or topic instead of scrolling. Findability is one of the structural advantages of a live system over a long static document.</p></div></details>
    <details><summary><span class="chev">&#9656;</span>Guides — in-product help and history<span class="tag">Core</span></summary><div class="det-body"><p>A built-in Guides view holds the how-to documentation (including this document), a short orientation to the capabilities that matter most, and a release history that records how the dashboard itself has advanced — written for executives rather than developers.</p></div></details>
    <details><summary><span class="chev">&#9656;</span>Meetings view<span class="tag">Coming Soon</span></summary><div class="det-body"><p>A meetings view is held as a "Coming Soon" placeholder. The intended approach is to surface a durable record of meeting decisions, with Fireflies.ai as the planned source — auto-transcripts and extracted action items flowing into the right workstream. Until then, decisions are captured against the work items themselves through the review flow.</p></div></details>
  </section>

  <!-- HOW IT WORKS -->
  <section class="sec">
    <h2>How It Works</h2>
    <p class="lead">Four parts working as one system. Expand any layer for detail.</p>
    <div class="card accent"><strong>The architecture in one sentence:</strong> Jira holds the work (system of record), Confluence holds the documents, the dashboard reads both and presents them for executives, and the review flow writes decisions back — all live, with no hand-rebuilt report in the middle.</div>
    <details><summary><span class="chev">&#9656;</span>1 · Jira — the single system of record<span class="tag">Layer</span></summary><div class="det-body"><p>All executive-reportable work lives in the Jira LEAD project as initiatives and tasks, tagged so they roll up into the portfolio view. Status, owner, priority, next steps, and comments all live here. Because Jira is the one authoritative source, there is no second copy to keep in sync — the dashboard simply reflects it.</p></div></details>
    <details><summary><span class="chev">&#9656;</span>2 · Confluence — the canonical document home<span class="tag">Layer</span></summary><div class="det-body"><p>Briefs, decision documents, and review materials live in Confluence, which renders in the browser and is the single source of truth for those documents. The dashboard links to them rather than embedding copies, so executives always see the current version.</p></div></details>
    <details><summary><span class="chev">&#9656;</span>3 · The dashboard — the executive consumption layer<span class="tag">Layer</span></summary><div class="det-body"><p>The dashboard is a web application, hosted at a stable address behind CoStar single sign-on. It reads Jira and Confluence through a thin service that holds the credential server-side and calls the Jira REST API directly, then renders the portfolio view, the priority order, the status groupings, the counts, and the links. Executives simply sign in — there is no per-person connection to set up.</p></div></details>
    <details><summary><span class="chev">&#9656;</span>4 · The review flow — decisions written back<span class="tag">Layer</span></summary><div class="det-body"><p>When an executive comments, approves, or requests more information, the dashboard writes that action back into Jira and moves the item to the appropriate status. This closes the loop: the dashboard is not just a read-only window, it is where executive decisions enter the system of record.</p></div></details>
    <div class="callout note"><span class="ico">i</span><div><strong>Why "live" is the whole point:</strong> because every view is read from Jira and Confluence at open time, the dashboard cannot drift from reality the way a hand-assembled weekly report does. There is no separate document to update.</div></div>
  </section>

  <!-- WHY IT MATTERS -->
  <section class="sec">
    <h2>Why It Matters</h2>
    <p class="lead">What it changes for running CoStar's initiatives, projects, and portfolio.</p>
    <div class="card"><strong>The management case:</strong> the recurring cost of executive reporting is re-manufacturing the report every cycle. The dashboard moves that cost from recurring to one-time: build the view once, and it stays current on its own. The decisions that used to live only in a meeting now live permanently against the work.</div>
    <details><summary><span class="chev">&#9656;</span>One system of record, not two surfaces kept in sync<span class="tag">Argument</span></summary><div class="det-body"><p>The strongest argument is architectural. A hand-built weekly report is a second surface that someone must reconcile against Jira every cycle. The dashboard removes the second surface entirely: there is one source, and the executive view is a live read of it. Less reconciliation, less drift, less manual labor.</p></div></details>
    <details><summary><span class="chev">&#9656;</span>Recurring labor becomes one-time labor<span class="tag">Argument</span></summary><div class="det-body"><p>A weekly report is rebuilt on a calendar — forever. The dashboard changes only when requirements change, and each change compounds into a permanent capability rather than being re-spent next week. The investment curve bends down over time instead of staying flat.</p></div></details>
    <details><summary><span class="chev">&#9656;</span>Decisions survive — comment fragmentation solved<span class="tag">Argument</span></summary><div class="det-body"><p>This is the sharpest practical benefit. When a decision is made in a meeting or a side document, it only survives in the system of record if someone remembers to copy it over — and an executive's own words about a decision can simply be lost. Posting the decision in-context to Jira means it is captured against the work, attributed correctly, and recoverable later.</p></div></details>
    <details><summary><span class="chev">&#9656;</span>The portfolio is always current<span class="tag">Argument</span></summary><div class="det-body"><p>Counts, statuses, and priority order are read live. An executive looking at the dashboard during a discussion is looking at the real state of the work, not a snapshot that has aged since Friday. That matters most when decisions are being made off the numbers.</p></div></details>
    <details><summary><span class="chev">&#9656;</span>Reduced key-person risk<span class="tag">Argument</span></summary><div class="det-body"><p>A manual weekly process depends on the one or two people who know how to assemble it. Encoding the view as software that reads the system of record reduces that dependency: the report does not have to be re-produced by hand by a specific person each week.</p></div></details>
    <h3>How it maps to executive needs</h3>
    <p>The dashboard was built directly against a documented set of executive needs. Each capability traces to one of them.</p>
    <table>
      <thead><tr><th>Executive need</th><th>How the dashboard meets it</th></tr></thead>
      <tbody>
        <tr><td>Portfolio view at the initiative level</td><td>One live portfolio view, grouped by org and lane.</td></tr>
        <tr><td>Reprioritize week to week</td><td>Drag-to-reorder priority, written back to Jira.</td></tr>
        <tr><td>Consistent Summary / Status / Next Steps</td><td>Uniform framing on every item.</td></tr>
        <tr><td>Dated next steps</td><td>Next steps carry dates where they exist.</td></tr>
        <tr><td>Substantive comments captured as the executive</td><td>In-context comments post to Jira under the executive's identity.</td></tr>
        <tr><td>Know what's being asked, and whether it blocks</td><td>Review Ask and Review Type (Decision / Acknowledge) on every review.</td></tr>
        <tr><td>Summary first, detail on demand</td><td>Progressive disclosure throughout.</td></tr>
        <tr><td>Status grouped by lane</td><td>Lane grouping with live counts.</td></tr>
        <tr><td>Links, not copied documents</td><td>Canonical links to Confluence and Jira.</td></tr>
        <tr><td>See what any one person is carrying</td><td>Owner filter across the portfolio.</td></tr>
        <tr><td>Findability</td><td>Search across the portfolio.</td></tr>
      </tbody>
    </table>
  </section>

  <!-- USING IT -->
  <section class="sec">
    <h2>Using It</h2>
    <p class="lead">How an executive reaches the dashboard and works the review queue today.</p>
    <details open><summary><span class="chev">&#9656;</span>Getting access<span class="tag">How-to</span></summary><div class="det-body"><p>The dashboard is hosted on the web at a stable address and protected by CoStar single sign-on. Open the address in a browser, sign in with your CoStar account, and the live portfolio loads — there is nothing to install and no per-person connection to authorize. A separate one-page getting-started guide covers the exact address and first-time sign-in; this section is the orientation.</p></div></details>
    <details><summary><span class="chev">&#9656;</span>Working the review queue<span class="tag">How-to</span></summary><div class="det-body"><p><strong>Scan the portfolio.</strong> Start at the summary level; the counts and lane groupings show where attention is needed.</p><p><strong>Open an item.</strong> Expand to read the full Summary / Status / Next Steps, recent comments, and links.</p><p><strong>Decide in place.</strong> Post a comment, mark Reviewed, or use Request Info to send it back — each action writes to Jira and moves the item.</p><p><strong>Set priority.</strong> Drag initiatives into the order you want; the order saves to Jira.</p></div></details>
    <details><summary><span class="chev">&#9656;</span>Attribution — actions are recorded as you<span class="tag">How-to</span></summary><div class="det-body"><p>Every comment and status change is recorded in Jira under your own identity, so the record reflects who actually made each decision. Actions are deliberate — you choose to comment, mark Reviewed, or request information — so nothing is posted by accident.</p></div></details>
    <div class="callout note"><span class="ico">i</span><div>If the page loads but the data looks empty, refresh once — the portfolio reads live from Jira at open time and a refresh re-pulls it.</div></div>
  </section>

</div>

<footer>
  <strong>Executive Dashboard — What It Is, How It Works, Why It Matters</strong> · v2 · June 23, 2026 · CoStar Partners, Unified Work Management.<br>
  Supersedes v1 (June 18, 2026). Updated for the web-hosted release; the Roadmap and Appendix sections were retired (their content is now reflected in the live dashboard's Guides tab and release history).
</footer>

` }} />
                        </div>
                      )}
                    </div>
                  );
                })()}
              <div style={{ height: 18 }} />
              
              <div style={{ fontSize: 11.5, textTransform: "uppercase", letterSpacing: "0.06em", color: C.blue, fontWeight: 700, marginTop: 4, marginBottom: 2 }}>Running the method</div>
              <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 12 }}>How we operate the Unified Work Management method day to day in Claude.</div>
              
                {(() => {
                  const gid = "quickref";
                  const isOpen = openGuide === gid;
                  return (
                    <div style={{ border: `1px solid ${C.border}`, borderRadius: 9, marginBottom: 10, overflow: "hidden", background: C.paper }}>
                      <button onClick={() => setOpenGuide(isOpen ? null : gid)} style={{
                        width: "100%", textAlign: "left", display: "flex", alignItems: "center", gap: 12,
                        padding: "14px 16px", border: "none", cursor: "pointer", background: isOpen ? C.accentLt : C.card,
                        fontFamily: "'Outfit',sans-serif",
                      }}>
                        <span style={{ fontSize: 18, color: C.accent, transform: isOpen ? "rotate(90deg)" : "none", transition: "transform 0.15s", lineHeight: 1 }}>▸</span>
                        <span style={{ flex: 1 }}>
                          <span style={{ display: "block", fontSize: 14.5, fontWeight: 700, color: C.ink }}>Quick Reference — the UWM working method</span>
                          <span style={{ display: "block", fontSize: 12.5, color: C.muted, marginTop: 2 }}>How we run the method in Claude: chat naming, the conversation index, the daily workflow.</span>
                        </span>
                        <span style={{ fontSize: 11, fontWeight: 600, color: C.accent, whiteSpace: "nowrap" }}>{isOpen ? "Hide" : "Read"}</span>
                      </button>
                      {isOpen && (
                        <div style={{ borderTop: `1px solid ${C.border}`, maxHeight: 620, overflowY: "auto" }}>
                          <style>{`.guide-quickref *, .guide-quickref *::before, .guide-quickref *::after{ margin: 0; padding: 0; box-sizing: border-box; }.guide-quickref{
    --ink: #1B1B18; --paper: #FAF9F6; --card: #FFFFFF; --border: #E0DED6; --muted: #7A786F;
    --accent: #2D5A3D; --accent-light: #E1F5EE;
    --blue: #1B5B8A; --blue-light: #E6F1FB;
    --gold: #8B6914; --gold-light: #FFF8E1;
    --risk: #B33B3B; --risk-light: #FCEBEB;
  }
  @media print {.guide-quickref{ font-size: 9pt; }.guide-quickref .page-break{ page-break-before: always; }.guide-quickref section{ break-inside: avoid; } }.guide-quickref{ font-family: 'DM Sans', system-ui, sans-serif; background: var(--paper); color: var(--ink); line-height: 1.5; -webkit-font-smoothing: antialiased; }.guide-quickref .page{ max-width: 820px; margin: 0 auto; padding: 32px 28px; }.guide-quickref .header{ display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 16px; border-bottom: 3px solid var(--accent); margin-bottom: 24px; }.guide-quickref .header h1{ font-size: 20px; font-weight: 700; color: var(--accent); letter-spacing: -0.02em; line-height: 1.2; }.guide-quickref .header .subtitle{ font-size: 12px; color: var(--muted); margin-top: 4px; }.guide-quickref .header .badge{ background: var(--accent); color: #fff; font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; padding: 5px 12px; border-radius: 4px; white-space: nowrap; margin-top: 2px; }.guide-quickref section{ margin-bottom: 22px; }.guide-quickref section h2{ font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: var(--accent); margin-bottom: 10px; display: flex; align-items: center; gap: 8px; }.guide-quickref section h2::after{ content: ''; flex: 1; height: 1px; background: var(--border); }.guide-quickref table{ width: 100%; border-collapse: collapse; font-size: 13px; }.guide-quickref thead th{ background: var(--ink); color: #fff; font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; padding: 7px 12px; text-align: left; }.guide-quickref thead th:first-child{ border-radius: 4px 0 0 0; }.guide-quickref thead th:last-child{ border-radius: 0 4px 0 0; }.guide-quickref tbody td{ padding: 7px 12px; border-bottom: 1px solid var(--border); vertical-align: top; }.guide-quickref tbody tr:last-child td{ border-bottom: none; }.guide-quickref tbody tr:nth-child(even) td{ background: #F5F4F0; }.guide-quickref .prefix-code{ font-family: 'DM Mono', monospace; font-weight: 500; font-size: 12px; background: var(--accent-light); color: var(--accent); padding: 2px 8px; border-radius: 3px; white-space: nowrap; }.guide-quickref .cmd{ font-family: 'DM Mono', monospace; font-size: 11.5px; color: var(--blue); line-height: 1.5; }.guide-quickref .workflow{ display: grid; grid-template-columns: 36px 1fr; gap: 0; }.guide-quickref .wf-step{ display: contents; }.guide-quickref .wf-num{ display: flex; flex-direction: column; align-items: center; }.guide-quickref .wf-circle{ width: 28px; height: 28px; border-radius: 50%; background: var(--accent); color: #fff; font-size: 13px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }.guide-quickref .wf-line{ width: 2px; flex: 1; background: var(--border); min-height: 8px; }.guide-quickref .wf-step:last-child .wf-line{ display: none; }.guide-quickref .wf-content{ padding: 2px 0 16px 14px; }.guide-quickref .wf-content strong{ font-size: 13px; color: var(--ink); }.guide-quickref .wf-content p{ font-size: 12px; color: var(--muted); margin-top: 2px; line-height: 1.5; }.guide-quickref .wf-content .wf-cmd{ font-family: 'DM Mono', monospace; font-size: 11px; color: var(--blue); background: var(--blue-light); padding: 3px 8px; border-radius: 3px; display: inline-block; margin-top: 4px; }.guide-quickref .naming-examples{ display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-top: 8px; }.guide-quickref .naming-ex{ background: var(--card); border: 1px solid var(--border); border-radius: 6px; padding: 8px 12px; font-size: 12px; }.guide-quickref .naming-ex .ex-name{ font-family: 'DM Mono', monospace; font-size: 11px; color: var(--ink); font-weight: 500; }.guide-quickref .naming-ex .ex-why{ color: var(--muted); font-size: 11px; margin-top: 2px; }.guide-quickref .callout{ border-radius: 6px; padding: 12px 16px; font-size: 12px; line-height: 1.6; margin-top: 8px; }.guide-quickref .callout-green{ background: var(--accent-light); border-left: 4px solid var(--accent); }.guide-quickref .callout-blue{ background: var(--blue-light); border-left: 4px solid var(--blue); }.guide-quickref .callout-gold{ background: var(--gold-light); border-left: 4px solid var(--gold); }.guide-quickref .callout-red{ background: var(--risk-light); border-left: 4px solid var(--risk); }.guide-quickref .callout strong{ font-weight: 600; }.guide-quickref .instructions-block{ background: #1B1B18; color: #D4D2CA; font-family: 'DM Mono', monospace; font-size: 11px; line-height: 1.7; padding: 16px 20px; border-radius: 6px; overflow-x: auto; white-space: pre-wrap; word-wrap: break-word; margin-top: 8px; position: relative; }.guide-quickref .instructions-block .kw{ color: #8BB9FE; }.guide-quickref .copy-hint{ position: absolute; top: 8px; right: 12px; font-size: 9px; text-transform: uppercase; letter-spacing: 0.08em; color: #7A786F; font-family: 'DM Sans', sans-serif; }.guide-quickref .footer{ margin-top: 20px; padding-top: 12px; border-top: 1px solid var(--border); font-size: 10px; color: var(--muted); display: flex; justify-content: space-between; }.guide-quickref .page-break{ margin-top: 32px; padding-top: 24px; border-top: 3px solid var(--accent); }.guide-quickref .page-break-label{ font-size: 10px; text-transform: uppercase; letter-spacing: 0.1em; color: var(--muted); margin-bottom: 20px; }
  @media (max-width: 600px) {.guide-quickref .page{ padding: 20px 16px; }.guide-quickref .header{ flex-direction: column; gap: 8px; }.guide-quickref .naming-examples{ grid-template-columns: 1fr; }.guide-quickref table{ font-size: 12px; }.guide-quickref thead th, .guide-quickref tbody td{ padding: 6px 8px; } }
`}</style>
                          <div className="guide-quickref" dangerouslySetInnerHTML={{ __html: `
<div class="page">

  <div class="header">
    <div>
      <h1>Project 1: Unified Work Management</h1>
      <div class="subtitle">Quick Reference — Prefixes · Commands · Daily Workflow</div>
    </div>
    <div class="badge">Bookmark This</div>
  </div>

  <!-- ═══ 1. PREFIX KEY ═══ -->
  <section>
    <h2>① Prefix Key — Chat Naming Convention</h2>
    <table>
      <thead><tr><th style="width:80px;">Prefix</th><th>Domain</th><th>Examples</th></tr></thead>
      <tbody>
        <tr><td><span class="prefix-code">DASH:</span></td><td>Executive Dashboard design, layout, features</td><td style="color:var(--muted);">Dashboard views, weekly brief format, approval workflows</td></tr>
        <tr><td><span class="prefix-code">CONF:</span></td><td>Confluence architecture, artifact storage, templates</td><td style="color:var(--muted);">Space hierarchy, page organization, HTML hosting</td></tr>
        <tr><td><span class="prefix-code">JIRA:</span></td><td>Jira schema, normalization, cross-project structure</td><td style="color:var(--muted);">Epic structure, label taxonomy, field design</td></tr>
        <tr><td><span class="prefix-code">ARCH:</span></td><td>System integration, automation, data flows</td><td style="color:var(--muted);">n8n flows, Fireflies bridge, MCP patterns</td></tr>
        <tr><td><span class="prefix-code">RPT:</span></td><td>Reporting strategy, brief generation, metrics</td><td style="color:var(--muted);">Weekly brief automation, status report design</td></tr>
        <tr><td><span class="prefix-code">META:</span></td><td>Project organization, process, planning</td><td style="color:var(--muted);">Conversation index, project setup, retrospectives</td></tr>
      </tbody>
    </table>
    <div class="callout callout-red" style="margin-top:10px;">
      <strong>⚠ Claude ignores your prefix.</strong> It auto-generates a chat name from your first message. You must <strong>manually rename</strong> every chat after starting it: hover over the chat name in the sidebar → pencil/edit icon → type <span style="font-family:'DM Mono',monospace; font-size:12px;">PREFIX: Descriptive topic name</span>. Do this before your second message.
    </div>
  </section>

  <!-- ═══ 2. NAMING EXAMPLES ═══ -->
  <section>
    <h2>② Naming Examples</h2>
    <div class="naming-examples">
      <div class="naming-ex"><div class="ex-name">DASH: Executive Dashboard — Epic structure design</div><div class="ex-why">Clear prefix + specific topic = findable in 3 seconds</div></div>
      <div class="naming-ex"><div class="ex-name">CONF: HTML artifact storage and organization</div><div class="ex-why">Confluence thread — distinct from Jira or dashboard chats</div></div>
      <div class="naming-ex"><div class="ex-name">JIRA: Cross-project label taxonomy</div><div class="ex-why">Schema decision — searchable by JIRA: prefix</div></div>
      <div class="naming-ex"><div class="ex-name">CONF: HTML artifact storage (v2)</div><div class="ex-why">Continuation chat — same prefix, version indicator</div></div>
    </div>
  </section>

  <!-- ═══ 3. KEY COMMANDS ═══ -->
  <section>
    <h2>③ Key Commands — Say These to Claude</h2>
    <table>
      <thead><tr><th style="width:200px;">When You Want To…</th><th>Say This</th></tr></thead>
      <tbody>
        <tr><td>View the conversation index</td><td><span class="cmd">"Show me the conversation index"</span></td></tr>
        <tr><td>Find a prior conversation</td><td><span class="cmd">"Find my last CONF: conversation about [topic]"</span></td></tr>
        <tr><td>Pick up where you left off</td><td><span class="cmd">"Check the conversation index for the [PREFIX: topic] thread and continue from the open items"</span></td></tr>
        <tr><td>Get a session summary</td><td><span class="cmd">"Summarize this conversation: decisions made, artifacts created, open items, and what the next conversation should pick up"</span></td></tr>
        <tr><td>Get a handoff for a new chat</td><td><span class="cmd">"Summarize everything we've decided and produced — format as a handoff I can paste into a new chat"</span></td></tr>
        <tr><td>Update the index</td><td><span class="cmd">"Update the conversation index with this session"</span></td></tr>
      </tbody>
    </table>
    <div class="callout callout-blue" style="margin-top:10px;">
      <strong>Tip:</strong> The "update the index" and "show the index" commands require the project instructions block (page 2) to be pasted into your project settings.
    </div>
  </section>

  <!-- ═══ 4. DAILY WORKFLOW ═══ -->
  <section>
    <h2>④ Daily Workflow</h2>
    <div class="workflow">
      <div class="wf-step">
        <div class="wf-num"><div class="wf-circle">1</div><div class="wf-line"></div></div>
        <div class="wf-content">
          <strong>Check the index</strong>
          <p>Start a conversation. Ask Claude to show the index. Scan Active Threads for open items.</p>
          <div class="wf-cmd">"Show me the conversation index"</div>
        </div>
      </div>
      <div class="wf-step">
        <div class="wf-num"><div class="wf-circle">2</div><div class="wf-line"></div></div>
        <div class="wf-content">
          <strong>Find or start a thread</strong>
          <p>Scan your conversation list by prefix, or ask Claude to find it. <strong>New thread?</strong> Send your first message, then <strong>immediately rename the chat</strong> — hover over the auto-generated name → pencil icon → type <span style="font-family:'DM Mono',monospace; font-size:11px;">PREFIX: Topic name</span>.</p>
          <div class="wf-cmd">"Find my last CONF: conversation about [topic]"</div>
        </div>
      </div>
      <div class="wf-step">
        <div class="wf-num"><div class="wf-circle">3</div><div class="wf-line"></div></div>
        <div class="wf-content">
          <strong>Establish context</strong>
          <p><strong>Short gap:</strong> Reference the topic and last decision from the index.<br>
          <strong>Longer gap:</strong> Paste a handoff summary from the prior chat.<br>
          <strong>Let Claude do it:</strong> Ask Claude to check the index and continue from open items.</p>
        </div>
      </div>
      <div class="wf-step">
        <div class="wf-num"><div class="wf-circle">4</div><div class="wf-line"></div></div>
        <div class="wf-content">
          <strong>Do the work</strong>
          <p>Stay on topic. Produce artifacts. Make decisions. Note tangential topics as open items for separate chats.</p>
        </div>
      </div>
      <div class="wf-step">
        <div class="wf-num"><div class="wf-circle">5</div><div class="wf-line"></div></div>
        <div class="wf-content">
          <strong>Session summary</strong>
          <p>Before closing, get the summary. It stays in the chat (searchable) and feeds the index update.</p>
          <div class="wf-cmd">"Summarize this conversation: decisions, artifacts, open items, next steps"</div>
        </div>
      </div>
      <div class="wf-step">
        <div class="wf-num"><div class="wf-circle">6</div><div class="wf-line"></div></div>
        <div class="wf-content">
          <strong>Update the index</strong>
          <p>Claude reads the current index, adds this session, and produces an updated file.</p>
          <div class="wf-cmd">"Update the conversation index with this session"</div>
        </div>
      </div>
      <div class="wf-step">
        <div class="wf-num"><div class="wf-circle">7</div><div class="wf-line"></div></div>
        <div class="wf-content">
          <strong>Re-upload the index</strong>
          <p>Download the updated file. Project Knowledge → delete old <span style="font-family:'DM Mono',monospace;">conversation-index.md</span> → upload new one. <strong>~15 sec.</strong></p>
        </div>
      </div>
    </div>
    <div class="callout callout-gold">
      <strong>Total overhead: ~90 seconds per conversation.</strong> Steps 1, 5, 6, 7 are the only overhead.
    </div>
  </section>

  <!-- ═══ PAGE 2 ═══ -->
  <div class="page-break"><div class="page-break-label">Page 2 — Setup &amp; Reference</div></div>

  <!-- ═══ 5. PROJECT INSTRUCTIONS ═══ -->
  <section>
    <h2>⑤ Project Instructions — Paste Into Project Settings</h2>
    <p style="font-size:12px; color:var(--muted); margin-bottom:8px;">Project → Settings (gear icon) → Instructions field. Paste this block.</p>
    <div class="instructions-block"><span class="copy-hint">Copy &amp; paste →</span><span class="kw">CONVERSATION MANAGEMENT:</span>

This project uses a conversation index file (conversation-index.md) in project
knowledge as a living table of contents for all conversations.

When I say <span class="kw">"show me the conversation index"</span> or <span class="kw">"show the index,"</span> read the
conversation-index file from project knowledge and display it formatted in the chat.

When I say <span class="kw">"update the conversation index"</span> or <span class="kw">"update the index,"</span> do the following:
1. Read the current conversation-index file from project knowledge
2. Add an entry for this conversation to the appropriate Active Threads section
   (match by the conversation's topic prefix)
3. Include: today's date, the chat name, key decisions, artifacts produced, and
   open items
4. Add any artifacts to the Artifact Registry section
5. Add any cross-cutting decisions to the Decision Log section
6. Produce the updated file for me to download

When I ask for a <span class="kw">"session summary,"</span> provide: decisions made, artifacts created,
open items, and what the next conversation on this topic should pick up.

When I ask for a <span class="kw">"handoff summary,"</span> provide a fuller context package suitable for
pasting into a new conversation to continue this work — include enough detail for
Claude to resume without re-reading this thread.</div>
  </section>

  <!-- ═══ 6. RULES ═══ -->
  <section>
    <h2>⑥ Rules to Remember</h2>
    <table>
      <thead><tr><th style="width:50%;">Do</th><th>Don't</th></tr></thead>
      <tbody>
        <tr><td><strong>Rename every chat immediately</strong> after your first message (Claude ignores your prefix)</td><td>Leave chats with Claude's auto-generated names</td></tr>
        <tr><td><strong>One topic per conversation</strong> — note tangents as open items</td><td>Let conversations drift across unrelated topics</td></tr>
        <tr><td><strong>Session summary before closing</strong> any substantive chat</td><td>Close a working conversation without capturing what happened</td></tr>
        <tr><td><strong>Consistent index filename:</strong> <span style="font-family:'DM Mono',monospace; font-size:11px;">conversation-index.md</span></td><td>Version the filename (index-v2.md, index-v3.md)</td></tr>
        <tr><td><strong>Save standalone artifacts</strong> (designs, schemas) to project knowledge</td><td>Save individual session summaries as separate files</td></tr>
        <tr><td><strong>Use handoff summaries</strong> when a thread gets long (20+ exchanges)</td><td>Keep pushing a stale conversation past context limits</td></tr>
      </tbody>
    </table>
  </section>

  <!-- ═══ 7. CHECKLIST ═══ -->
  <section>
    <h2>⑦ New Project Setup Checklist</h2>
    <div style="display:grid; grid-template-columns: 24px 1fr; gap: 4px 10px; font-size: 13px; line-height: 1.8;">
      <span style="color:var(--accent);">☐</span><span>Create the project with a clear, specific name</span>
      <span style="color:var(--accent);">☐</span><span>Upload a <strong>seed knowledge document</strong> (purpose, stakeholders, current state, technical context, next steps)</span>
      <span style="color:var(--accent);">☐</span><span>Define <strong>4–8 prefix categories</strong> for conversation topics</span>
      <span style="color:var(--accent);">☐</span><span>Create <strong>conversation-index.md</strong> — customize prefixes and Active Thread sections</span>
      <span style="color:var(--accent);">☐</span><span>Upload <strong>conversation-index.md</strong> to project knowledge</span>
      <span style="color:var(--accent);">☐</span><span>Paste the <strong>project instructions</strong> block into Settings → Instructions</span>
      <span style="color:var(--accent);">☐</span><span>Upload <strong>reference documents</strong> (proposals, schemas, dashboards)</span>
      <span style="color:var(--accent);">☐</span><span>Start first chat → send message → <strong>immediately rename with prefix</strong></span>
    </div>
  </section>

  <div class="footer">
    <span>CoStar Partners — Project 1: Unified Work Management</span>
    <span>Bookmark this page for daily reference</span>
  </div>

</div>
` }} />
                        </div>
                      )}
                    </div>
                  );
                })()}
            </div>

            {/* ── RELEASE HISTORY ─────────────────────── */}
            <div style={{ background: C.card, borderLeft: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, borderRadius: "0 0 10px 10px", padding: "18px 22px" }}>
              <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.06em", color: C.ink, fontWeight: 700, marginBottom: 4 }}>Release history</div>
              <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 8 }}>How this solution evolved — built conversationally, one iteration at a time. Each step answered a real need; together they're something a weekly document structurally can't show: its own history. Currently live: <strong>build {BUILD}</strong>.</div>
              <div>
                {(() => { const dim = true; return (
                    <div style={{ display: "flex", gap: 14, padding: "12px 0", borderBottom: `1px solid ${C.border}` }}>
                      <div style={{ minWidth: 84, flexShrink: 0 }}>
                        <div style={{ fontFamily: "'Source Serif 4',Georgia,serif", fontSize: 13, fontWeight: 700, color: dim ? C.muted : C.accent }}>pre-v3</div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: dim ? C.muted : C.ink, marginBottom: 3 }}>Foundations</div>
                        <div style={{ fontSize: 13, lineHeight: 1.55, color: "#4a4842" }}>Before the executive view existed, the early builds were plumbing — connecting live to Jira, getting it fast, and making it read on any screen. None of it is visible to you; all of it is what makes the rest possible.</div>
                      </div>
                    </div> ); })()}
                {(() => { const dim = false; return (
                    <div style={{ display: "flex", gap: 14, padding: "12px 0", borderBottom: `1px solid ${C.border}` }}>
                      <div style={{ minWidth: 84, flexShrink: 0 }}>
                        <div style={{ fontFamily: "'Source Serif 4',Georgia,serif", fontSize: 13, fontWeight: 700, color: dim ? C.muted : C.accent }}>v3 – v13</div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: dim ? C.muted : C.ink, marginBottom: 3 }}>The portfolio takes shape</div>
                        <div style={{ fontSize: 13, lineHeight: 1.55, color: "#4a4842" }}>The first build you'd recognize: workstreams organized by organization, each opening to the work beneath it. Summary / Status / Next-Step rendered live from the item itself. Comment threads with activity indicators so you can follow the thinking, not just the last note. Related documents — Confluence pages, attachments, and web links — surfaced on the item, with the link-fetch gap that hid them quietly fixed.</div>
                      </div>
                    </div> ); })()}
                {(() => { const dim = false; return (
                    <div style={{ display: "flex", gap: 14, padding: "12px 0", borderBottom: `1px solid ${C.border}` }}>
                      <div style={{ minWidth: 84, flexShrink: 0 }}>
                        <div style={{ fontFamily: "'Source Serif 4',Georgia,serif", fontSize: 13, fontWeight: 700, color: dim ? C.muted : C.accent }}>v16 – v22</div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: dim ? C.muted : C.ink, marginBottom: 3 }}>You set the priorities</div>
                        <div style={{ fontSize: 13, lineHeight: 1.55, color: "#4a4842" }}>The headline capability: drag workstreams into your priority order and it saves straight back to Jira — your order, persisted, visible to everyone on their next refresh. Documents made to open in the browser the way you liked in Loop, and the remote-link gap that dropped Confluence and web links closed for good.</div>
                      </div>
                    </div> ); })()}
                {(() => { const dim = false; return (
                    <div style={{ display: "flex", gap: 14, padding: "12px 0", borderBottom: `1px solid ${C.border}` }}>
                      <div style={{ minWidth: 84, flexShrink: 0 }}>
                        <div style={{ fontFamily: "'Source Serif 4',Georgia,serif", fontSize: 13, fontWeight: 700, color: dim ? C.muted : C.accent }}>v23 – v31</div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: dim ? C.muted : C.ink, marginBottom: 3 }}>The review lane</div>
                        <div style={{ fontSize: 13, lineHeight: 1.55, color: "#4a4842" }}>The chess-clock review flow became real: an item moves to Awaiting Review when it's your turn, carries a Review Type (Decision or Acknowledge) and a Review Ask, and returns Reviewed or Awaiting Info. Dedicated lanes surface what's waiting on you and what's blocked. Comments post as you, newest-first, mirroring Jira exactly. Weekly Status can be saved to Confluence as a dated, searchable record.</div>
                      </div>
                    </div> ); })()}
                {(() => { const dim = false; return (
                    <div style={{ display: "flex", gap: 14, padding: "12px 0", borderBottom: `1px solid ${C.border}` }}>
                      <div style={{ minWidth: 84, flexShrink: 0 }}>
                        <div style={{ fontFamily: "'Source Serif 4',Georgia,serif", fontSize: 13, fontWeight: 700, color: dim ? C.muted : C.accent }}>v32 – v37</div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: dim ? C.muted : C.ink, marginBottom: 3 }}>Live on the web</div>
                        <div style={{ fontSize: 13, lineHeight: 1.55, color: "#4a4842" }}>The dashboard moved from a Claude artifact to a hosted web address behind CoStar sign-in — and the limit that capped it at 100 items was solved with pagination, so every item shows and status, not a page count, decides what you see. The data path was hardened (one model setting, stronger auto-retry on a cold start), and this Guides tab brought the how-to documentation and this history into the product itself.</div>
                      </div>
                    </div> ); })()}
                {(() => { const dim = false; return (
                    <div style={{ display: "flex", gap: 14, padding: "12px 0", borderBottom: `1px solid ${C.border}` }}>
                      <div style={{ minWidth: 84, flexShrink: 0 }}>
                        <div style={{ fontFamily: "'Source Serif 4',Georgia,serif", fontSize: 13, fontWeight: 700, color: dim ? C.muted : C.accent }}>v38</div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: dim ? C.muted : C.ink, marginBottom: 3 }}>The activity feed</div>
                        <div style={{ fontSize: 13, lineHeight: 1.55, color: "#4a4842" }}>A new Activity panel gathers the latest comments from across the portfolio into one running feed, newest first — a quick read of what's moving, the way you'd scan a news update. Each entry carries the workstream and item it belongs to and opens straight to that work, so a comment that catches your eye is one tap from acting on it. The feed is scoped to exec-reportable items, keeping routine Jira activity out of view.</div>
                      </div>
                    </div> ); })()}
                {(() => { const dim = false; return (
                    <div style={{ display: "flex", gap: 14, padding: "12px 0", borderBottom: "none" }}>
                      <div style={{ minWidth: 84, flexShrink: 0 }}>
                        <div style={{ fontFamily: "'Source Serif 4',Georgia,serif", fontSize: 13, fontWeight: 700, color: dim ? C.muted : C.accent }}>v39 – v42</div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: dim ? C.muted : C.ink, marginBottom: 3 }}>Goals, and time from Cohesion</div>
                        <div style={{ fontSize: 13, lineHeight: 1.55, color: "#4a4842" }}>The portfolio gained a Goals view — outcomes grouped by organization, each with its success metric and the work tagged to it, so direction and execution read together. Each goal opens to its owner, target date, notes, a comment thread that posts to Jira like epics and tasks, and its contributing work: the exact epics and tasks tagged to the goal, each one clickable straight through to the workstream. On the Portfolio view, a workstream shows a Goal marker that links to the goal it serves. And where a task is mapped to a Cohesion project, opening it shows that project's time — hours against budget and how much is consumed — beside the work. The time view runs on sample figures for now, with the live Cohesion connection to follow when it ports to the web host.</div>
                      </div>
                    </div> ); })()}</div>
            </div>

          </div>
          );
        })()}

        {!loading && view === "goals" && (() => {
          const ORG_ORDER = ["OPAL", "CoStar", "Patter"];
          const ORG_LABEL = { OPAL: "One OPAL", CoStar: "One CoStar", Patter: "One Patter" };
          const rollupFor = (slug) => {
            if (!slug) return { items: [], total: 0, done: 0, blocked: 0 };
            // Membership is by label ONLY — an issue contributes to a goal iff it carries the
            // goal-<slug> label. No epic-child inheritance: a tagged epic counts as ONE item
            // (by its own status); tagged tasks each count as one. Untagged children never count.
            const taggedEpics = epics.filter(e => (e.labels || []).includes(slug)).map(e => ({ ...e, isEpic: true }));
            const taggedTasks = tasks.filter(t => (t.labels || []).includes(slug)).map(t => ({ ...t, isEpic: false }));
            const items = [...new Map([...taggedEpics, ...taggedTasks].map(i => [i.key, i])).values()];
            return {
              items,
              total: items.length,
              done: items.filter(i => i.status === "done").length,
              blocked: items.filter(i => i.status === "blocked").length,
            };
          };
          const byOrg = {};
          goals.forEach(g => { const o = g.org || "CoStar"; (byOrg[o] = byOrg[o] || []).push(g); });
          const orgsPresent = ORG_ORDER.filter(o => byOrg[o] && byOrg[o].length);

          if (goals.length === 0) {
            return (
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: "32px 24px", textAlign: "center" }}>
                <div style={{ fontFamily: "'Source Serif 4',Georgia,serif", fontSize: 17, fontWeight: 600, marginBottom: 6 }}>No goals loaded yet</div>
                <div style={{ fontSize: 13, color: C.muted, maxWidth: "56ch", margin: "0 auto", lineHeight: 1.6 }}>
                  Goals are read from Jira issues of type <strong>Goal</strong> carrying a <code style={{ fontFamily: "'DM Mono',monospace", background: C.accentLt, padding: "1px 5px", borderRadius: 3 }}>[GOAL]</code> block. Once they load, each goal appears here with its metric and the work tagged to it. Try Refresh if you just created them.
                </div>
                <button onClick={() => setCreateGoalOpen(true)} style={{
                  marginTop: 16, padding: "9px 18px", border: "none", borderRadius: 7, cursor: "pointer",
                  background: C.ink, color: "#fff", fontSize: 13, fontWeight: 600, fontFamily: "inherit",
                }}>+ Create New Goal</button>
              </div>
            );
          }

          return (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 6 }}>
                <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, maxWidth: "72ch" }}>
                  Portfolio outcomes, grouped by organization. Each goal shows its <strong>metric</strong> (the outcome) and the <strong>work tagged to it</strong> (the activity) — the two read together keep direction and execution in alignment. A goal can draw work from more than one epic; work is bound to a goal by a <code style={{ fontFamily: "'DM Mono',monospace", background: C.accentLt, padding: "1px 5px", borderRadius: 3 }}>goal-</code> label.
                </div>
                <button onClick={() => setCreateGoalOpen(true)} title="Create a new portfolio goal" style={{
                  flexShrink: 0, padding: "8px 15px", border: "none", borderRadius: 7, cursor: "pointer",
                  background: C.ink, color: "#fff", fontSize: 13, fontWeight: 600, fontFamily: "inherit", whiteSpace: "nowrap",
                }}>+ New Goal</button>
              </div>

              {orgsPresent.map(org => {
                const oc = ORGS[org] || ORGS.CoStar;
                const list = byOrg[org];
                const atRisk = list.filter(g => { const s = (g.goal.status || "").toLowerCase(); return s.includes("risk") || s.includes("off"); }).length;
                return (
                  <div key={org}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "20px 0 8px" }}>
                      <span style={{ fontFamily: "'Source Serif 4',Georgia,serif", fontSize: 17, fontWeight: 700, color: oc.accent, whiteSpace: "nowrap" }}>{ORG_LABEL[org] || org}</span>
                      <span style={{ flex: 1, height: 2, borderRadius: 2, background: oc.ring }} />
                      <span style={{ fontSize: 11, color: C.muted, whiteSpace: "nowrap" }}>{list.length} goal{list.length === 1 ? "" : "s"}{atRisk > 0 ? ` · ${atRisk} needs attention` : ""}</span>
                    </div>

                    {list.map(g => {
                      const gd = g.goal;
                      const sp = goalStatusStyle(gd.status);
                      const r = rollupFor(g.slug);
                      const pct = r.total ? Math.round(r.done / r.total * 100) : 0;
                      const goalExpanded = expandedGoals.has(g.key);
                      const workExpanded = expandedGoalWork.has(g.key);
                      const gc = g.totalComments || (g.recentComments ? g.recentComments.length : 0);
                      return (
                        <div key={g.key} id={`goal-${g.key}`} style={{ background: C.card, border: `1px solid ${C.border}`, borderLeft: `4px solid ${oc.accent}`, borderRadius: 10, padding: "14px 16px", margin: "10px 0" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                            <div>
                              <div style={{ fontFamily: "'Source Serif 4',Georgia,serif", fontSize: 16, fontWeight: 600, lineHeight: 1.25, color: C.ink }}>{gd.goal || g.summary}</div>
                              <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                                {g.key}
                                {gd.owner ? ` · Owner: ${gd.owner}` : ""}
                                {gd.targetDate ? ` · Target: ${gd.targetDate}` : ""}
                                {gc > 0 && (
                                  <span title={`${gc} comment${gc === 1 ? "" : "s"} on this goal`} style={{ marginLeft: 6, color: C.gold, fontWeight: 600 }}>💬 {gc}</span>
                                )}
                              </div>
                            </div>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600, padding: "3px 9px", borderRadius: 5, whiteSpace: "nowrap", color: sp.c, background: sp.bg }}>
                              <span style={{ width: 6, height: 6, borderRadius: "50%", background: sp.dot }} />{sp.label}
                            </span>
                          </div>

                          {/* Outcome */}
                          <div style={{ fontSize: 13, marginTop: 10, color: C.ink }}>
                            <span style={{ color: C.muted }}>Metric:</span> {gd.metric}
                            {" — "}
                            <span style={{ fontFamily: "'Source Serif 4',Georgia,serif", fontWeight: 700 }}>{gd.baseline || "?"}</span>
                            {gd.current && gd.current !== "—" && (
                              <>
                                <span style={{ color: C.muted }}> → </span>
                                <span style={{ fontFamily: "'Source Serif 4',Georgia,serif", fontWeight: 700, color: oc.accent }}>{gd.current}</span>
                                <span style={{ fontSize: 10, color: oc.accent, fontWeight: 600 }}> now</span>
                              </>
                            )}
                            <span style={{ color: C.muted }}> → </span>
                            <span style={{ fontFamily: "'Source Serif 4',Georgia,serif", fontWeight: 700 }}>{gd.target || "?"}</span>
                            {gd.unit ? <span style={{ color: C.muted }}> {gd.unit}</span> : null}
                          </div>
                          {/* Outcome position bar — only when the owner has entered a real Current value and a numeric baseline/target exist. Never faked from activity. */}
                          {gd.currentNum != null && gd.targetNum != null && gd.baselineNum != null && gd.targetNum !== gd.baselineNum && (() => {
                            const raw = (gd.currentNum - gd.baselineNum) / (gd.targetNum - gd.baselineNum) * 100;
                            const posPct = Math.max(0, Math.min(100, Math.round(raw)));
                            return (
                              <div style={{ marginTop: 8 }}>
                                <div style={{ position: "relative", height: 6, background: "#EEEDE6", borderRadius: 4 }}>
                                  <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${posPct}%`, background: oc.accent, borderRadius: 4, transition: "width .3s" }} />
                                  <div title={`Current: ${gd.current} ${gd.unit || ""}`.trim()} style={{ position: "absolute", left: `calc(${posPct}% - 5px)`, top: -2, width: 10, height: 10, borderRadius: "50%", background: "#fff", border: `2px solid ${oc.accent}`, boxShadow: "0 1px 2px rgba(0,0,0,.2)" }} />
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9.5, color: C.muted, marginTop: 3, fontFamily: "'DM Mono',monospace" }}>
                                  <span>{gd.baseline}</span>
                                  <span style={{ color: oc.accent, fontWeight: 700 }}>{posPct}% of the way</span>
                                  <span>{gd.target}</span>
                                </div>
                              </div>
                            );
                          })()}
                          {gd.targetTBD && (
                            <div style={{ fontSize: 11, color: C.gold, fontWeight: 600, marginTop: 5 }}>
                              {/\[base\]/i.test(gd.baseline) ? "Baseline not set" : "Target not set"}
                              {/Scott/i.test(gd.notes) ? " — Scott" : /Eric/i.test(gd.notes) ? " — Eric" : ""}
                            </div>
                          )}
                          {gd.secondary && (
                            <div style={{ fontSize: 12, color: C.muted, marginTop: 8, lineHeight: 1.5 }}>Secondary: {gd.secondary}</div>
                          )}

                          {/* Contributing work — items that literally carry the goal-<slug> label (epics + tasks). Epic counts as one. */}
                          <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px dashed ${C.border}` }}>
                            <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.07em", color: C.muted, fontWeight: 600, marginBottom: 6, display: "flex", justifyContent: "space-between" }}>
                              <span>Contributing work</span>
                              {r.total > 0 && <span style={{ fontFamily: "'DM Mono',monospace", textTransform: "none", letterSpacing: 0 }}>{r.done} of {r.total} done{r.blocked > 0 ? ` · ${r.blocked} blocked` : ""}</span>}
                            </div>
                            {g.slug && (
                              <button onClick={() => setTagWorkGoal(g)} title={`Apply the ${g.slug} label to epics/tasks that serve this goal`} style={{
                                marginBottom: 8, padding: "3px 10px", border: `1px solid ${C.border}`, borderRadius: 6, cursor: "pointer",
                                background: C.paper, color: C.ink, fontSize: 11.5, fontWeight: 600, fontFamily: "inherit",
                              }}>+ Tag work to this goal</button>
                            )}
                            {r.total === 0 ? (
                              <div style={{ fontSize: 12, color: C.muted, fontStyle: "italic" }}>
                                No work tagged yet — add the <code style={{ fontFamily: "'DM Mono',monospace", background: C.paper, padding: "1px 5px", borderRadius: 3, fontStyle: "normal" }}>{g.slug || "goal-…"}</code> label to the specific epics and tasks that serve this goal.
                              </div>
                            ) : (
                              <>
                                <div style={{ height: 8, background: "#EEEDE6", borderRadius: 5, overflow: "hidden" }}>
                                  <div style={{ height: "100%", width: `${pct}%`, background: oc.accent, borderRadius: 5, transition: "width .3s" }} />
                                </div>
                                <button onClick={() => toggleGoalWork(g.key)} style={{
                                  marginTop: 8, padding: "2px 0", border: "none", background: "none", color: C.blue,
                                  fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                                }}>
                                  {workExpanded ? "▾ Hide" : "▸ Show"} {r.total} tagged item{r.total === 1 ? "" : "s"}
                                </button>
                                {workExpanded && (
                                  <div style={{ marginTop: 4 }}>
                                    {r.items.map(it => {
                                      const kids = it.isEpic ? tasks.filter(t => t.parentKey === it.key) : [];
                                      const kd = kids.filter(t => t.status === "done").length;
                                      const done = it.status === "done";
                                      const blocked = it.status === "blocked";
                                      return (
                                        <div key={it.key}
                                          onClick={() => it.isEpic ? openWorkstream(it.key) : openItemFromActivity({ isEpic: false, key: it.key, item: it })}
                                          title={`Open ${it.key} on the Portfolio view`}
                                          style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, padding: "4px 6px", margin: "0 -6px", borderRadius: 5, cursor: "pointer", transition: "background 0.1s" }}
                                          onMouseEnter={ev => ev.currentTarget.style.background = C.paper}
                                          onMouseLeave={ev => ev.currentTarget.style.background = "transparent"}>
                                          <span style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", padding: "1px 5px", borderRadius: 3, background: it.isEpic ? "#EEEDFE" : C.paper, color: it.isEpic ? "#534AB7" : C.muted, border: it.isEpic ? "none" : `1px solid ${C.border}` }}>{it.isEpic ? "Epic" : "Task"}</span>
                                          <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: C.blue }}>{it.key}</span>
                                          <span style={{ color: done ? C.muted : C.ink, textDecoration: done ? "line-through" : "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.summary}</span>
                                          <span style={{ marginLeft: "auto", fontSize: 11, color: blocked ? C.warn : C.muted, fontFamily: "'DM Mono',monospace", whiteSpace: "nowrap" }}>
                                            {it.isEpic && kids.length ? `${it.statusName || "Epic"} · ${kd}/${kids.length}` : (it.statusName || it.status)}
                                          </span>
                                          <span style={{ fontSize: 11, color: C.blue, flexShrink: 0 }}>↗</span>
                                        </div>
                                      );
                                    })}
                                    <div style={{ fontSize: 10.5, color: C.muted, fontStyle: "italic", marginTop: 6 }}>
                                      Only items carrying <code style={{ fontFamily: "'DM Mono',monospace", background: C.paper, padding: "1px 4px", borderRadius: 3, fontStyle: "normal" }}>{g.slug}</code> appear here. A tagged epic counts as one item — tag its child tasks to count them individually.
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                          </div>

                          {/* Details toggle → notes + discussion */}
                          <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-end" }}>
                            <button onClick={() => toggleGoal(g.key)} style={{
                              padding: "3px 10px", borderRadius: 5, border: `1px solid ${goalExpanded ? oc.accent : C.border}`,
                              background: goalExpanded ? oc.bg : "transparent", color: goalExpanded ? oc.accent : C.muted,
                              fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", transition: "all 0.15s",
                            }}>
                              {goalExpanded ? "▾ Hide details" : `▸ Details${gc > 0 ? ` & discussion (${gc})` : " & discussion"}`}
                            </button>
                          </div>

                          {goalExpanded && (
                            <div style={{ marginTop: 8, paddingTop: 12, borderTop: `1px solid ${C.border}`, animation: "fadeIn 0.15s ease" }}>
                              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, fontSize: 12, marginBottom: 12 }}>
                                <div>
                                  <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted, fontWeight: 600, marginBottom: 3 }}>Owner</div>
                                  <div style={{ color: C.ink }}>{gd.owner || "—"}</div>
                                </div>
                                <div>
                                  <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted, fontWeight: 600, marginBottom: 3 }}>Target date</div>
                                  <div style={{ color: C.ink }}>{gd.targetDate || "—"}</div>
                                </div>
                                {gd.notes && (
                                  <div style={{ gridColumn: "1 / -1" }}>
                                    <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted, fontWeight: 600, marginBottom: 3 }}>Notes</div>
                                    <div style={{ color: "#3a3a36", lineHeight: 1.5 }}>{gd.notes}</div>
                                  </div>
                                )}
                              </div>
                              <CommentThread item={g} onAddComment={addThreadComment} accentColor={oc.accent} />
                              <div style={{ marginTop: 10, paddingTop: 8, borderTop: `1px solid ${C.border}` }}>
                                <a href={`${JIRA_BASE}/browse/${g.key}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none", fontSize: 11, color: C.blue, fontWeight: 600 }}>Open goal {g.key} in Jira ↗</a>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}

              <div style={{ fontSize: 11, color: C.muted, marginTop: 16, paddingTop: 10, borderTop: `1px solid ${C.border}`, lineHeight: 1.6 }}>
                Status is owner-reported at review — goals showing “Status not set” are awaiting their first reading, not in error. Work not tagged to any goal appears in the Portfolio view.
              </div>
            </>
          );
        })()}

        {!loading && view === "portfolio" && !detailItem && (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, gap: 10, flexWrap: "wrap" }}>
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
                {["All", "OPAL", "CoStar", "Patter"].map(o => (
                  <button key={o} onClick={() => setOrgFilter(o)} style={{
                    padding: "5px 12px", borderRadius: 6, border: `1px solid ${C.border}`, cursor: "pointer",
                    fontSize: 12, fontWeight: 600, fontFamily: "inherit", transition: "all 0.15s",
                    background: orgFilter === o ? (ORGS[o]?.bg || C.accentLt) : C.card,
                    color: orgFilter === o ? (ORGS[o]?.text || C.accentDk) : C.muted,
                  }}>{o}{o !== "All" && <span style={{ opacity: .5, marginLeft: 3 }}>({workstreams.filter(w => w.org === o).length})</span>}</button>
                ))}
                <span style={{ width: 1, height: 20, background: C.border, margin: "0 4px" }} />
                {["All", "Active", "Inactive"].map(af => {
                  const count = af === "All" ? workstreams.length : workstreams.filter(w => {
                    const hasActivity = w.items.some(i => hasRecentDiscussion(i)) ||
                      w.items.some(i => ["in_progress", "waiting", "blocked"].includes(i.status));
                    return af === "Active" ? hasActivity : !hasActivity;
                  }).length;
                  const afColors = { All: {}, Active: { bg: "#E8F0EB", text: C.accent }, Inactive: { bg: "#F5F3ED", text: C.muted } };
                  const afc = afColors[af] || {};
                  return (
                    <button key={af} onClick={() => setActivityFilter(af)} style={{
                      padding: "5px 12px", borderRadius: 6, border: `1px solid ${C.border}`, cursor: "pointer",
                      fontSize: 12, fontWeight: 600, fontFamily: "inherit", transition: "all 0.15s",
                      background: activityFilter === af ? (afc.bg || C.accentLt) : C.card,
                      color: activityFilter === af ? (afc.text || C.accentDk) : C.muted,
                    }}>
                      {af === "Active" && "🟢 "}{af === "Inactive" && "○ "}{af}
                      {af !== "All" && <span style={{ opacity: .5, marginLeft: 3 }}>({count})</span>}
                    </button>
                  );
                })}
                <span style={{ width: 1, height: 20, background: C.border, margin: "0 4px" }} />
                <select value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)} title="Filter by who it's assigned to"
                  style={{ padding: "5px 10px", borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 12, fontWeight: 600, fontFamily: "inherit", cursor: "pointer", background: ownerFilter === "All" ? C.card : C.accentLt, color: ownerFilter === "All" ? C.muted : C.accentDk, outline: "none" }}>
                  {["All", "David Comora", "Lee Duis", "Eric Baughman", "Dave Nichols", "Scott Newman", "Unassigned"].map(o => (
                    <option key={o} value={o}>{o === "All" ? "Owner: All" : o}</option>
                  ))}
                </select>
              </div>
              <input placeholder="Search workstreams or tasks..." value={search} onChange={e => setSearch(e.target.value)}
                style={{ padding: "7px 12px", borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: "inherit", width: 230, background: C.card, color: C.ink, outline: "none" }} />
            </div>

            {/* ── PRIORITY REORDER BAR ── */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
              {!reorderMode ? (
                <>
                  <button onClick={enterReorder} disabled={!canReorder} title={canReorder ? "Drag workstreams into David's priority order" : "Clear search/filters to reorder the full portfolio"}
                    style={{ padding: "6px 12px", borderRadius: 6, border: `1px solid ${C.border}`, background: canReorder ? C.card : C.paper, color: canReorder ? C.ink : C.muted, fontSize: 12, fontWeight: 600, fontFamily: "inherit", cursor: canReorder ? "pointer" : "not-allowed" }}>
                    ↕ Reorder priorities
                  </button>
                  {!canReorder && <span style={{ fontSize: 11, color: C.muted }}>Clear search/filters to reorder the full list.</span>}
                  {orderMsg && <span style={{ fontSize: 11.5, color: orderMsg.includes("Couldn't") || orderMsg.includes("not wired") ? C.warn : C.accent, fontWeight: 600 }}>{orderMsg}</span>}
                </>
              ) : (
                <>
                  <span style={{ fontSize: 12, fontWeight: 700, color: C.ink, display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 14 }}>⠿</span> Drag workstreams into priority order
                  </span>
                  <button onClick={saveOrder} disabled={savingOrder}
                    style={{ padding: "6px 14px", borderRadius: 6, border: "none", background: C.accent, color: "#fff", fontSize: 12, fontWeight: 700, fontFamily: "inherit", cursor: savingOrder ? "wait" : "pointer", opacity: savingOrder ? 0.7 : 1 }}>
                    {savingOrder ? "Saving…" : "✓ Save order"}
                  </button>
                  <button onClick={cancelReorder} disabled={savingOrder}
                    style={{ padding: "6px 12px", borderRadius: 6, border: `1px solid ${C.border}`, background: C.card, color: C.muted, fontSize: 12, fontWeight: 600, fontFamily: "inherit", cursor: "pointer" }}>
                    Cancel
                  </button>
                  <span style={{ fontSize: 11, color: C.muted }}>Saves to Jira as you; others see it on their next refresh.</span>
                </>
              )}
            </div>

            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
              <div className="ws-header" style={{ padding: "7px 14px", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.07em", color: C.muted, fontWeight: 600, borderBottom: `1px solid ${C.border}`, background: C.paper }}>
                <div /><div>Workstream</div><div className="ws-col-priority">Priority</div><div className="ws-col-progress">Progress</div><div className="ws-col-mix">Status Mix</div><div />
              </div>
              {portfolioList.length === 0 && (
                <div style={{ padding: 40, textAlign: "center", color: C.muted, fontSize: 14 }}>
                  {search ? "No workstreams match your search" : "No workstreams found for this filter"}
                </div>
              )}
              {portfolioList.map(ws => {
                const sc = ws.items.reduce((a, i) => { a[i.status] = (a[i.status] || 0) + 1; return a; }, {});
                const bc = sc.blocked || 0;
                const oc = ORGS[ws.org] || ORGS.CoStar;
                const isExpanded = expanded.has(ws.key);
                const showingInfo = infoTooltip === ws.key;
                // Goal chip: which goal (if any) this workstream serves, matched by goal-<slug> label.
                const wsGoal = goals.find(g => g.slug && (ws.labels || []).includes(g.slug)) || null;
                return (
                  <div key={ws.key}
                    id={`ws-${ws.key}`}
                    draggable={reorderMode}
                    onDragStart={reorderMode ? (() => setDragKey(ws.key)) : undefined}
                    onDragOver={reorderMode ? ((e) => { e.preventDefault(); if (dragKey && dragKey !== ws.key) reorderDraft(dragKey, ws.key); }) : undefined}
                    onDragEnd={reorderMode ? (() => setDragKey(null)) : undefined}
                    style={{
                      borderBottom: `1px solid ${C.border}`,
                      cursor: reorderMode ? "grab" : "default",
                      opacity: reorderMode && dragKey === ws.key ? 0.45 : 1,
                      background: reorderMode ? C.paper : "transparent",
                    }}>
                    {/* ── WORKSTREAM ROW ── */}
                    <div className="ws-row" style={{
                      padding: "11px 14px", background: isExpanded ? C.paper : "transparent", transition: "background 0.12s",
                    }}>
                      <div className="ws-col-bar" style={{ width: 5, height: 28, borderRadius: 2, background: oc.accent }} />
                      <div className="ws-col-name" style={{ minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          {reorderMode && <span title="Drag to reorder" style={{ color: C.muted, fontSize: 15, cursor: "grab", flexShrink: 0 }}>⠿</span>}
                          <span style={{ fontWeight: 600, fontSize: 14 }}>{ws.name}</span>
                          <OrgBadge org={ws.org} />
                          {wsGoal && (
                            <span
                              title={`Serves goal: ${wsGoal.goal?.goal || wsGoal.summary} — click to open`}
                              onClick={(e) => { e.stopPropagation(); openGoal(wsGoal.key); }}
                              style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 10, background: oc.bg, color: oc.text, border: `1px solid ${oc.ring || C.border}`, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}
                            >
                              🎯 Goal
                            </span>
                          )}
                          {bc > 0 && <span style={{ fontSize: 10, color: "#B33B3B", fontWeight: 600 }}>⚠ {bc} blocked</span>}
                          {(() => {
                            const activeCount = ws.items.filter(i => hasRecentDiscussion(i)).length;
                            const totalComments = ws.items.reduce((sum, i) => sum + (i.totalComments || 0), 0);
                            return activeCount > 0 ? (
                              <span style={{ fontSize: 10, color: C.gold, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 2 }}
                                title={`${activeCount} task${activeCount > 1 ? "s" : ""} with comments in the last 7 days (${totalComments} total comments)`}>
                                💬 {activeCount} active
                              </span>
                            ) : null;
                          })()}
                          <span
                            onClick={(e) => { e.stopPropagation(); setInfoTooltip(showingInfo ? null : ws.key); }}
                            style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 18, height: 18, borderRadius: "50%", border: `1px solid ${showingInfo ? C.blue : C.border}`, background: showingInfo ? C.blueLt : "transparent", color: showingInfo ? C.blue : C.muted, fontSize: 11, fontWeight: 700, cursor: "pointer", transition: "all 0.15s", fontStyle: "italic", fontFamily: "Georgia, serif", lineHeight: 1 }}
                            title="View workstream description"
                          >i</span>
                        </div>
                        <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                          {ws.key} · {ws.owner} · {ws.totalCount} tasks
                          {ws.components.length > 0 && ` · ${ws.components.join(", ")}`}
                        </div>
                        {/* Mobile-only: show priority + progress inline under name */}
                        <div className="mobile-only" style={{ gap: 8, alignItems: "center", marginTop: 4 }}>
                          <PriBadge pri={ws.pri} />
                          <span style={{ fontSize: 11, color: C.muted }}><span style={{ fontWeight: 600, color: C.accent }}>{ws.doneCount}</span>/{ws.totalCount} done</span>
                        </div>
                      </div>
                      <div className="ws-col-priority"><PriBadge pri={ws.pri} /></div>
                      <div className="ws-col-progress" style={{ fontSize: 12, color: C.muted }}>
                        <span style={{ fontWeight: 600, color: C.accent }}>{ws.doneCount}</span>/{ws.totalCount} done
                      </div>
                      <div className="ws-col-mix" style={{ display: "flex", gap: 2, alignItems: "center" }}>
                        {ws.totalCount > 0
                          ? Object.entries(sc).map(([s, c]) => <div key={s} style={{ height: 6, borderRadius: 3, flex: c, background: STATUS[s]?.c || "#ccc", opacity: .65 }} />)
                          : <div style={{ height: 6, borderRadius: 3, flex: 1, background: "#ddd", opacity: .3 }} />
                        }
                      </div>
                      <div className="ws-col-action">
                        <button onClick={() => toggle(ws.key)} style={{
                          padding: "4px 10px", borderRadius: 5, border: `1px solid ${isExpanded ? C.accent : C.border}`,
                          background: isExpanded ? C.accentLt : "transparent", color: isExpanded ? C.accent : C.muted,
                          fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
                          transition: "all 0.15s",
                        }}>
                          {isExpanded ? "▾ Collapse" : "▸ Drill Down"}
                        </button>
                      </div>
                    </div>

                    {/* ── INFO TOOLTIP ── */}
                    {showingInfo && (
                      <div style={{ padding: "8px 26px 12px", animation: "fadeIn 0.2s ease" }}>
                        <div style={{ background: C.blueLt, border: `1px solid ${C.blue}33`, borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#2a4a5a", lineHeight: 1.6, position: "relative" }}>
                          <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: C.blue, fontWeight: 700, marginBottom: 4 }}>About this workstream</div>
                          {ws.description
                            ? (typeof ws.description === "string" ? <FormattedDescription text={ws.description} max={400} color="#2a4a5a" labelColor={C.blue} /> : "See Jira for full description")
                            : <span style={{ fontStyle: "italic", color: C.muted }}>No description set in Jira</span>
                          }
                          <button onClick={() => setInfoTooltip(null)} style={{ position: "absolute", top: 6, right: 10, background: "none", border: "none", color: C.blue, cursor: "pointer", fontSize: 14, fontWeight: 600, fontFamily: "inherit" }}>×</button>
                        </div>
                      </div>
                    )}

                    {/* ── EXPANDED TASKS ── */}
                    {isExpanded && !reorderMode && (
                      <div style={{ padding: "0 14px 10px 26px", animation: "fadeIn 0.2s ease" }}>
                        {ws.description && typeof ws.description === "string" ? (
                          <div style={{ background: `${C.blue}0D`, border: `1px solid ${C.blue}33`, borderRadius: 8, padding: "12px 16px", marginBottom: 10, maxHeight: 460, overflow: "auto" }}>
                            <FormattedDescription text={ws.description} labelColor={C.blue} color={C.ink} />
                          </div>
                        ) : ws.statusUpdate && (
                          <div style={{ background: `${C.blue}0D`, border: `1px solid ${C.blue}33`, borderRadius: 8, padding: "10px 14px", marginBottom: 10 }}>
                            <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: C.blue, fontWeight: 700, marginBottom: 3 }}>
                              Status Update{ws.statusUpdate.date ? ` · ${ws.statusUpdate.date}` : ""}
                            </div>
                            <div style={{ fontSize: 13, lineHeight: 1.5, color: C.ink, whiteSpace: "pre-wrap" }}>{ws.statusUpdate.text.slice(0, 600)}</div>
                          </div>
                        )}
                        {((ws.recentComments && ws.recentComments.length > 0) || ws.totalComments > 0) && (
                          <div style={{ marginBottom: 8, paddingBottom: 8, borderBottom: `1px solid ${C.border}` }}>
                            <CommentThread item={{ key: ws.key, recentComments: ws.recentComments, lastComment: ws.lastComment, totalComments: ws.totalComments }} onAddComment={addThreadComment} accentColor={C.blue} />
                          </div>
                        )}
                        {/* ── COHESION TIME — workstream level: the Epic's ClockIn project ── */}
                        {ws.cohesionProjectId && (
                          <div style={{ marginBottom: 8 }}>
                            <CohesionTime state={cohesionTimeByKey[ws.key]} />
                          </div>
                        )}
                        {ws.items.length === 0 && (
                          <div style={{ padding: "12px 10px", fontSize: 12, color: C.muted, fontStyle: "italic" }}>No tasks yet</div>
                        )}
                        {[...ws.items].sort((a, b) => (PRI_SORT[a.priority] ?? 1) - (PRI_SORT[b.priority] ?? 1)).map(item => {
                          const taskExpanded = expandedTasks.has(item.key);
                          const ns = nextStepDisplay(item);
                          return (
                            <div key={item.key} id={`task-${item.key}`} style={{ borderBottom: "1px solid rgba(0,0,0,0.04)" }}>
                              <div className="task-row" style={{
                                padding: "8px 10px",
                                borderRadius: 4, transition: "background 0.1s",
                                background: taskExpanded ? "rgba(234,242,248,0.5)" : "transparent",
                              }}>
                                <div>
                                  {item.priority === "High" && <span style={{ color: "#A32D2D", marginRight: 4, fontSize: 8 }}>●</span>}
                                  <span style={{ fontWeight: 500, fontSize: 13 }}>{item.summary}</span>
                                  <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>
                                    {item.key} · {item.assignee}
                                    {(() => {
                                      const dd = docsFor(item);
                                      const cl = dd.confluenceLinks.length;
                                      const wl = dd.webLinks.length;
                                      const at = item.attachments?.length || 0;
                                      const docCount = cl + wl + at;
                                      if (docCount === 0) return null;
                                      const parts = [];
                                      if (cl) parts.push(`${cl} Confluence link${cl > 1 ? "s" : ""}`);
                                      if (wl) parts.push(`${wl} web link${wl > 1 ? "s" : ""}`);
                                      if (at) parts.push(`${at} attachment${at > 1 ? "s" : ""}`);
                                      return (
                                        <span title={parts.join(" · ")} style={{ marginLeft: 6, color: C.blue, cursor: "default" }}>
                                          📄 {docCount}
                                        </span>
                                      );
                                    })()}
                                    {hasRecentDiscussion(item) && (
                                      <span title={`${recentDiscussionCount(item)} comment${recentDiscussionCount(item) > 1 ? "s" : ""} in the last 7 days`} style={{ marginLeft: 6, color: C.gold, cursor: "default", fontWeight: 600 }}>
                                        💬 {item.totalComments || recentDiscussionCount(item)}
                                      </span>
                                    )}
                                  </div>
                                  {ns && (
                                    <div style={{ fontSize: 11, marginTop: 3, display: "flex", gap: 5, alignItems: "baseline", minWidth: 0, color: ns.explicit ? C.accent : C.muted, fontStyle: ns.explicit ? "normal" : "italic" }}>
                                      <span style={{ flexShrink: 0, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", fontSize: 9 }}>Next</span>
                                      <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ns.text}</span>
                                    </div>
                                  )}
                                </div>
                                <StatusPill status={item.status} />
                                <span className="task-col-pri"><PriBadge pri={item.priority} /></span>
                                <button onClick={() => toggleTask(item.key)} style={{
                                  padding: "2px 8px", borderRadius: 4, border: `1px solid ${taskExpanded ? C.blue : C.border}`,
                                  background: taskExpanded ? C.blueLt : "transparent", color: taskExpanded ? C.blue : C.muted,
                                  fontSize: 10, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
                                  transition: "all 0.15s",
                                }}>
                                  {taskExpanded ? "▾ Less" : "▸ Details"}
                                </button>
                                <a href={`${JIRA_BASE}/browse/${item.key}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ color: C.blue, fontSize: 12, textAlign: "right", textDecoration: "none" }}>↗</a>
                              </div>
                              {/* ── TASK DETAIL PANEL ── */}
                              {taskExpanded && (
                                <div style={{ padding: "8px 10px 12px 24px", animation: "fadeIn 0.15s ease" }}>
                                  <div style={{ background: C.paper, border: `1px solid ${C.border}`, borderRadius: 8, padding: "12px 16px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, fontSize: 12 }}>
                                    {ns && (
                                      <div style={{ gridColumn: "1 / -1", background: ns.explicit ? `${C.accent}0D` : "transparent", border: ns.explicit ? `1px solid ${C.accent}33` : `1px dashed ${C.border}`, borderRadius: 6, padding: "8px 12px" }}>
                                        <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: C.accent, fontWeight: 700, marginBottom: 3 }}>→ Next Step</div>
                                        <div style={{ color: ns.explicit ? C.ink : C.muted, fontStyle: ns.explicit ? "normal" : "italic", fontSize: 13, lineHeight: 1.5 }}>
                                          {ns.text}
                                          {!ns.explicit && <span style={{ fontSize: 10, marginLeft: 6, color: C.muted }}>(default from status — no Next Step set)</span>}
                                        </div>
                                      </div>
                                    )}
                                    <div>
                                      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted, fontWeight: 600, marginBottom: 4 }}>Labels</div>
                                      <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                                        {visibleLabels(item.labels).map(l => (
                                          <span key={l} style={{ fontSize: 10, padding: "1px 6px", borderRadius: 3, background: C.card, border: `1px solid ${C.border}`, color: C.muted }}>{l}</span>
                                        ))}
                                        {visibleLabels(item.labels).length === 0 && <span style={{ color: C.muted, fontStyle: "italic" }}>—</span>}
                                      </div>
                                    </div>
                                    <div>
                                      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted, fontWeight: 600, marginBottom: 4 }}>Updated</div>
                                      <div style={{ color: C.ink }}>{item.updated ? new Date(item.updated).toLocaleDateString() : "—"}</div>
                                    </div>
                                    {item.description && (
                                      <div style={{ gridColumn: "1 / -1" }}>
                                        <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted, fontWeight: 600, marginBottom: 4 }}>Description</div>
                                        <div style={{ color: "#3a3a36", lineHeight: 1.5, maxHeight: 120, overflow: "auto" }}>
                                          {typeof item.description === "string" ? <FormattedDescription text={item.description} /> : "See Jira for full description"}
                                        </div>
                                      </div>
                                    )}
                                    {/* ── RELATED DOCUMENTS ── */}
                                    {(() => {
                                      const d = docsFor(item);
                                      const hasLinks = d.confluenceLinks.length > 0 || d.webLinks.length > 0;
                                      const hasAtt = item.attachments && item.attachments.length > 0;
                                      if (!hasLinks && !hasAtt) return null;
                                      return (
                                      <div style={{ gridColumn: "1 / -1" }}>
                                        <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: C.blue, fontWeight: 700, marginBottom: 6 }}>
                                          📄 Related Documents ({d.confluenceLinks.length + d.webLinks.length + (item.attachments?.length || 0)})
                                        </div>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                          {d.confluenceLinks.map((link, li) => (
                                            <a key={`cl-${li}`} href={link.url} target="_blank" rel="noopener noreferrer" style={{
                                              display: "flex", alignItems: "center", gap: 8, padding: "8px 12px",
                                              background: C.blueLt || "#EAF2F8", borderRadius: 6, border: `1px solid ${C.blue}33`,
                                              textDecoration: "none",
                                            }}>
                                              <span style={{ fontSize: 14, flexShrink: 0 }}>📋</span>
                                              <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontSize: 12, fontWeight: 600, color: C.blue }}>{link.title || "Confluence Document"}</div>
                                                <div style={{ fontSize: 10, color: C.muted }}>Confluence page</div>
                                              </div>
                                              <span style={{ fontSize: 11, color: C.blue, flexShrink: 0 }}>Open ↗</span>
                                            </a>
                                          ))}
                                          {d.webLinks.map((link, li) => {
                                            const u = (link.url || "").toLowerCase();
                                            const kind = /sharepoint\.com|-my\.sharepoint|onedrive/.test(u) ? "SharePoint document"
                                              : /docs\.google\.com|drive\.google\.com/.test(u) ? "Google Doc"
                                              : "Web link";
                                            return (
                                              <a key={`wl-${li}`} href={link.url} target="_blank" rel="noopener noreferrer" style={{
                                                display: "flex", alignItems: "center", gap: 8, padding: "8px 12px",
                                                background: C.blueLt || "#EAF2F8", borderRadius: 6, border: `1px solid ${C.blue}33`,
                                                textDecoration: "none",
                                              }}>
                                                <span style={{ fontSize: 14, flexShrink: 0 }}>🔗</span>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                  <div style={{ fontSize: 12, fontWeight: 600, color: C.blue, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{link.title || "Web link"}</div>
                                                  <div style={{ fontSize: 10, color: C.muted }}>{kind} · opens in browser</div>
                                                </div>
                                                <span style={{ fontSize: 11, color: C.blue, flexShrink: 0 }}>Open ↗</span>
                                              </a>
                                            );
                                          })}
                                          {(item.attachments || []).map((att, ai) => {
                                            const isPdf = (att.mime || "").includes("pdf");
                                            const glyph = isPdf ? "📕" : (att.mime || "").includes("html") ? "📄" : "📎";
                                            const kind = isPdf ? "PDF" : (att.mime || "").includes("html") ? "HTML" : (att.mime || "File");
                                            return (
                                              <a key={`at-${ai}`} href={att.url} target="_blank" rel="noopener noreferrer" style={{
                                                display: "flex", alignItems: "center", gap: 8, padding: "8px 12px",
                                                background: C.blueLt || "#EAF2F8", borderRadius: 6, border: `1px solid ${C.blue}33`,
                                                textDecoration: "none",
                                              }}>
                                                <span style={{ fontSize: 14, flexShrink: 0 }}>{glyph}</span>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                  <div style={{ fontSize: 12, fontWeight: 600, color: C.blue, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{att.filename}</div>
                                                  <div style={{ fontSize: 10, color: C.muted }}>Jira attachment · {kind}</div>
                                                </div>
                                                <span style={{ fontSize: 11, color: C.blue, flexShrink: 0 }}>Download ↓</span>
                                              </a>
                                            );
                                          })}
                                        </div>
                                      </div>
                                      );
                                    })()}
                                    {/* ── COHESION TIME (ENG-18) — only when the task is mapped to a Cohesion project ── */}
                                    {item.cohesionProjectId && (
                                      <CohesionTime state={cohesionTimeByKey[item.key]} />
                                    )}
                                    {/* ── DISCUSSION THREAD ── */}
                                    <div style={{ gridColumn: "1 / -1" }}>
                                      <CommentThread item={item} onAddComment={addThreadComment} accentColor={C.accent} />
                                    </div>
                                    <div style={{ gridColumn: "1 / -1", display: "flex", gap: 6, paddingTop: 8, borderTop: `1px solid ${C.border}` }}>
                                      <a href={`${JIRA_BASE}/browse/${item.key}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                                        <Btn variant="outline" style={{ fontSize: 11, padding: "4px 10px" }}>Open in Jira ↗</Btn>
                                      </a>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Orphan tasks */}
            {orphanTasks.length > 0 && orgFilter === "All" && !search && (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted, fontWeight: 600, marginBottom: 6 }}>Tasks without a Workstream ({orphanTasks.length})</div>
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
                  {orphanTasks.slice(0, 10).map(item => (
                    <div key={item.key} style={{ display: "grid", gridTemplateColumns: "1fr 110px 80px 20px", gap: 8, padding: "10px 14px", borderBottom: "1px solid rgba(0,0,0,0.04)" }}>
                      <div>
                        <span style={{ fontWeight: 500, fontSize: 13 }}>{item.summary}</span>
                        <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>{item.key} · {item.assignee}</div>
                      </div>
                      <StatusPill status={item.status} />
                      <OrgBadge org={item.org} />
                      <a href={`${JIRA_BASE}/browse/${item.key}`} target="_blank" rel="noopener noreferrer" style={{ color: C.blue, fontSize: 12, textDecoration: "none" }}>↗</a>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* ── DETAIL VIEW ── */}
        {!loading && view === "portfolio" && detailItem && (
          <div style={{ animation: "fadeIn 0.25s ease" }}>
            <button onClick={() => setDetailItem(null)} style={{ background: "none", border: "none", cursor: "pointer", color: C.blue, fontSize: 13, fontFamily: "inherit", padding: 0, marginBottom: 12, fontWeight: 500 }}>← Back to portfolio</button>
            <div style={{ background: C.card, border: `2px solid ${ORGS[detailItem.org]?.accent || C.accent}`, borderRadius: 10, overflow: "hidden" }}>
              <div style={{ padding: "18px 22px", borderBottom: `1px solid ${C.border}` }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 8 }}>
                      <StatusPill status={detailItem.status} />
                      <OrgBadge org={detailItem.org} />
                      <span style={{ fontSize: 11, color: C.muted, fontFamily: "monospace" }}>{detailItem.key}</span>
                    </div>
                    <div style={{ fontSize: 20, fontWeight: 700 }}>{detailItem.summary}</div>
                    <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>
                      {detailItem.ws} · Assigned to {detailItem.assignee}
                      {detailItem.epicKey && ` · Epic ${detailItem.epicKey}`}
                    </div>
                  </div>
                  <PriBadge pri={detailItem.priority} />
                </div>
              </div>
              <div style={{ padding: "18px 22px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                  <div>
                    <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted, fontWeight: 600, marginBottom: 6 }}>Labels</div>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {visibleLabels(detailItem.labels).map(l => (
                        <span key={l} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, background: C.paper, border: `1px solid ${C.border}`, color: C.muted }}>{l}</span>
                      ))}
                      {visibleLabels(detailItem.labels).length === 0 && <span style={{ fontSize: 12, color: C.muted, fontStyle: "italic" }}>No labels</span>}
                    </div>
                    {detailItem.components.length > 0 && (
                      <>
                        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted, fontWeight: 600, marginBottom: 6, marginTop: 16 }}>Components</div>
                        <div style={{ fontSize: 13 }}>{detailItem.components.join(", ")}</div>
                      </>
                    )}
                    <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted, fontWeight: 600, marginBottom: 6, marginTop: 16 }}>Updated</div>
                    <div style={{ fontSize: 13 }}>{detailItem.updated ? new Date(detailItem.updated).toLocaleDateString() : "—"}</div>
                  </div>
                  <div>
                    {detailItem.description && (
                      <>
                        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted, fontWeight: 600, marginBottom: 6 }}>Description</div>
                        <div style={{ fontSize: 13, lineHeight: 1.6, color: "#3a3a36", maxHeight: 200, overflow: "auto" }}>
                          {typeof detailItem.description === "string" ? <FormattedDescription text={detailItem.description} /> : "See Jira for full description"}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
              {/* ── COHESION TIME (ENG-18) — only when mapped ── */}
              {detailItem.cohesionProjectId && (
                <div style={{ padding: "0 22px 18px" }}>
                  <CohesionTime state={cohesionTimeByKey[detailItem.key]} />
                </div>
              )}
              <div style={{ padding: "12px 22px", borderTop: `1px solid ${C.border}`, display: "flex", gap: 6 }}>
                <a href={`${JIRA_BASE}/browse/${detailItem.key}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                  <Btn variant="outline">Open in Jira ↗</Btn>
                </a>
              </div>
            </div>
          </div>
        )}

        {/* ╔══════════════ WEEKLY STATUS VIEW ══════════════╗ */}
        {!loading && view === "brief" && (
          <div style={{ animation: "fadeIn 0.25s ease" }}>
            <div style={{ background: C.ink, borderRadius: "10px 10px 0 0", padding: "18px 22px", color: "#fff" }}>
              <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.14em", color: "#9c9a92" }}>CoStar Partners · Live Portfolio Status</div>
              <div style={{ fontFamily: "'Source Serif 4',Georgia,serif", fontSize: 20, fontWeight: 600, marginTop: 4 }}>Executive Portfolio — Current Status</div>
              <div style={{ fontSize: 12, color: "#9c9a92", marginTop: 2 }}>Live from Jira · LEAD Project · {lastRefresh?.toLocaleString() || "—"}</div>
            </div>

            {/* Metrics */}
            <div style={{ background: C.card, borderLeft: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`, padding: "16px 22px", borderBottom: `1px solid ${C.border}` }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 8 }}>
                {[
                  { l: "Done This Week", v: doneThisWeek.length, c: C.accent },
                  { l: "In Progress", v: totalActive, c: C.blue },
                  { l: "Blocked", v: totalBlocked, c: "#B33B3B" },
                  { l: "Awaiting Review", v: awaitingItems.length, c: C.gold },
                  { l: "Total Tasks", v: tasks.length, c: C.muted },
                ].map((m, i) => (
                  <div key={i} style={{ background: C.paper, borderRadius: 6, padding: "8px 10px", textAlign: "center" }}>
                    <div style={{ fontSize: 22, fontWeight: 700, color: m.c }}>{m.v}</div>
                    <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted }}>{m.l}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Done This Week */}
            <div style={{ background: C.card, borderLeft: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`, padding: "18px 22px", borderBottom: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.06em", color: C.accent, fontWeight: 700, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 20, height: 20, borderRadius: "50%", background: C.accent, color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11 }}>✓</span>
                Completed this week — {doneThisWeek.length} items
              </div>
              {doneThisWeek.length === 0 && <div style={{ fontSize: 13, color: C.muted, padding: "8px 10px" }}>No items completed this week yet</div>}
              {doneThisWeek.map((item, i) => (
                <div key={item.key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderBottom: "1px solid rgba(0,0,0,0.04)", fontSize: 13 }}>
                  <span style={{ fontFamily: "monospace", fontSize: 11, color: C.muted, minWidth: 70 }}>{item.key}</span>
                  <span style={{ flex: 1, fontWeight: 500 }}>{item.summary}</span>
                  <span style={{ fontSize: 11, color: C.muted }}>{item.assignee}</span>
                  <OrgBadge org={item.org} />
                </div>
              ))}
            </div>

            {/* Status by workstream — the report (replaces the flat status lists) */}
            <div style={{ background: C.card, borderLeft: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`, padding: "18px 22px", borderBottom: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.06em", color: C.ink, fontWeight: 700, marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 20, height: 20, borderRadius: "50%", background: C.ink, color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11 }}>≣</span>
                Status by workstream
              </div>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 12 }}>In priority order · each workstream's status update and the work underneath it.</div>
              {[...workstreams]
                .sort((a, b) => (execRank(a) - execRank(b)) || ((PRI_SORT[a.pri] ?? 1) - (PRI_SORT[b.pri] ?? 1)))
                .map((ws, wsIdx) => {
                  const rank = wsIdx + 1; // positional rank in the sorted list (matches order regardless of stored tens)
                  const sOrder = { blocked: 0, waiting: 1, in_progress: 2, backlog: 3, approved: 4, done: 5 };
                  const items = [...ws.items].sort((a, b) => (sOrder[a.status] ?? 9) - (sOrder[b.status] ?? 9));
                  return (
                    <div key={ws.key} style={{ marginBottom: 14, paddingBottom: 12, borderBottom: `1px solid ${C.border}` }}>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                        {rank != null && <span style={{ fontSize: 10, fontWeight: 700, color: "#fff", background: C.accent, borderRadius: 4, padding: "1px 6px" }}>{rank}</span>}
                        <span style={{ fontWeight: 700, fontSize: 14 }}>{ws.name}</span>
                        <OrgBadge org={ws.org} />
                        <span style={{ fontFamily: "monospace", fontSize: 11, color: C.muted }}>{ws.key}</span>
                        <span style={{ marginLeft: "auto", fontSize: 11, color: C.muted }}>{ws.doneCount}/{ws.totalCount} done</span>
                      </div>
                      {typeof ws.description === "string" && ws.description.trim() ? (
                        <div style={{ marginTop: 6, background: `${C.blue}0D`, border: `1px solid ${C.blue}33`, borderRadius: 6, padding: "10px 14px" }}>
                          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 2 }}>
                            <a href={`${JIRA_BASE}/browse/${ws.key}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 10, color: C.blue, textDecoration: "none", whiteSpace: "nowrap", fontWeight: 600 }}>View in Jira ↗</a>
                          </div>
                          <div style={{ maxHeight: 360, overflow: "auto" }}>
                            <FormattedDescription text={ws.description} labelColor={C.blue} color={C.ink} fontSize={12.5} />
                          </div>
                        </div>
                      ) : ws.statusUpdate ? (
                        <div style={{ marginTop: 6, background: `${C.blue}0D`, border: `1px solid ${C.blue}33`, borderRadius: 6, padding: "8px 12px" }}>
                          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
                            <span style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.06em", color: C.blue, fontWeight: 700 }}>Status Update{ws.statusUpdate.date ? ` · ${ws.statusUpdate.date}` : ""}</span>
                            <a href={`${JIRA_BASE}/browse/${ws.key}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 10, color: C.blue, textDecoration: "none", whiteSpace: "nowrap", fontWeight: 600 }}>View in Jira ↗</a>
                          </div>
                          <div style={{ marginTop: 4, fontSize: 12.5, color: C.ink, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{ws.statusUpdate.text}</div>
                        </div>
                      ) : null}
                      {items.length === 0 && <div style={{ fontSize: 12, color: C.muted, fontStyle: "italic", marginTop: 6, marginLeft: 4 }}>No tasks defined yet</div>}
                      {items.map(item => {
                        const ns = nextStepDisplay(item);
                        return (
                          <div key={item.key} style={{ marginTop: 8, paddingLeft: 12, borderLeft: `2px solid ${C.border}` }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                              <StatusPill status={item.status} />
                              <span style={{ flex: 1, fontWeight: 500 }}>{item.summary}</span>
                              {hasRecentDiscussion(item) && <span style={{ fontSize: 11, color: C.gold, fontWeight: 600 }}>💬 {recentDiscussionCount(item)}</span>}
                              <span style={{ fontSize: 11, color: C.muted }}>{item.assignee}</span>
                            </div>
                            {ns && (
                              <div style={{ fontSize: 11, marginTop: 2, color: ns.explicit ? C.accent : C.muted, fontStyle: ns.explicit ? "normal" : "italic" }}>
                                <span style={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", fontSize: 9 }}>Next</span> {ns.text}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
            </div>

            {/* Blocked + Awaiting items now appear within "Status by workstream" above (with their status pills); counts remain in the metrics band, and David's actions live on the Awaiting Review tab. */}

            {/* (Awaiting Review detail lives on its own tab; items also appear by workstream above.) */}

            {/* Team Highlights — flagged by team members */}
            <div style={{ background: C.card, borderLeft: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`, padding: "18px 22px", borderBottom: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.06em", color: "#534AB7", fontWeight: 700, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 20, height: 20, borderRadius: "50%", background: "#534AB7", color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11 }}>★</span>
                Team Highlights — {highlights.length} items flagged by team members
              </div>
              {highlights.length === 0 && (
                <div style={{ fontSize: 13, color: C.muted, padding: "12px 10px", lineHeight: 1.6 }}>
                  <div style={{ fontStyle: "italic", marginBottom: 8 }}>No items flagged this week</div>
                  <div style={{ fontSize: 12, color: C.muted }}>
                    Team members can flag items for this section by adding the <span style={{ fontFamily: "monospace", background: C.paper, padding: "1px 6px", borderRadius: 3, border: `1px solid ${C.border}`, fontSize: 11 }}>brief-highlight</span> label to any Jira task in the LEAD project.
                  </div>
                </div>
              )}
              {highlights.map(item => (
                <div key={item.key} style={{ padding: "10px 10px", borderBottom: "1px solid rgba(0,0,0,0.04)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}>
                    <span style={{ fontFamily: "monospace", fontSize: 11, color: C.muted, minWidth: 70 }}>{item.key}</span>
                    <span style={{ flex: 1, fontWeight: 600 }}>{item.summary}</span>
                    <span style={{ fontSize: 11, color: C.muted }}>{item.assignee}</span>
                    <OrgBadge org={item.org} />
                    <StatusPill status={item.status} />
                  </div>
                  {/* Show the latest comment as the team member's narrative */}
                  {item.lastComment && (
                    <div style={{ marginTop: 6, marginLeft: 80, padding: "6px 10px", background: "#EEEDFE", borderRadius: 6, border: "1px solid #534AB733", fontSize: 12, color: "#3C3489", lineHeight: 1.5 }}>
                      <span style={{ fontWeight: 600 }}>{item.lastComment.author}:</span>{" "}
                      {item.lastComment.body.substring(0, 200)}{item.lastComment.body.length > 200 ? "…" : ""}
                    </div>
                  )}
                  {!item.lastComment && (
                    <div style={{ marginTop: 4, marginLeft: 80, fontSize: 11, color: C.muted, fontStyle: "italic" }}>
                      Flagged for brief — no comment added yet
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div style={{ background: C.paper, borderRadius: "0 0 10px 10px", border: `1px solid ${C.border}`, borderTop: "none", padding: "14px 22px" }}>
              {/* Snapshot result banner */}
              {snapshotResult && (
                <div style={{
                  padding: "10px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600, marginBottom: 12,
                  background: snapshotResult.success ? "#E8F0EB" : C.warnLt,
                  color: snapshotResult.success ? C.accent : C.warn,
                  border: `1px solid ${snapshotResult.success ? C.accent : C.warn}`,
                  animation: "fadeIn 0.25s ease",
                  display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
                }}>
                  <span>{snapshotResult.success ? "✓" : "⚠"} {snapshotResult.message}</span>
                  {snapshotResult.url && (
                    <a href={snapshotResult.url} target="_blank" rel="noopener noreferrer" style={{
                      color: C.accent, fontSize: 12, fontWeight: 600, textDecoration: "underline",
                    }}>View in Confluence ↗</a>
                  )}
                </div>
              )}

              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ fontSize: 12, color: C.muted }}>
                  Data pulled live from LEAD Jira project · {tasks.length} tasks across {epics.length} workstreams
                </div>
                <button onClick={saveWeeklySnapshot} disabled={snapshotSaving} style={{
                  padding: "8px 16px", borderRadius: 7, border: `1px solid ${C.accent}`,
                  background: snapshotSaving ? C.accentLt : C.accent, color: snapshotSaving ? C.accent : "#fff",
                  fontSize: 12, fontWeight: 600, cursor: snapshotSaving ? "default" : "pointer",
                  fontFamily: "inherit", transition: "all 0.15s", display: "flex", alignItems: "center", gap: 6,
                  opacity: snapshotSaving ? 0.7 : 1,
                }}>
                  {snapshotSaving ? (
                    <>
                      <span style={{ display: "flex", gap: 3 }}>{[0,1,2].map(i => <span key={i} style={{ display: "inline-block", width: 4, height: 4, borderRadius: "50%", background: C.accent, animation: `pulse 1.2s ease-in-out ${i * 0.15}s infinite` }} />)}</span>
                      Saving to Confluence...
                    </>
                  ) : (
                    <>📸 Save Weekly Snapshot</>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ╔══════════════ AWAITING DAVID VIEW ══════════════╗ */}
        {!loading && view === "david" && !activeApproval && (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
            <div style={{ padding: "14px 18px", background: `linear-gradient(135deg,${C.goldLt} 0%,#fff 100%)`, borderBottom: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: C.gold }}>Items Awaiting Review</div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{awaitingItems.length} items in "Awaiting Review" status need a decision or direction</div>
            </div>
            {awaitingItems.length === 0 && (
              <div style={{ padding: 40, textAlign: "center", color: C.muted, fontSize: 14 }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>✓</div>
                All caught up — no items in Awaiting Review status
              </div>
            )}
            {awaitingItems.sort((a, b) => daysSince(b.updated) - daysSince(a.updated)).map(item => {
              const age = daysSince(item.updated);
              const commentPreview = item.lastComment?.body ? item.lastComment.body.substring(0, 120) : null;
              return (
                <div key={item.key} onClick={() => setActiveApproval(item)} style={{
                  padding: "14px 18px",
                  borderBottom: `1px solid ${C.border}`, cursor: "pointer", transition: "all 0.15s",
                  background: age >= 7 ? "rgba(226,75,74,0.03)" : "transparent",
                }}>
                  <div className="awaiting-row">
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{item.summary}</div>
                      <div style={{ fontSize: 11, color: C.muted }}>{item.key} · Assigned to {item.assignee}</div>
                    </div>
                    <span className="aw-col-org"><OrgBadge org={item.org} /></span>
                    <span className="aw-col-pri"><PriBadge pri={item.priority} /></span>
                    <div style={{ fontSize: 13, fontWeight: 600, textAlign: "right", color: age >= 7 ? "#A32D2D" : age >= 3 ? C.gold : C.accent }}>
                      {age === 0 ? "Today" : `${age}d`}
                    </div>
                  </div>
                  {/* ── LATEST COMMENT PREVIEW ── */}
                  {commentPreview && (
                    <div style={{ marginTop: 6, padding: "6px 10px", background: C.paper, borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 12, color: "#4a4a46", lineHeight: 1.5 }}>
                      <span style={{ fontWeight: 600, color: C.blue, fontSize: 11 }}>{item.lastComment.author}:</span>{" "}
                      {commentPreview}{commentPreview.length >= 120 ? "…" : ""}
                    </div>
                  )}
                  {!commentPreview && (
                    <div style={{ marginTop: 6, fontSize: 11, color: C.muted, fontStyle: "italic" }}>
                      No comment — tap to view details and take action
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── APPROVAL DETAIL WITH ACTIONS ── */}
        {!loading && view === "david" && activeApproval && (
          <div style={{ animation: "fadeIn 0.25s ease" }}>
            <button onClick={() => { setActiveApproval(null); setActionResult(null); setActionComment(""); }} style={{ background: "none", border: "none", cursor: "pointer", color: C.blue, fontSize: 13, fontFamily: "inherit", padding: 0, marginBottom: 12, fontWeight: 500 }}>← Back to review queue</button>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
              <div style={{ padding: "14px 18px", background: C.goldLt, borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 12, fontSize: 11, fontWeight: 600, background: C.goldLt, color: C.gold, border: `1px solid ${C.gold}` }}>⏳ Awaiting Review</span>
                  <span style={{ fontSize: 12, color: C.gold, fontWeight: 600 }}>{daysSince(activeApproval.updated)} days since last update</span>
                </div>
                <OrgBadge org={activeApproval.org} />
              </div>
              <div style={{ padding: "18px 22px" }}>
                <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>{activeApproval.summary}</div>
                <div style={{ fontSize: 13, color: C.muted, marginBottom: 16 }}>
                  {activeApproval.key} · Assigned to {activeApproval.assignee}
                  {activeApproval.components.length > 0 && ` · ${activeApproval.components.join(", ")}`}
                </div>

                {/* ── REVIEW ASK + TYPE — what David is being asked to do ── */}
                {(() => {
                  const rt = (activeApproval.reviewType || "").toLowerCase();
                  const isAck = rt === "acknowledge";
                  const isDecision = rt === "decision";
                  const pill = isDecision
                    ? { label: "⬠ DECISION", bg: C.blueLt || "#EAF2F8", fg: C.blue, bd: C.blue }
                    : isAck
                    ? { label: "👁 ACKNOWLEDGE", bg: C.goldLt, fg: C.gold, bd: C.gold }
                    : { label: "⚑ TYPE NOT SET", bg: C.paper, fg: C.muted, bd: C.border };
                  return (
                    <div style={{ marginBottom: 16, border: `1px solid ${pill.bd}`, borderRadius: 8, overflow: "hidden" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 14px", background: pill.bg, borderBottom: `1px solid ${pill.bd}33` }}>
                        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", color: pill.fg }}>{pill.label}</span>
                        <span style={{ fontSize: 11, color: C.muted }}>
                          {isDecision ? "David must approve, send back, or decline"
                            : isAck ? "For David to read and mark reviewed — no approval needed"
                            : "Set Review Type in Jira (Decision / Acknowledge) to tailor the actions below"}
                        </span>
                      </div>
                      <div style={{ padding: "10px 14px", background: C.card }}>
                        <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted, fontWeight: 700, marginBottom: 4 }}>The ask</div>
                        <div style={{ fontSize: 14, lineHeight: 1.5, color: activeApproval.reviewAsk ? C.ink : C.muted, fontStyle: activeApproval.reviewAsk ? "normal" : "italic" }}>
                          {activeApproval.reviewAsk || "No specific ask recorded — set \"Review Ask\" on the Jira issue so David knows what's expected."}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div>
                    <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted, fontWeight: 600, marginBottom: 6 }}>Labels</div>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {visibleLabels(activeApproval.labels).map(l => (
                        <span key={l} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, background: C.paper, border: `1px solid ${C.border}`, color: C.muted }}>{l}</span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted, fontWeight: 600, marginBottom: 6 }}>Timeline</div>
                    <div style={{ fontSize: 12, color: C.muted }}>
                      Created: {activeApproval.created ? new Date(activeApproval.created).toLocaleDateString() : "—"}<br />
                      Updated: {activeApproval.updated ? new Date(activeApproval.updated).toLocaleDateString() : "—"}
                    </div>
                  </div>
                </div>

                {/* ── DISCUSSION THREAD ── */}
                <CommentThread item={activeApproval} onAddComment={addThreadComment} accentColor={C.gold} />

                {/* Description — background context */}
                {activeApproval.description && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted, fontWeight: 600, marginBottom: 6 }}>Background</div>
                    <div style={{ fontSize: 13, lineHeight: 1.6, color: "#3a3a36", maxHeight: 150, overflow: "auto", padding: "8px 10px", background: C.paper, borderRadius: 6, border: `1px solid ${C.border}` }}>
                      {typeof activeApproval.description === "string" ? <FormattedDescription text={activeApproval.description} /> : "See Jira for full description"}
                    </div>
                  </div>
                )}

                {/* ── PRIMARY: readable documents that open in the browser — Confluence pages + web links (e.g. SharePoint) ── */}
                {(() => {
                  const d = docsFor(activeApproval);
                  const readable = [
                    ...d.confluenceLinks.map(l => ({ ...l, kind: "Confluence page" })),
                    ...d.webLinks.map(l => {
                      const u = (l.url || "").toLowerCase();
                      const kind = /sharepoint\.com|-my\.sharepoint|onedrive/.test(u) ? "SharePoint document"
                        : /docs\.google\.com|drive\.google\.com/.test(u) ? "Google Doc"
                        : "Web link";
                      return { ...l, kind };
                    }),
                  ];
                  if (readable.length === 0) {
                    return d.linksLoading
                      ? <div style={{ marginTop: 16, fontSize: 12, color: C.muted, fontStyle: "italic" }}>Checking for linked documents…</div>
                      : null;
                  }
                  return (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: C.accent, fontWeight: 700, marginBottom: 8 }}>
                      📖 Open to read ({readable.length})
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {readable.map((link, i) => (
                        <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" style={{
                          display: "flex", alignItems: "center", gap: 12, padding: "13px 16px",
                          background: C.accentLt, borderRadius: 8, border: `1px solid ${C.accent}44`,
                          textDecoration: "none", transition: "all 0.15s",
                        }}>
                          <span style={{ fontSize: 20, flexShrink: 0 }}>📖</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: C.accentDk, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{link.title || "Review document"}</div>
                            <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>{link.kind} · opens in your browser</div>
                          </div>
                          <span style={{ fontSize: 13, fontWeight: 600, color: C.accent, flexShrink: 0 }}>Open to read ↗</span>
                        </a>
                      ))}
                    </div>
                  </div>
                  );
                })()}

                {/* ── SECONDARY: download-only fallback — Jira attachments ── */}
                {activeApproval.attachments && activeApproval.attachments.length > 0 && (
                  <div style={{ marginTop: (() => { const d = docsFor(activeApproval); return (d.confluenceLinks.length || d.webLinks.length) ? 10 : 16; })() }}>
                    <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted, fontWeight: 600, marginBottom: 6 }}>
                      Download a copy ({activeApproval.attachments.length})
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                      {activeApproval.attachments.map((att, i) => {
                        const isPdf = (att.mime || "").includes("pdf") || /\.pdf$/i.test(att.filename || "");
                        const isHtml = (att.mime || "").includes("html") || /\.html?$/i.test(att.filename || "");
                        const kind = isPdf ? "PDF" : isHtml ? "HTML" : (att.filename?.includes(".") ? att.filename.split(".").pop().toUpperCase() : "FILE");
                        const icon = isPdf ? "📄" : isHtml ? "🌐" : "📎";
                        return (
                          <a key={i} href={att.url} target="_blank" rel="noopener noreferrer" download style={{
                            display: "flex", alignItems: "center", gap: 9, padding: "8px 12px",
                            background: C.paper, borderRadius: 6, border: `1px solid ${C.border}`,
                            textDecoration: "none", transition: "all 0.15s",
                          }}>
                            <span style={{ fontSize: 15, flexShrink: 0, opacity: 0.7 }}>{icon}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 12, fontWeight: 600, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{att.filename || "Attachment"}</div>
                              <div style={{ fontSize: 10, color: C.muted, marginTop: 1 }}>{kind} · downloads a copy</div>
                            </div>
                            <span style={{ fontSize: 11, color: C.muted, flexShrink: 0 }}>Download ↓</span>
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ── ACTION RESULT BANNER ── */}
                {actionResult && (
                  <div style={{
                    marginTop: 16, padding: "10px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600,
                    background: actionResult.success ? "#E8F0EB" : C.warnLt,
                    color: actionResult.success ? C.accent : C.warn,
                    border: `1px solid ${actionResult.success ? C.accent : C.warn}`,
                    animation: "fadeIn 0.25s ease",
                  }}>
                    {actionResult.success ? "✓ " : "⚠ "}{actionResult.message}
                  </div>
                )}

                {/* ── ACTING AS + COMMENT INPUT ── */}
                {!actionResult?.success && (
                  <div style={{ marginTop: 16 }}>
                    {/* Acting As */}
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, padding: "10px 14px", background: C.paper, borderRadius: 8, border: `1px solid ${C.border}` }}>
                      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted, fontWeight: 600, whiteSpace: "nowrap" }}>Acting as:</div>
                      <input
                        value={actingAs}
                        onChange={e => setActingAs(e.target.value)}
                        style={{
                          flex: 1, padding: "5px 10px", borderRadius: 5, border: `1px solid ${C.border}`,
                          fontSize: 13, fontFamily: "inherit", fontWeight: 600, color: C.ink,
                          background: C.card, outline: "none",
                        }}
                      />
                      <div style={{ fontSize: 10, color: C.muted, maxWidth: 200, lineHeight: 1.4 }}>
                        Jira audit trail captures the authenticated user. This name appears in the comment.
                      </div>
                    </div>
                    {/* Comment */}
                    <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted, fontWeight: 600, marginBottom: 6 }}>
                      Your note — the outcome of your review (required for Request Info)
                    </div>
                    <textarea
                      value={actionComment}
                      onChange={e => setActionComment(e.target.value)}
                      placeholder="e.g., Proceed as asked. / Hold until we discuss Friday. / Need a cost breakdown before I can weigh in."
                      style={{
                        width: "100%", minHeight: 70, padding: "10px 12px", borderRadius: 6,
                        border: `1px solid ${C.border}`, fontSize: 13, fontFamily: "inherit",
                        background: C.paper, color: C.ink, resize: "vertical", outline: "none",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                )}

                {/* ── ACTION BUTTONS ── */}
                {!actionResult?.success && (
                  <div style={{ display: "flex", gap: 8, marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}`, flexWrap: "wrap", alignItems: "center" }}>
                    <button
                      onClick={() => executeAction("reviewed", activeApproval)}
                      disabled={!!actionPending}
                      style={{
                        padding: "8px 20px", borderRadius: 6, border: "none", fontSize: 13, fontWeight: 700,
                        cursor: actionPending ? "not-allowed" : "pointer", fontFamily: "inherit",
                        background: "#534AB7", color: "#fff", opacity: actionPending && actionPending !== "reviewed" ? 0.4 : 1,
                        transition: "all 0.15s",
                      }}
                    >
                      {actionPending === "reviewed" ? "Recording..." : "👁 Reviewed"}
                    </button>
                    <button
                      onClick={() => {
                        if (!actionComment.trim()) { setActionResult({ success: false, message: "Add a note saying what you need, then Request Info." }); return; }
                        executeAction("info", activeApproval);
                      }}
                      disabled={!!actionPending}
                      style={{
                        padding: "8px 20px", borderRadius: 6, border: `1px solid #B5651D`, fontSize: 13, fontWeight: 700,
                        cursor: actionPending ? "not-allowed" : "pointer", fontFamily: "inherit",
                        background: "transparent", color: "#B5651D", opacity: actionPending && actionPending !== "info" ? 0.4 : 1,
                        transition: "all 0.15s",
                      }}
                    >
                      {actionPending === "info" ? "Sending..." : "? Request Info"}
                    </button>
                    <div style={{ flex: 1 }} />
                    <a href={`${JIRA_BASE}/browse/${activeApproval.key}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                      <Btn variant="outline">Open in Jira ↗</Btn>
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ╔══════════════ BLOCKED VIEW ══════════════╗ */}
        {!loading && view === "blocked" && !activeBlockedItem && (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
            <div style={{ padding: "14px 18px", background: "rgba(252,235,235,0.5)", borderBottom: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: "#B33B3B" }}>Blocked Items</div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{blockedItems.length} items cannot proceed — tap any item to see details or provide direction</div>
            </div>
            {blockedItems.length === 0 && (
              <div style={{ padding: 40, textAlign: "center", color: C.muted, fontSize: 14 }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>✓</div>
                Nothing blocked — all clear
              </div>
            )}
            {blockedItems.map(item => {
              const age = daysSince(item.updated);
              const commentPreview = item.lastComment?.body ? item.lastComment.body.substring(0, 120) : null;
              return (
                <div key={item.key} onClick={() => setActiveBlockedItem(item)} style={{
                  padding: "14px 18px", borderBottom: `1px solid ${C.border}`, cursor: "pointer",
                  transition: "all 0.15s", background: age >= 7 ? "rgba(226,75,74,0.03)" : "transparent",
                }}>
                  <div className="blocked-row">
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{item.summary}</div>
                      <div style={{ fontSize: 11, color: C.muted }}>{item.key} · {item.assignee} · {item.components.join(", ") || "No component"}</div>
                    </div>
                    <span className="bl-col-org"><OrgBadge org={item.org} /></span>
                    <span className="bl-col-pri"><PriBadge pri={item.priority} /></span>
                    <div style={{ fontSize: 13, fontWeight: 600, textAlign: "right", color: age >= 7 ? "#A32D2D" : age >= 3 ? C.gold : C.accent }}>
                      {age === 0 ? "Today" : `${age}d`}
                    </div>
                  </div>
                  {/* Latest comment — why is it blocked? */}
                  {commentPreview && (
                    <div style={{ marginTop: 6, padding: "6px 10px", background: C.warnLt, borderRadius: 6, border: `1px solid ${C.warn}33`, fontSize: 12, color: "#5a3a2a", lineHeight: 1.5 }}>
                      <span style={{ fontWeight: 600, color: C.warn, fontSize: 11 }}>{item.lastComment.author}:</span>{" "}
                      {commentPreview}{commentPreview.length >= 120 ? "…" : ""}
                    </div>
                  )}
                  {!commentPreview && (
                    <div style={{ marginTop: 6, fontSize: 11, color: C.muted, fontStyle: "italic" }}>
                      No comment explaining the blocker — tap to view details
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── BLOCKED ITEM DETAIL ── */}
        {!loading && view === "blocked" && activeBlockedItem && (
          <div style={{ animation: "fadeIn 0.25s ease" }}>
            <button onClick={() => { setActiveBlockedItem(null); setBlockedActionResult(null); setBlockedComment(""); }} style={{ background: "none", border: "none", cursor: "pointer", color: C.blue, fontSize: 13, fontFamily: "inherit", padding: 0, marginBottom: 12, fontWeight: 500 }}>← Back to blocked items</button>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
              <div style={{ padding: "14px 18px", background: C.warnLt, borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 12, fontSize: 11, fontWeight: 600, background: "#FCEBEB", color: "#B33B3B", border: "1px solid #B33B3B" }}>⚠ Blocked</span>
                  <span style={{ fontSize: 12, color: C.warn, fontWeight: 600 }}>{daysSince(activeBlockedItem.updated)} days since last update</span>
                </div>
                <OrgBadge org={activeBlockedItem.org} />
              </div>
              <div style={{ padding: "18px 22px" }}>
                <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>{activeBlockedItem.summary}</div>
                <div style={{ fontSize: 13, color: C.muted, marginBottom: 16 }}>
                  {activeBlockedItem.key} · Assigned to {activeBlockedItem.assignee}
                  {activeBlockedItem.components.length > 0 && ` · ${activeBlockedItem.components.join(", ")}`}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div>
                    <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted, fontWeight: 600, marginBottom: 6 }}>Labels</div>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {visibleLabels(activeBlockedItem.labels).map(l => (
                        <span key={l} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, background: C.paper, border: `1px solid ${C.border}`, color: C.muted }}>{l}</span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted, fontWeight: 600, marginBottom: 6 }}>Timeline</div>
                    <div style={{ fontSize: 12, color: C.muted }}>
                      Created: {activeBlockedItem.created ? new Date(activeBlockedItem.created).toLocaleDateString() : "—"}<br />
                      Updated: {activeBlockedItem.updated ? new Date(activeBlockedItem.updated).toLocaleDateString() : "—"}
                    </div>
                  </div>
                </div>

                {/* Discussion Thread */}
                <CommentThread item={activeBlockedItem} onAddComment={addThreadComment} accentColor={C.warn} />

                {/* Background */}
                {activeBlockedItem.description && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted, fontWeight: 600, marginBottom: 6 }}>Background</div>
                    <div style={{ fontSize: 13, lineHeight: 1.6, color: "#3a3a36", maxHeight: 120, overflow: "auto", padding: "8px 10px", background: C.paper, borderRadius: 6, border: `1px solid ${C.border}` }}>
                      {typeof activeBlockedItem.description === "string" ? <FormattedDescription text={activeBlockedItem.description} /> : "See Jira for full description"}
                    </div>
                  </div>
                )}

                {/* ── RELATED DOCUMENTS (Confluence links + web links on the Jira issue) ── */}
                {(() => {
                  const d = docsFor(activeBlockedItem);
                  if (!d.confluenceLinks.length && !d.webLinks.length) {
                    return d.linksLoading
                      ? <div style={{ marginTop: 16, fontSize: 12, color: C.muted, fontStyle: "italic" }}>Checking for linked documents…</div>
                      : null;
                  }
                  return (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: C.blue, fontWeight: 700, marginBottom: 8 }}>
                      📄 Related Documents ({d.confluenceLinks.length + d.webLinks.length})
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {d.confluenceLinks.map((link, i) => (
                        <a key={`cl-${i}`} href={link.url} target="_blank" rel="noopener noreferrer" style={{
                          display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
                          background: C.blueLt || "#EAF2F8", borderRadius: 8, border: `1px solid ${C.blue}33`,
                          textDecoration: "none", transition: "all 0.15s",
                        }}>
                          <span style={{ fontSize: 18, flexShrink: 0 }}>📋</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: C.blue }}>{link.title || "Confluence Document"}</div>
                            <div style={{ fontSize: 11, color: C.muted, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {link.url}
                            </div>
                          </div>
                          <span style={{ fontSize: 12, color: C.blue, flexShrink: 0 }}>Open ↗</span>
                        </a>
                      ))}
                      {d.webLinks.map((link, i) => (
                        <a key={`wl-${i}`} href={link.url} target="_blank" rel="noopener noreferrer" style={{
                          display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
                          background: C.blueLt || "#EAF2F8", borderRadius: 8, border: `1px solid ${C.blue}33`,
                          textDecoration: "none", transition: "all 0.15s",
                        }}>
                          <span style={{ fontSize: 18, flexShrink: 0 }}>🔗</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: C.blue }}>{link.title || "Web link"}</div>
                            <div style={{ fontSize: 11, color: C.muted, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {link.url}
                            </div>
                          </div>
                          <span style={{ fontSize: 12, color: C.blue, flexShrink: 0 }}>Open ↗</span>
                        </a>
                      ))}
                    </div>
                  </div>
                  );
                })()}

                {/* Action result */}
                {blockedActionResult && (
                  <div style={{
                    marginTop: 16, padding: "10px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600,
                    background: blockedActionResult.success ? "#E8F0EB" : C.warnLt,
                    color: blockedActionResult.success ? C.accent : C.warn,
                    border: `1px solid ${blockedActionResult.success ? C.accent : C.warn}`,
                    animation: "fadeIn 0.25s ease",
                  }}>
                    {blockedActionResult.success ? "✓ " : "⚠ "}{blockedActionResult.message}
                  </div>
                )}

                {/* Provide Direction */}
                {!blockedActionResult?.success && (
                  <>
                    <div style={{ marginTop: 16 }}>
                      {/* Acting As */}
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, padding: "10px 14px", background: C.paper, borderRadius: 8, border: `1px solid ${C.border}` }}>
                        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted, fontWeight: 600, whiteSpace: "nowrap" }}>Acting as:</div>
                        <input
                          value={actingAs}
                          onChange={e => setActingAs(e.target.value)}
                          style={{
                            flex: 1, padding: "5px 10px", borderRadius: 5, border: `1px solid ${C.border}`,
                            fontSize: 13, fontFamily: "inherit", fontWeight: 600, color: C.ink,
                            background: C.card, outline: "none",
                          }}
                        />
                        <div style={{ fontSize: 10, color: C.muted, maxWidth: 200, lineHeight: 1.4 }}>
                          Jira audit trail captures the authenticated user. This name appears in the comment.
                        </div>
                      </div>
                      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted, fontWeight: 600, marginBottom: 6 }}>
                        Provide direction or guidance
                      </div>
                      <textarea
                        value={blockedComment}
                        onChange={e => setBlockedComment(e.target.value)}
                        placeholder="e.g., Go ahead with Option B for the vendor. / I'll call Darrell about this on Thursday. / Move forward without waiting for the assessment."
                        style={{
                          width: "100%", minHeight: 70, padding: "10px 12px", borderRadius: 6,
                          border: `1px solid ${C.border}`, fontSize: 13, fontFamily: "inherit",
                          background: C.paper, color: C.ink, resize: "vertical", outline: "none",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>

                    <div style={{ display: "flex", gap: 8, marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}`, flexWrap: "wrap", alignItems: "center" }}>
                      <button
                        onClick={() => provideDirection(activeBlockedItem, true)}
                        disabled={blockedActionPending}
                        style={{
                          padding: "8px 20px", borderRadius: 6, border: "none", fontSize: 13, fontWeight: 700,
                          cursor: blockedActionPending ? "not-allowed" : "pointer", fontFamily: "inherit",
                          background: C.accent, color: "#fff", opacity: blockedActionPending ? 0.6 : 1,
                          transition: "all 0.15s",
                        }}
                      >
                        {blockedActionPending ? "Sending..." : "✓ Unblock & Provide Direction"}
                      </button>
                      <button
                        onClick={() => provideDirection(activeBlockedItem, false)}
                        disabled={blockedActionPending}
                        style={{
                          padding: "8px 20px", borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 13, fontWeight: 700,
                          cursor: blockedActionPending ? "not-allowed" : "pointer", fontFamily: "inherit",
                          background: "transparent", color: C.muted, opacity: blockedActionPending ? 0.6 : 1,
                          transition: "all 0.15s",
                        }}
                      >
                        💬 Comment Only (keep blocked)
                      </button>
                      <div style={{ flex: 1 }} />
                      <a href={`${JIRA_BASE}/browse/${activeBlockedItem.key}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                        <Btn variant="outline">Open in Jira ↗</Btn>
                      </a>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ╔══════════════ APPROVED VIEW ══════════════╗ */}
        {/* ╔══════════════ REVIEWED — OWNER'S MOVE ══════════════╗ */}
        {!loading && view === "reviewed" && (
          <div style={{ animation: "fadeIn 0.25s ease" }}>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
              <div style={{ padding: "14px 18px", background: "linear-gradient(135deg, #EEEDFE 0%, #fff 100%)", borderBottom: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: "#534AB7" }}>Reviewed — owner's move</div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{reviewedItems.length} item{reviewedItems.length === 1 ? "" : "s"} David has reviewed — read the note and take it from there</div>
              </div>
              {reviewedItems.length === 0 && (
                <div style={{ padding: 40, textAlign: "center", color: C.muted, fontSize: 14 }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>👁️</div>
                  Nothing reviewed and waiting on an owner right now
                </div>
              )}
              {reviewedItems.map(item => (
                <div key={item.key} style={{ padding: "12px 18px", borderBottom: `1px solid ${C.border}` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <StatusPill status={item.status} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{item.summary}</div>
                      <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{item.key} · {item.assignee}</div>
                    </div>
                    <OrgBadge org={item.org} />
                    <a href={`${JIRA_BASE}/browse/${item.key}`} target="_blank" rel="noopener noreferrer" style={{ color: C.blue, fontSize: 12, textDecoration: "none", flexShrink: 0 }}>Open in Jira ↗</a>
                  </div>
                  {item.lastComment && (
                    <div style={{ marginTop: 8, background: "#EEEDFE", border: "1px solid #534AB733", borderRadius: 6, padding: "8px 12px" }}>
                      <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.06em", color: "#534AB7", fontWeight: 700, marginBottom: 2 }}>David's note</div>
                      <div style={{ fontSize: 12.5, color: C.ink, lineHeight: 1.5 }}>{cleanCommentBody(item.lastComment.body)}</div>
                    </div>
                  )}
                  {(() => { const ns = nextStepDisplay(item); return ns && ns.explicit ? (
                    <div style={{ fontSize: 11, marginTop: 6, color: C.accent }}>
                      <span style={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", fontSize: 9, marginRight: 5 }}>Next</span>{ns.text}
                    </div>
                  ) : null; })()}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ╔══════════════ AWAITING INFO ══════════════╗ */}
        {!loading && view === "awaitinginfo" && (
          <div style={{ animation: "fadeIn 0.25s ease" }}>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
              <div style={{ padding: "14px 18px", background: "linear-gradient(135deg, #FBEEDD 0%, #fff 100%)", borderBottom: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: "#B5651D" }}>Awaiting Info</div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{awaitingInfoItems.length} item{awaitingInfoItems.length === 1 ? "" : "s"} where David needs more before he can review — owner supplies it, then sends back to Awaiting Review</div>
              </div>
              {awaitingInfoItems.length === 0 && (
                <div style={{ padding: 40, textAlign: "center", color: C.muted, fontSize: 14 }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>❓</div>
                  Nothing waiting on more information right now
                </div>
              )}
              {awaitingInfoItems.map(item => (
                <div key={item.key} style={{ padding: "12px 18px", borderBottom: `1px solid ${C.border}` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <StatusPill status={item.status} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{item.summary}</div>
                      <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{item.key} · {item.assignee}</div>
                    </div>
                    <OrgBadge org={item.org} />
                    <a href={`${JIRA_BASE}/browse/${item.key}`} target="_blank" rel="noopener noreferrer" style={{ color: C.blue, fontSize: 12, textDecoration: "none", flexShrink: 0 }}>Open in Jira ↗</a>
                  </div>
                  {item.lastComment && (
                    <div style={{ marginTop: 8, background: "#FBEEDD", border: "1px solid #B5651D33", borderRadius: 6, padding: "8px 12px" }}>
                      <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.06em", color: "#B5651D", fontWeight: 700, marginBottom: 2 }}>David needs</div>
                      <div style={{ fontSize: 12.5, color: C.ink, lineHeight: 1.5 }}>{cleanCommentBody(item.lastComment.body)}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ╔══════════════ MEETINGS VIEW (PLACEHOLDER) ══════════════╗ */}
        {!loading && view === "meetings" && (
          <div style={{ animation: "fadeIn 0.25s ease" }}>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
              <div style={{ padding: "18px 22px", background: `linear-gradient(135deg, ${C.blueLt} 0%, #fff 100%)`, borderBottom: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: C.blue }}>Meeting Intelligence</div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Recordings, transcripts, action items, and decisions from leadership meetings</div>
              </div>
              <div style={{ padding: "40px 22px", textAlign: "center" }}>
                <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.3 }}>🎙</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: C.ink, marginBottom: 8 }}>Coming Soon</div>
                <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.7, maxWidth: 520, margin: "0 auto" }}>
                  This tab will surface meeting recordings, transcripts, action items, and key decisions from leadership meetings — giving David a single place to review what was discussed without digging through Teams.
                </div>

                <div style={{ marginTop: 24, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, maxWidth: 560, margin: "24px auto 0", textAlign: "left" }}>
                  <div style={{ background: C.blueLt, borderRadius: 8, padding: "14px 16px", border: `1px solid ${C.blue}22` }}>
                    <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: C.blue, fontWeight: 700, marginBottom: 6 }}>Phase 1 — Now available</div>
                    <div style={{ fontSize: 12, color: "#2a4a5a", lineHeight: 1.6 }}>
                      Microsoft Teams Copilot captures meeting recaps, action items, and summaries for CoStar-organized meetings. These can be pulled on demand through Claude.
                    </div>
                  </div>
                  <div style={{ background: C.paper, borderRadius: 8, padding: "14px 16px", border: `1px solid ${C.border}` }}>
                    <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted, fontWeight: 700, marginBottom: 6 }}>Phase 2 — Planned</div>
                    <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
                      Fireflies.ai will capture all meetings regardless of organizer or platform — Teams, Google Meet, Zoom — and route action items into Jira automatically.
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: 24, padding: "12px 16px", background: C.paper, borderRadius: 8, border: `1px solid ${C.border}`, maxWidth: 560, margin: "24px auto 0" }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>What David will see here</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 12, color: C.ink }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ color: C.accent }}>✓</span> Recent meetings with date and attendees
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ color: C.accent }}>✓</span> Key topics discussed
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ color: C.accent }}>✓</span> Action items with owners
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ color: C.accent }}>✓</span> Decisions made and their context
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ color: C.accent }}>✓</span> Cross-tenant meeting capture
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ color: C.accent }}>✓</span> Search by topic, person, or date
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── FOOTER ── */}
        <div style={{ marginTop: 16, padding: "10px 14px", borderRadius: 8, background: C.card, border: `1px solid ${C.border}`, display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center", fontSize: 11, color: C.muted }}>
          <span style={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em" }}>Status:</span>
          {Object.entries(STATUS).map(([k, v]) => (
            <span key={k} style={{ display: "flex", alignItems: "center", gap: 4 }}><span style={{ width: 7, height: 7, borderRadius: "50%", background: v.c }} />{v.l}</span>
          ))}
          <span style={{ marginLeft: "auto", fontStyle: "italic" }}>
            {cacheAge ? `Cached data · refreshing` : `Live data from LEAD Jira project`} · Cloud ID {CLOUD_ID.substring(0, 8)}... · Build {BUILD}
          </span>
        </div>

        {/* ── VISION GOVERNANCE NOTE (v44) — transparency for David & users ── */}
        <div style={{ marginTop: 8, padding: "9px 14px", borderRadius: 8, background: C.card, border: `1px solid ${C.border}`, fontSize: 10.5, color: C.muted, lineHeight: 1.55, display: "flex", alignItems: "flex-start", gap: 8 }}>
          <span style={{ marginTop: 1 }}><EyeIcon size={13} color={C.muted} /></span>
          <span>
            <strong style={{ color: C.ink, fontWeight: 700 }}>About the Vision bar &amp; who can change it:</strong> each organization's Vision is a reference statement stored on its own Confluence page. Editing is open to dashboard users; every change is written to Confluence <strong>as the signed-in user</strong> and recorded in that page's version history, and edit authority is governed by <strong>Confluence page permissions</strong> — not by this dashboard. A Vision marked <span style={{ color: C.gold, fontWeight: 700 }}>DRAFT — AWAITING EXECUTIVE APPROVAL</span> is a proposal, not an approved statement; the accountable executive (shown as its owner) can revise the wording or set it to <strong>Confirmed</strong> to approve, which removes the flag.
          </span>
        </div>

        {/* ══════════════ ACTIVITY DRAWER (comment ticker) ══════════════ */}
        {activityOpen && (
          <div style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", justifyContent: "flex-end" }}>
            <div onClick={() => setActivityOpen(false)} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.32)" }} />
            <div style={{ position: "relative", width: 384, maxWidth: "92vw", height: "100%", background: C.card, boxShadow: "-4px 0 24px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column", animation: "slideInRight 0.22s ease" }}>
              <div style={{ padding: "16px 18px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 16 }}>🔔</span>
                  <span style={{ fontFamily: "'Source Serif 4',Georgia,serif", fontSize: 17, fontWeight: 600, color: C.ink }}>Activity</span>
                </div>
                <button onClick={() => setActivityOpen(false)} aria-label="Close" style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, fontSize: 22, lineHeight: 1, padding: "0 4px", fontFamily: "inherit" }}>×</button>
              </div>
              <div style={{ padding: "9px 18px", fontSize: 12, color: C.muted, borderBottom: `1px solid ${C.border}`, flexShrink: 0, lineHeight: 1.5 }}>
                Recent comments across the portfolio, newest first{activityRecentCount > 0 ? ` · ${activityRecentCount} in the last 7 days` : ""}.
              </div>
              <div style={{ overflowY: "auto", flex: 1 }}>
                {activityFeed.length === 0 && (
                  <div style={{ padding: 28, textAlign: "center", color: C.muted, fontSize: 13 }}>
                    {loading ? "Loading comments…" : "No comments on exec-reportable items yet."}
                  </div>
                )}
                {(() => {
                  let lastBucket = null;
                  return activityFeed.map(e => {
                    const bucket = dayBucket(e.created);
                    const showHead = bucket !== lastBucket;
                    lastBucket = bucket;
                    const oc = ORGS[e.org] || ORGS.CoStar;
                    const text = e.body.length > 180 ? e.body.slice(0, 180).trimEnd() + "…" : e.body;
                    return (
                      <div key={e.id}>
                        {showHead && (
                          <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: C.muted, padding: "12px 18px 5px", background: C.paper }}>{bucket}</div>
                        )}
                        <button onClick={() => openItemFromActivity(e)} style={{
                          display: "block", width: "100%", textAlign: "left", border: "none", borderBottom: `1px solid ${C.border}`,
                          background: "transparent", cursor: "pointer", padding: "11px 18px", fontFamily: "inherit",
                        }}
                        onMouseEnter={ev => ev.currentTarget.style.background = C.paper}
                        onMouseLeave={ev => ev.currentTarget.style.background = "transparent"}>
                          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
                            <span style={{ fontSize: 13, fontWeight: 700, color: C.ink }}>{e.author}</span>
                            <span style={{ fontSize: 11, color: C.muted, whiteSpace: "nowrap", flexShrink: 0 }}>{relTime(e.created)}</span>
                          </div>
                          <div style={{ fontSize: 12.5, lineHeight: 1.5, color: "#4a4842", margin: "3px 0 7px" }}>{text}</div>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, minWidth: 0 }}>
                            <span style={{ width: 4, height: 4, borderRadius: "50%", background: oc.accent, flexShrink: 0 }} />
                            <span style={{ fontWeight: 700, color: C.blue, fontFamily: "monospace", fontSize: 10.5, flexShrink: 0 }}>{e.key}</span>
                            <span style={{ color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.isEpic ? e.summary : e.wsName}</span>
                            <span style={{ marginLeft: "auto", color: C.muted, flexShrink: 0, fontSize: 13 }}>›</span>
                          </div>
                        </button>
                      </div>
                    );
                  });
                })()}
              </div>
              <div style={{ padding: "10px 18px", borderTop: `1px solid ${C.border}`, fontSize: 11, color: C.muted, lineHeight: 1.5, flexShrink: 0, background: C.paper }}>
                Showing comments on <strong>exec-reportable</strong> items only, so routine activity elsewhere in Jira stays out of the feed. Tap any entry to open the item.
              </div>
            </div>
          </div>
        )}

        {/* ══════════════ CREATE NEW GOAL MODAL (v43) ══════════════ */}
        {createGoalOpen && (
          <CreateGoalModal
            existingSlugs={goals.map(g => g.slug).filter(Boolean)}
            visions={visions}
            onClose={() => setCreateGoalOpen(false)}
            onCreated={() => { setCreateGoalOpen(false); fetchData(false); }}
          />
        )}

        {/* ══════════════ NEW / EDIT VISION WIZARD (v44) ══════════════ */}
        {newVisionOpen && (
          <VisionWizard
            prefix={visionSel}
            existing={visions[visionSel]}
            onClose={() => setNewVisionOpen(false)}
            onSaved={() => { setNewVisionOpen(false); loadVisions(); }}
          />
        )}

        {/* ══════════════ TAG WORK TO GOAL MODAL (v43) ══════════════ */}
        {tagWorkGoal && (
          <TagWorkModal
            goal={tagWorkGoal}
            epics={epics}
            tasks={tasks}
            onClose={() => setTagWorkGoal(null)}
            onApplied={() => { setTagWorkGoal(null); fetchData(false); }}
          />
        )}
      </div>
    </div>
  );
}
